import React, { useEffect, useState, useCallback, Fragment } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  SelectGroup, SelectLabel, SelectSeparator,
} from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  Calculator, TrendingUp, TrendingDown, ChevronRight, Upload, FileSpreadsheet,
  AlertTriangle, Users, Landmark, Settings2, ReceiptEuro, CheckCircle2, XCircle,
  Clock, RefreshCw, Info, ShieldAlert, FileWarning, Table2, CalendarRange,
  LayoutGrid, Plus, Trash2, Pencil, ArrowLeft, Save,
  Lock, Unlock, KeyRound, Rocket, Download, ShieldCheck, Fingerprint, FileClock,
  Undo2, Eye, ArrowRightLeft, ListChecks, Ban, FileSearch, Search,
  CalendarPlus, RotateCcw, MapPin, Hash, Home, Coins, Gauge,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { fmtEur, fmtInt, fmtDateTime } from '@/lib/format';
import { ProgrammationTab } from './ProgrammationTab';
import { ForecastView } from './ForecastView';

// --------------------------------------------------------------------------- //
//  Helpers                                                                     //
// --------------------------------------------------------------------------- //
const MOIS_FR = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const MOIS_ABBR = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
const moisLabel = (ym) => { if (!ym) return '—'; const [y, m] = ym.split('-'); return `${MOIS_FR[parseInt(m, 10) - 1]} ${y}`; };
const moisShort = (ym) => { if (!ym) return '—'; const [y, m] = ym.split('-'); return `${MOIS_ABBR[parseInt(m, 10) - 1]} ${y.slice(2)}`; };

const QONTO_AUDIT_LABEL = { set: 'Dépôt de clé', replace: 'Remplacement de clé', delete: 'Suppression de clé', test: 'Test de connexion' };
const CLOTURE_AUDIT_LABEL = { close: 'Clôture du mois', reopen: 'Réouverture du mois' };

// P3 — signale (sans corriger) les entrées de correspondance canal terrain → facturation
// ambiguës : itinérance rattachée à autre chose que RUE ITI, ou doublons de casse.
const canalAmbiguities = (cm) => {
  const lower = {};
  (cm || []).forEach((m) => { const k = (m.canal_terrain || '').trim().toLowerCase(); lower[k] = (lower[k] || 0) + 1; });
  return (cm || []).map((m) => {
    const t = m.canal_terrain || '';
    const iti = /itin/i.test(t) && m.canal_facturation !== 'RUE ITI';
    const dup = lower[t.trim().toLowerCase()] > 1;
    if (iti) return { ...m, flag: 'ambigu', msg: `Mentionne une itinérance mais rattaché à « ${m.canal_facturation} » (RUE ITI attendu ?)` };
    if (dup) return { ...m, flag: 'doublon', msg: 'Doublon de casse (même libellé à la casse près)' };
    return { ...m, flag: null };
  });
};
const eur0 = (v) => fmtEur(v, 0);
const eur2 = (v) => fmtEur(v, 2);
const pct1 = (v) => (v === null || v === undefined || isNaN(v) ? '—' : `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 }).format(v)}\u202f%`);
const pct2 = (v) => (v === null || v === undefined || isNaN(v) ? '—' : `${new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v)}\u202f%`);
const isNum = (v) => typeof v === 'number' && !isNaN(v);
// Poids d'un poste en % du CA (vue Tableau). Coloré selon la cible max si fournie :
// vert si sous la cible, rouge si au-dessus ; gris neutre si aucune cible.
const PctCa = ({ pct, target }) => {
  if (pct === null || pct === undefined || isNaN(pct)) return null;
  const hasTarget = target !== null && target !== undefined;
  const cls = hasTarget ? (pct > target ? 'text-rose-600' : 'text-emerald-600') : 'text-muted-foreground';
  return (
    <span className={`block text-[9px] font-medium leading-none ${cls}`}
      title={hasTarget ? `${pct2(pct)} du CA · cible max ${target}\u202f%` : `${pct2(pct)} du CA`}
      data-testid="rex-pct-ca">
      {pct1(pct)}
    </span>
  );
};

// Sélecteur de mois réutilisable (règle CEO « toujours voir/changer le mois »).
// Les mois FIGÉS (historique nov. 2023 → mai 2026) sont grisés + « mois figé ».
const MonthPicker = ({ value, onChange, months, testid = 'rex-month-picker', className = '' }) => {
  const list = (months || []).slice().sort((a, b) => (a.mois < b.mois ? 1 : -1));
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger className={`h-9 w-[210px] ${className}`} data-testid={testid}>
        <SelectValue placeholder="Choisir un mois" />
      </SelectTrigger>
      <SelectContent className="max-h-[320px]">
        {list.map((m) => (
          <SelectItem key={m.mois} value={m.mois} disabled={!!m.frozen}
            data-testid={`${testid}-opt-${m.mois}`}>
            <span className="flex items-center gap-2">
              <span>{moisLabel(m.mois)}</span>
              {m.frozen ? <span className="text-[10px] text-muted-foreground">· mois figé</span>
                : m.closed ? <span className="text-[10px] text-emerald-600">· clôturé</span>
                : m.provisoire ? <span className="text-[10px] text-amber-600">· provisoire</span>
                : <span className="text-[10px] text-sky-600">· ouvert</span>}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};


// Montant comptable : négatifs en rouge ET entre parenthèses (jamais la seule couleur).
const Money = ({ v, d = 0, muted = false, className = '' }) => {
  if (!isNum(v)) return <span className={`tabular ${muted ? 'text-muted-foreground' : ''} ${className}`}>—</span>;
  const neg = v < 0;
  const txt = fmtEur(Math.abs(v), d);
  return <span className={`tabular ${neg ? 'text-rose-600 font-medium' : (muted ? 'text-muted-foreground' : '')} ${className}`}>{neg ? `(${txt})` : txt}</span>;
};

// Pastille "résultat" cerclée : VERT si positif, ROUGE si négatif (contour + fond doux + flèche
// de tendance). Les valeurs négatives sont affichées entre parenthèses, SANS signe moins
// (notation comptable). Réservée aux lignes de résultat/REX cumulé/% REX-CA (lignes héros du P&L).
const ResultChip = ({ v, isPct = false, strong = false }) => {
  if (!isNum(v)) return <span className="tabular text-muted-foreground">—</span>;
  const pos = v >= 0;
  const Icon = pos ? TrendingUp : TrendingDown;
  const txt = isPct ? pct2(Math.abs(v)) : fmtEur(Math.abs(v), 2);
  const tone = pos
    ? 'bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/30'
    : 'bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/30';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ring-1 ring-inset tabular ${strong ? 'text-[12.5px] font-semibold' : 'font-medium'} ${tone}`}>
      <Icon className="h-3 w-3 shrink-0" strokeWidth={2.6} aria-hidden="true" />
      <span>{pos ? txt : `(${txt})`}</span>
    </span>
  );
};

const Sparkline = ({ values, tone = 'accent' }) => {
  const nums = (values || []).map((v) => (isNum(v) ? v : null));
  const valid = nums.filter((v) => v !== null);
  if (valid.length < 2) return <span className="text-[11px] text-muted-foreground">—</span>;
  const min = Math.min(...valid), max = Math.max(...valid);
  const range = (max - min) || 1;
  const w = 80, h = 26, pad = 3;
  const step = w / (nums.length - 1);
  const xy = nums.map((v, i) => (v === null ? null : [i * step, pad + (h - 2 * pad) - ((v - min) / range) * (h - 2 * pad)])).filter(Boolean);
  const line = xy.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const last = xy[xy.length - 1];
  const area = `${xy[0][0].toFixed(1)},${h} ${line} ${xy[xy.length - 1][0].toFixed(1)},${h}`;
  const stroke = tone === 'resultat' ? 'var(--tenant-accent-600)' : 'var(--tenant-accent-500)';
  return (
    <svg width={w} height={h} className="overflow-visible" aria-hidden="true">
      <polygon points={area} fill={stroke} opacity="0.09" />
      <polyline points={line} fill="none" stroke={stroke} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={last[0]} cy={last[1]} r="2.4" fill={stroke} stroke="hsl(var(--card))" strokeWidth="1" />
    </svg>
  );
};

const KpiCard = ({ label, value, sub, tone = 'default', testid }) => {
  const toneCls = { default: 'text-foreground', revenu: 'text-[color:var(--tenant-accent-600)]', cout: 'text-foreground', resultat: 'text-emerald-600', deficit: 'text-rose-600' }[tone] || 'text-foreground';
  return (
    <Card className="card-shadow" data-testid={testid}>
      <CardContent className="p-4">
        <p className="section-kicker text-xs uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
        <p className={`mt-1 font-display text-2xl font-semibold tabular ${toneCls}`} data-testid={`${testid}-value`}>{value}</p>
        {sub ? <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p> : null}
      </CardContent>
    </Card>
  );
};

const StatusPill = ({ ok, warn, label, testid }) => {
  const cls = ok ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : warn ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-muted text-muted-foreground border-border';
  const Icon = ok ? CheckCircle2 : warn ? Clock : XCircle;
  return <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium ${cls}`} data-testid={testid}><Icon className="h-3.5 w-3.5" /> {label}</span>;
};

const Delta = ({ current, previous }) => {
  if (!isNum(current) || !isNum(previous) || previous === 0) return null;
  const d = current - previous;
  const pct = (d / Math.abs(previous)) * 100;
  const up = d >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return <span className={`ml-2 inline-flex items-center gap-0.5 text-xs font-medium ${up ? 'text-emerald-600' : 'text-rose-600'}`}><Icon className="h-3.5 w-3.5" />{`${up ? '+' : ''}${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 }).format(pct)} %`}</span>;
};

const SectionTitle = ({ icon: Icon, children, desc }) => (
  <div className="mb-4 flex items-start gap-3">
    <div className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ background: 'var(--tenant-accent-100)' }}><Icon className="h-5 w-5" style={{ color: 'var(--tenant-accent-600)' }} /></div>
    <div><h2 className="font-display text-lg font-semibold text-foreground">{children}</h2>{desc ? <p className="text-sm text-muted-foreground">{desc}</p> : null}</div>
  </div>
);

const Loading = () => <div className="space-y-3"><Skeleton className="h-24 w-full" /><Skeleton className="h-64 w-full" /></div>;

// =========================================================================== //
//  E1 — VUE TABLEAU                                                            //
// =========================================================================== //
const RANGES = [
  ['annee', 'Année en cours'], ['exercice_prec', 'Exercice précédent'], ['12m', '12 derniers mois'],
  ['complet', 'Historique complet'], ['custom', 'Plage personnalisée'],
];
const EXPANDABLE = { ca: true, cout_rd: true, cout_re: true, cout_rp: true, cout_bureau: true, frais_generaux: true };

const TableauView = ({ live, setLive }) => {
  const [range, setRange] = useState('annee');
  const [custom, setCustom] = useState({ start: '2023-11', end: '2026-07' });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});
  const [leafOpen, setLeafOpen] = useState({});
  const [fcOpen, setFcOpen] = useState(false);
  const [syncingQonto, setSyncingQonto] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const autoSyncRef = React.useRef(false);
  const loadedOnceRef = React.useRef(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const params = { range };
      if (range === 'custom') { params.start = custom.start; params.end = custom.end; }
      if (live) params.live = true;
      const { data } = await api.get('/rex/table', { params });
      setData(data.table);
    } catch (e) { toast.error('Chargement du tableau impossible'); }
    if (!silent) setLoading(false); else setRefreshing(false);
  }, [range, custom, live]);
  // 1er affichage = skeleton ; ensuite rechargement SILENCIEUX (on n'efface plus
  // l'écran) → la bascule « Mois en cours » garde le tableau visible et se met à
  // jour en place, sans attendre devant un écran vide.
  useEffect(() => {
    load(loadedOnceRef.current);
    loadedOnceRef.current = true;
  }, [load]);

  const fetchLastSync = useCallback(async () => {
    try {
      const { data: d } = await api.get('/rex/forecast/qonto/last-sync');
      if (d?.last_sync) setLastSync(d.last_sync);
    } catch { /* silencieux */ }
  }, []);

  const syncQonto = useCallback(async ({ silent = false } = {}) => {
    setSyncingQonto(true);
    try {
      const { data: d } = await api.post('/rex/forecast/qonto/sync'); // mois défaut = mois courant serveur
      const q = d?.qonto || {};
      if (!q.configured) {
        if (!silent) toast.error('Qonto non connecté — déposez la clé API (lecture seule) dans Suivi Social → Qonto.');
      } else if (q.error) {
        if (!silent) toast.error(`Synchronisation Qonto échouée (${q.error}).`);
      } else {
        setLastSync({ at: q.fetched_at || new Date().toISOString(), nb_txns: q.nb_txns, configured: true });
        const up = q.upstream;
        if (up && up.status === 'needs_validation') {
          if (!silent) toast.warning(`Qonto synchronisé — ${q.nb_txns || 0} transaction(s). Le mois précédent ${up.mois} a une variation >50 % : validez-le dans l'onglet Qonto pour l'appliquer.`);
          else toast.warning(`Mois précédent ${up.mois} à valider (>50 %) — onglet Qonto.`);
        } else if (up && up.status === 'applied') {
          if (!silent) toast.success(`Qonto synchronisé — ${q.nb_txns || 0} transaction(s) · mois précédent ${up.mois} mis à jour automatiquement.`);
        } else {
          if (!silent) toast.success(`Qonto synchronisé — ${q.nb_txns || 0} transaction(s) réelle(s) ce mois.`);
        }
      }
      await load(true);
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || 'Synchronisation Qonto impossible');
    }
    setSyncingQonto(false);
  }, [load]);

  // Auto-synchro Qonto à l'ouverture du mois en cours (temps réel) — en TÂCHE DE FOND
  // différée : n'interrompt jamais l'affichage (le cache serveur est pré-chauffé).
  useEffect(() => {
    if (!live) { autoSyncRef.current = false; return undefined; }
    if (autoSyncRef.current) return undefined;
    autoSyncRef.current = true;
    fetchLastSync();
    const t = setTimeout(() => { syncQonto({ silent: true }); }, 1500);
    return () => clearTimeout(t);
  }, [live, fetchLastSync, syncQonto]);

  if (loading) return <Loading />;
  if (!data) return null;
  const months = data.months;
  const frozenSet = new Set(data.meta?.frozen || []);
  const forecastSet = new Set(data.meta?.forecast || []);
  const isFc = (i) => forecastSet.has(months[i]);
  const isPctRow = (key) => key === 'pct' || key === 'pct_cumul';

  const lastSyncLabel = lastSync?.at ? (() => {
    try {
      return new Date(lastSync.at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    } catch { return null; }
  })() : null;

  const rowLabelCls = (kind) => {
    if (kind === 'resultat') return 'font-display font-semibold text-foreground';
    if (kind === 'total') return 'font-semibold text-foreground';
    if (kind === 'soustotal') return 'italic text-muted-foreground';
    if (kind === 'cumul') return 'text-muted-foreground';
    return 'text-foreground';
  };
  const rowBgFor = (row, ri) => {
    if (row.kind === 'resultat') return 'bg-[hsl(var(--rex-total-col))]';
    if (row.kind === 'total') return 'bg-[hsl(var(--rex-total-col))]';
    if (row.kind === 'cumul') return 'bg-[hsl(var(--rex-zebra))]';
    if (row.kind === 'soustotal') return 'bg-[hsl(var(--rex-zebra))]';
    if (row.key === 'pct' || row.key === 'pct_cumul') return 'bg-[hsl(var(--secondary))]';
    return ri % 2 === 1 ? 'bg-[hsl(var(--rex-zebra))]' : 'bg-card';
  };
  const isHero = (row) => row.kind === 'resultat' || row.kind === 'cumul' || isPctRow(row.key);
  const firstLiveIdx = months.findIndex((m) => !frozenSet.has(m));
  const dividerCls = (i) => (i === firstLiveIdx && firstLiveIdx > 0 ? 'border-l border-l-[color:var(--tenant-accent-100)]' : '');

  return (
    <div className="space-y-4" data-testid="rex-view-tableau">
      <div className="flex flex-wrap items-center gap-2">
        {RANGES.map(([k, lbl]) => (
          <button key={k} onClick={() => setRange(k)} data-testid={`rex-range-${k}`}
            className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--tenant-accent-500)] focus-visible:ring-offset-2 focus-visible:ring-offset-background ${k === range ? 'border-transparent text-white shadow-[0_6px_16px_-6px_var(--tenant-shadow)]' : 'border-border bg-card text-muted-foreground hover:border-[color:var(--tenant-accent-100)] hover:bg-[color:var(--tenant-accent-050)] hover:text-foreground'}`}
            style={k === range ? { background: 'var(--tenant-gradient)' } : {}}>{lbl}</button>
        ))}
        {range === 'custom' ? (
          <div className="flex items-center gap-2">
            <Input type="month" value={custom.start} min="2023-11" max="2026-07" className="h-9 w-36" data-testid="rex-range-start" onChange={(e) => setCustom((c) => ({ ...c, start: e.target.value }))} />
            <span className="text-muted-foreground">→</span>
            <Input type="month" value={custom.end} min="2023-11" max="2026-07" className="h-9 w-36" data-testid="rex-range-end" onChange={(e) => setCustom((c) => ({ ...c, end: e.target.value }))} />
          </div>
        ) : null}
        <div className="ml-auto flex items-center gap-3">
          {refreshing ? (
            <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground" data-testid="rex-tableau-refreshing">
              <RefreshCw className="h-3.5 w-3.5 animate-spin" />Actualisation…
            </span>
          ) : null}
          <label htmlFor="rex-live-toggle" className="flex cursor-pointer items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-sm">
            <Switch id="rex-live-toggle" checked={live} onCheckedChange={setLive} data-testid="rex-live-toggle" />
            <span className="font-medium">Mois en cours <span className="text-muted-foreground">(temps réel)</span></span>
          </label>
          {live ? (
            <>
              {lastSyncLabel ? (
                <span className="hidden text-[11px] text-muted-foreground sm:inline" data-testid="rex-qonto-last-sync"
                  title={`Dernière synchronisation Qonto réussie${lastSync?.nb_txns != null ? ` — ${lastSync.nb_txns} transaction(s)` : ''}`}>
                  Qonto&nbsp;: {lastSyncLabel}
                </span>
              ) : null}
              <Button variant="ghost" size="sm" className="h-9 text-muted-foreground hover:text-foreground" onClick={() => syncQonto()} disabled={syncingQonto} data-testid="rex-live-qonto-sync-btn" title="Récupérer les Frais généraux RÉELS du mois depuis Qonto (lecture seule)">
                <RefreshCw className={`mr-1.5 h-4 w-4 ${syncingQonto ? 'animate-spin' : ''}`} />
                {syncingQonto ? 'Synchro…' : 'Synchroniser Qonto'}
              </Button>
              <Button variant="outline" size="sm" className="h-9" onClick={() => setFcOpen(true)} data-testid="rex-live-detail-btn">
                <Gauge className="mr-1.5 h-4 w-4" />Détail & corrections
              </Button>
            </>
          ) : null}
        </div>
      </div>

      <Dialog open={fcOpen} onOpenChange={(o) => { setFcOpen(o); if (!o) load(true); }}>
        <DialogContent className="max-h-[92vh] max-w-[1000px] overflow-y-auto" data-testid="rex-live-detail-dialog">
          <DialogHeader>
            <DialogTitle>Mois en cours — détail du calcul & corrections</DialogTitle>
            <DialogDescription>Ajustez les postes estimés (réversible). Le tableau se met à jour à la fermeture.</DialogDescription>
          </DialogHeader>
          <ForecastView onChanged={() => load(true)} months={data.meta?.forecast || []} />
        </DialogContent>
      </Dialog>

      <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-[var(--rex-grid-shadow)]">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-gradient-to-r from-[color:var(--tenant-accent-050)] to-transparent px-4 py-3">
          <div className="flex items-center gap-2.5">
            <span className="grid h-7 w-7 place-items-center rounded-lg text-white shadow-[0_4px_10px_-4px_var(--tenant-shadow)]" style={{ background: 'var(--tenant-gradient)' }}><Table2 className="h-4 w-4" /></span>
            <div>
              <p className="font-display text-sm font-semibold leading-tight text-foreground">Compte de résultat</p>
              <p className="text-[11px] leading-tight text-muted-foreground">{months.length} mois · montants HT en euros</p>
            </div>
          </div>
          <div className="hidden items-center gap-3 text-[11px] text-muted-foreground sm:flex">
            <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full bg-[color:var(--tenant-accent-500)]" />résultat</span>
            <span className="inline-flex items-center gap-1.5"><span className="font-medium text-rose-600">(rouge)</span> négatif</span>
            <span className="inline-flex items-center gap-1.5"><span className="rounded-full border border-border bg-muted/60 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide">figé</span> lecture seule</span>
          </div>
        </div>
        <div className="rex-scroll max-h-[74vh] overflow-auto">
          <table className="min-w-full border-separate border-spacing-0 text-[13px] leading-[1.3]" data-testid="rex-tableau-grid">
            <thead>
              <tr>
                <th className="sticky left-0 top-0 z-40 w-[46vw] min-w-[150px] max-w-[204px] border-b border-r border-border bg-card px-2.5 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground shadow-[6px_0_10px_-6px_rgba(24,24,24,0.14)] md:w-auto md:min-w-[240px] md:max-w-none md:px-4">Poste</th>
                {months.map((m, i) => (
                  <th key={m} data-testid={`rex-month-header-${m}`} className={`sticky top-0 z-20 min-w-[104px] whitespace-nowrap border-b border-border px-2 py-2.5 text-right align-bottom backdrop-blur md:px-3 ${isFc(i) ? 'bg-[color:var(--tenant-accent-050)]' : 'bg-card/95'} ${dividerCls(i)}`}>
                    <div className="flex flex-col items-end gap-1">
                      <span className={`font-display text-[12.5px] font-semibold leading-none ${isFc(i) ? 'text-[color:var(--tenant-accent-700)]' : frozenSet.has(m) ? 'text-muted-foreground' : 'text-foreground'}`}>{moisShort(m)}</span>
                      {forecastSet.has(m)
                        ? <span className="rounded-full px-1.5 py-0.5 text-[8.5px] font-semibold uppercase tracking-wide text-white shadow-sm" style={{ background: 'var(--tenant-gradient)' }} aria-label="Mois en cours (forecast)">forecast</span>
                        : frozenSet.has(m)
                          ? <span className="rounded-full border border-border bg-muted/60 px-1.5 py-0.5 text-[8.5px] font-semibold uppercase tracking-wide text-muted-foreground" aria-label="Mois figé (lecture seule)">figé</span>
                          : <span className="rounded-full bg-[color:var(--tenant-accent-050)] px-1.5 py-0.5 text-[8.5px] font-semibold uppercase tracking-wide text-[color:var(--tenant-accent-600)]">vivant</span>}
                    </div>
                  </th>
                ))}
                <th data-testid="rex-total-header" className="sticky top-0 z-20 min-w-[132px] whitespace-nowrap border-b border-l-2 border-border border-l-[color:var(--tenant-accent-100)] bg-card/95 px-3 py-2.5 text-right align-bottom backdrop-blur">
                  <span className="font-display text-[12.5px] font-semibold text-foreground">Total</span>
                </th>
                <th data-testid="rex-trend-header" className="sticky top-0 z-20 min-w-[104px] border-b border-border bg-card/95 px-3 py-2.5 text-center align-bottom text-[11px] font-medium uppercase tracking-wide text-muted-foreground backdrop-blur">Tendance</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row, ri) => {
                const expandable = EXPANDABLE[row.key] && (row.children || []).length > 0;
                const isOpen = open[row.key];
                const bg = rowBgFor(row, ri);
                const accentBar = (row.kind === 'resultat' || row.key === 'ca') ? 'border-l-[3px] border-l-[color:var(--tenant-accent-500)]' : 'border-l-[3px] border-l-transparent';
                return (
                  <Fragment key={row.key}>
                    <tr className={`group ${bg} transition-colors duration-150 hover:bg-[hsl(var(--accent))]`} data-testid={`rex-trow-${row.key}`}>
                      <td className={`sticky left-0 z-30 w-[46vw] min-w-[150px] max-w-[204px] border-b border-r border-border ${bg} ${accentBar} px-2.5 py-2.5 text-left shadow-[6px_0_10px_-6px_rgba(24,24,24,0.14)] transition-colors duration-150 group-hover:bg-[hsl(var(--accent))] md:w-auto md:min-w-[240px] md:max-w-none md:px-4 ${rowLabelCls(row.kind)}`}>
                        <button className={`flex min-w-0 max-w-full items-center gap-1.5 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--tenant-accent-500)] ${expandable ? 'cursor-pointer' : 'cursor-default'}`} onClick={() => expandable && setOpen((o) => ({ ...o, [row.key]: !o[row.key] }))} data-testid={`rex-trow-toggle-${row.key}`} aria-expanded={expandable ? !!isOpen : undefined}>
                          {expandable ? <ChevronRight className={`h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-200 ${isOpen ? 'rotate-90 text-[color:var(--tenant-accent-600)]' : ''}`} /> : <span className="w-3.5 shrink-0" />}
                          <span className="min-w-0 truncate">{row.label}{row.target_pct != null ? <span className="ml-1 text-[9px] font-normal normal-case text-muted-foreground">≤ {row.target_pct}{'\u202f'}% CA</span> : null}</span>
                        </button>
                      </td>
                      {row.values.map((v, i) => (
                        <td key={i} className={`whitespace-nowrap border-b border-border/60 px-2 py-2.5 text-right tracking-[0.01em] md:px-3 ${isFc(i) ? 'bg-[color:var(--tenant-accent-050)]/60' : ''} ${dividerCls(i)}`}>
                          {isHero(row)
                            ? <ResultChip v={v} isPct={isPctRow(row.key)} strong={row.kind === 'resultat'} />
                            : <div className="flex flex-col items-end gap-0.5">
                                {row.pct_ca ? <PctCa pct={row.pct_ca[i]} target={row.target_pct} /> : null}
                                <Money v={v} d={2} className={row.kind === 'total' ? 'font-semibold' : ''} />
                              </div>}
                        </td>
                      ))}
                      <td className="whitespace-nowrap border-b border-l-2 border-border/60 border-l-[color:var(--tenant-accent-100)] px-3 py-2.5 text-right tracking-[0.01em]">
                        {isHero(row)
                          ? <ResultChip v={row.total} isPct={isPctRow(row.key)} strong={row.kind === 'resultat'} />
                          : <div className="flex flex-col items-end gap-0.5">
                              {row.pct_ca ? <PctCa pct={row.pct_ca_total} target={row.target_pct} /> : null}
                              <Money v={row.total} d={2} className="font-semibold" />
                            </div>}
                      </td>
                      <td className="border-b border-border/60 px-3 py-2.5"><div className="flex justify-center">{isPctRow(row.key) ? <span className="text-[11px] text-muted-foreground">—</span> : <Sparkline values={row.trend} tone={row.kind} />}</div></td>
                    </tr>
                    {expandable && isOpen ? (row.children || []).map((ch, ci) => {
                      const hasLeaves = (ch.leaves || []).length > 0;
                      const lk = `${row.key}-${ci}`;
                      const lOpen = leafOpen[lk];
                      return (
                        <Fragment key={lk}>
                          <tr className="fade-up bg-[hsl(var(--rex-zebra))]" data-testid={`rex-tchild-${row.key}-${ci}`}>
                            <td className="sticky left-0 z-30 w-[46vw] min-w-[150px] max-w-[204px] border-b border-r border-l-[3px] border-border border-l-transparent bg-[hsl(var(--rex-zebra))] px-2 py-1.5 pl-3 text-left text-xs text-muted-foreground shadow-[6px_0_10px_-6px_rgba(24,24,24,0.14)] md:w-auto md:min-w-[240px] md:max-w-none md:px-4 md:pl-9">
                              <button className={`inline-flex min-w-0 max-w-full items-center gap-2 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--tenant-accent-500)] ${hasLeaves ? 'cursor-pointer hover:text-foreground' : 'cursor-default'}`} onClick={() => hasLeaves && setLeafOpen((o) => ({ ...o, [lk]: !o[lk] }))} data-testid={`rex-tchild-toggle-${row.key}-${ci}`} aria-expanded={hasLeaves ? !!lOpen : undefined} title={hasLeaves ? 'Déplier le détail (transactions / personnes)' : undefined}>
                                {hasLeaves ? <ChevronRight className={`h-3 w-3 shrink-0 transition-transform duration-200 ${lOpen ? 'rotate-90 text-[color:var(--tenant-accent-600)]' : ''}`} /> : <span className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/50" />}
                                <span className="truncate">{ch.label}</span>
                                {hasLeaves ? <span className="shrink-0 text-[9px] text-muted-foreground/70">({ch.leaves.length})</span> : null}
                              </button>
                            </td>
                            {ch.values.map((v, i) => <td key={i} className={`whitespace-nowrap border-b border-border/50 px-3 py-1.5 text-right ${dividerCls(i)}`}><Money v={v} d={2} muted /></td>)}
                            <td className="border-b border-l-2 border-border/50 border-l-[color:var(--tenant-accent-100)] px-3 py-1.5" /><td className="border-b border-border/50 px-3 py-1.5" />
                          </tr>
                          {hasLeaves && lOpen ? (ch.leaves || []).map((lf, li) => {
                            const lfAmount = (lf.values || []).reduce((a, v) => a + (typeof v === 'number' ? v : 0), 0);
                            const lfHasAmount = (lf.values || []).some((v) => typeof v === 'number');
                            return (
                            <tr key={`${lk}-${li}`} className="fade-up bg-card" data-testid={`rex-tleaf-${row.key}-${ci}-${li}`}>
                              <td className="sticky left-0 z-30 w-[46vw] min-w-[150px] max-w-[204px] border-b border-r border-l-[3px] border-border border-l-transparent bg-card px-2 py-1 pl-4 text-left text-[11px] text-muted-foreground shadow-[6px_0_10px_-6px_rgba(24,24,24,0.14)] md:w-auto md:min-w-[240px] md:max-w-none md:px-4 md:pl-[3.25rem]">
                                <div className="flex min-w-0 items-center justify-between gap-1.5">
                                  <span className="flex min-w-0 items-center gap-1">
                                    {lf.kind === 'personne' ? <Users className="h-3 w-3 shrink-0 opacity-50" /> : <ReceiptEuro className="h-3 w-3 shrink-0 opacity-50" />}
                                    <span className="truncate">{lf.label}</span>
                                    {lf.etiquette ? <span className="hidden shrink-0 rounded bg-muted px-1 text-[9px] uppercase tracking-wide opacity-70 md:inline">{lf.etiquette}</span> : null}
                                  </span>
                                  {lfHasAmount ? <span className="shrink-0 rounded-full border border-[color:var(--tenant-accent-100)] bg-[color:var(--tenant-accent-050)] px-1.5 py-0.5 text-[10.5px] font-semibold tabular text-[color:var(--tenant-accent-700)] md:hidden" data-testid="rex-transaction-amount-chip">{eur2(lfAmount)}</span> : null}
                                </div>
                              </td>
                              {lf.values.map((v, i) => <td key={i} className={`whitespace-nowrap border-b border-border/40 px-2 py-1 text-right md:px-3 ${dividerCls(i)}`}>{v != null ? <Money v={v} d={2} muted /> : <span className="text-muted-foreground/30">·</span>}</td>)}
                              <td className="border-b border-l-2 border-border/40 border-l-[color:var(--tenant-accent-100)] px-3 py-1" /><td className="border-b border-border/40 px-3 py-1" />
                            </tr>
                            );
                          }) : null}
                        </Fragment>
                      );
                    }) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {data.main_doeuvre?.has_data ? (
        <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-[var(--rex-grid-shadow)]" data-testid="rex-md-recap">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-gradient-to-r from-[color:var(--tenant-accent-050)] to-transparent px-4 py-3">
            <div className="flex items-center gap-2.5">
              <span className="grid h-7 w-7 place-items-center rounded-lg text-white shadow-[0_4px_10px_-4px_var(--tenant-shadow)]" style={{ background: 'var(--tenant-gradient)' }}><Users className="h-4 w-4" /></span>
              <div>
                <p className="font-display text-sm font-semibold leading-tight text-foreground">Main-d'œuvre — salariés vs indépendants</p>
                <p className="text-[11px] leading-tight text-muted-foreground">Comparaison mensuelle · sous-ensemble des coûts de production (RD + RE + RP)</p>
              </div>
            </div>
            <div className="hidden items-center gap-3 text-[11px] text-muted-foreground sm:flex">
              <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full bg-[color:var(--tenant-accent-500)]" />salariés</span>
              <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full bg-amber-500" />indépendants</span>
            </div>
          </div>
          <div className="rex-scroll overflow-auto">
            <table className="min-w-full border-separate border-spacing-0 text-[13px] leading-[1.3]" data-testid="rex-md-grid">
              <thead>
                <tr>
                  <th className="sticky left-0 top-0 z-30 min-w-[240px] border-b border-r border-border bg-card px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground shadow-[6px_0_10px_-6px_rgba(24,24,24,0.14)]">Nature</th>
                  {months.map((m, i) => (
                    <th key={m} className={`min-w-[104px] whitespace-nowrap border-b border-border bg-card px-3 py-2.5 text-right ${dividerCls(i)}`}>
                      <span className={`font-display text-[12.5px] font-semibold ${frozenSet.has(m) ? 'text-muted-foreground' : 'text-foreground'}`}>{moisShort(m)}</span>
                    </th>
                  ))}
                  <th className="min-w-[150px] whitespace-nowrap border-b border-l-2 border-border border-l-[color:var(--tenant-accent-100)] bg-card px-3 py-2.5 text-right"><span className="font-display text-[12.5px] font-semibold text-foreground">Total · part</span></th>
                </tr>
              </thead>
              <tbody>
                {data.main_doeuvre.rows.map((r) => {
                  const tot = (r.values || []).reduce((a, v) => a + (typeof v === 'number' ? v : 0), 0);
                  const gtot = (data.main_doeuvre.total || []).reduce((a, v) => a + (typeof v === 'number' ? v : 0), 0);
                  const share = r.kind !== 'total' && gtot ? Math.round((tot / gtot) * 1000) / 10 : null;
                  const dot = r.kind === 'salaries' ? 'bg-[color:var(--tenant-accent-500)]' : r.kind === 'independants' ? 'bg-amber-500' : '';
                  const rowBg = r.kind === 'total' ? 'bg-[hsl(var(--rex-total-col))]' : 'bg-card';
                  return (
                    <tr key={r.key} className={rowBg} data-testid={`rex-md-row-${r.key}`}>
                      <td className={`sticky left-0 z-20 border-b border-r border-border ${rowBg} px-4 py-2 text-left shadow-[6px_0_10px_-6px_rgba(24,24,24,0.14)]`}>
                        <span className="flex items-center gap-2">
                          {dot ? <span className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${dot}`} /> : <span className="w-2.5 shrink-0" />}
                          <span className="flex flex-col">
                            <span className={r.kind === 'total' ? 'font-semibold text-foreground' : 'text-foreground'}>{r.label}</span>
                            {r.sublabel ? <span className="text-[10px] font-normal text-muted-foreground">{r.sublabel}</span> : null}
                          </span>
                        </span>
                      </td>
                      {(r.values || []).map((v, i) => (
                        <td key={i} className={`whitespace-nowrap border-b border-border/60 px-3 py-2 text-right tabular ${dividerCls(i)}`}>
                          {typeof v === 'number' ? <Money v={v} d={2} className={r.kind === 'total' ? 'font-semibold' : ''} /> : <span className="text-muted-foreground/40" title="Ventilation indisponible (mois figé en montant global)">—</span>}
                        </td>
                      ))}
                      <td className="whitespace-nowrap border-b border-l-2 border-border/60 border-l-[color:var(--tenant-accent-100)] px-3 py-2 text-right">
                        <Money v={tot} d={2} className="font-semibold" />
                        {share != null ? <span className="ml-1.5 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">{share}{'\u202f'}%</span> : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="px-4 py-2 text-[11px] text-muted-foreground">« — » = mois figé stocké en montant global (ventilation salariés/indépendants non disponible). La somme <b>salariés + indépendants</b> = coûts RD + RE + RP.</p>
        </div>
      ) : null}

      <p className="text-xs text-muted-foreground">Colonnes « figé » = historique importé (novembre 2023 → mai 2026, lecture seule). Chaque poste de coût se déplie : <b>Frais généraux</b> et <b>Coût Bureau</b> jusqu'à la <b>transaction</b> (date d'opération · contrepartie · HT · étiquette) ; <b>Coût RD</b> et <b>Coût RE</b> par <b>région</b> puis par <b>personne</b> (coût employeur). Montants HT en euros (2 décimales) ; négatifs entre parenthèses et en rouge.</p>
    </div>
  );
};

// =========================================================================== //
//  E1 — VUE MOIS                                                               //
// =========================================================================== //
// Détail COMPLET des frais généraux d'un mois — ligne par ligne AVEC l'origine
// (transactions Qonto). Répond à la demande CEO « détail complet de juillet ».
const FraisGenerauxDetailCard = ({ mois }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({});
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/rex/months/${mois}/frais-generaux-detail`); setData(data); }
    catch (e) { setData(null); }
  }, [mois]);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  return (
    <Card className="card-shadow" data-testid="rex-fg-detail-card">
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base"><ReceiptEuro className="h-4 w-4" />Détail des frais généraux — {moisLabel(mois)}</CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[10px]">{data.frozen ? 'valeurs figées' : 'aligné au tableau · origines Qonto'}</Badge>
            <span className="text-sm font-semibold tabular" data-testid="rex-fg-detail-total"><Money v={data.total} d={2} /></span>
          </div>
        </div>
        <CardDescription>{data.nb_lignes} ligne(s) · chaque montant est justifié par ses transactions (date de règlement · contrepartie · HT).</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="divide-y rounded-lg border">
          {(data.lignes || []).map((l, idx) => {
            const isOpen = open[idx];
            const txns = l.txns || [];
            return (
              <div key={idx} data-testid={`rex-fg-line-${idx}`}>
                <button onClick={() => txns.length && setOpen((o) => ({ ...o, [idx]: !o[idx] }))}
                  className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-sm ${txns.length ? 'cursor-pointer hover:bg-muted/40' : 'cursor-default'}`}>
                  <span className="flex min-w-0 items-center gap-1.5">
                    {txns.length ? <ChevronRight className={`h-3.5 w-3.5 shrink-0 transition-transform ${isOpen ? 'rotate-90' : ''}`} /> : <span className="w-3.5" />}
                    <span className="truncate font-medium">{l.label}</span>
                    {l.origine === 'tableau' ? <span className="shrink-0 rounded bg-muted px-1 py-0.5 text-[9px] font-medium uppercase tracking-wide text-muted-foreground" title="Rattachée au mois via le décalage (ex. carte Swile), hors flux Qonto live du mois — présente dans le tableau.">décalage</span> : null}
                    {l.origine === 'qonto_hors_tableau' ? <span className="shrink-0 rounded bg-amber-100 px-1 py-0.5 text-[9px] font-medium uppercase tracking-wide text-amber-700" title="Ligne détectée en live Qonto mais pas encore reportée au tableau — lancez un recalcul du mois.">à reporter</span> : null}
                    {l.nb ? <span className="text-[10px] text-muted-foreground">×{l.nb}</span> : null}
                  </span>
                  <Money v={l.montant} d={2} />
                </button>
                {isOpen && txns.length ? (
                  <div className="bg-muted/30 px-3 py-1.5" data-testid={`rex-fg-line-${idx}-txns`}>
                    {txns.map((t, ti) => (
                      <div key={ti} className="flex items-center justify-between gap-2 py-0.5 text-[11px] text-muted-foreground">
                        <span className="flex min-w-0 items-center gap-1.5">
                          <span className="tabular whitespace-nowrap opacity-70">{(t.date_operation || t.emitted_at || t.settled_at) ? String(t.date_operation || t.emitted_at || t.settled_at).slice(0, 10) : '—'}</span>
                          <span className="truncate">{t.contrepartie || t.label_txn || '—'}</span>
                        </span>
                        <Money v={t.montant} d={2} muted />
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
          {(data.lignes || []).length === 0 ? <p className="px-3 py-6 text-center text-sm text-muted-foreground">Aucune ligne de frais généraux pour ce mois.</p> : null}
        </div>
      </CardContent>
    </Card>
  );
};

// Rapport « libellés restants à reclasser » d'un mois : non mappés (à trancher) +
// exclus (justifiés) — reclassement en 1 clic (décision mémorisée + recalcul immédiat).
const LabelsRestantsCard = ({ mois, onChanged }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get('/rex/labels-restants', { params: { mois } }); setData(data); }
    catch (e) { setData(null); }
  }, [mois]);
  useEffect(() => { load(); }, [load]);
  const reclasser = async (label, poste) => {
    setBusy(label);
    try { await api.post('/rex/labels/reclasser', { label, poste, mois });
      toast.success(`« ${label} » → ${DEST_LABEL[poste] || poste}`); load(); onChanged && onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Reclassement impossible'); }
    setBusy(null);
  };
  if (!data) return null;
  const nonMappe = data.non_mappe || [];
  const exclus = data.exclus || [];
  const postes = data.postes_disponibles || [['frais_generaux', 'Frais généraux']].map(([v, l]) => ({ value: v, label: l }));
  const Row = ({ r }) => (
    <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2" data-testid={`rex-restant-${r.label}`}>
      <span className="flex min-w-0 flex-col">
        <span className="truncate text-sm font-medium">{r.label} <span className="text-xs text-muted-foreground">· {r.nb} txn</span></span>
        {r.justification ? <span className="text-[11px] text-muted-foreground">{r.justification}</span> : null}
      </span>
      <div className="flex items-center gap-2">
        <span className="tabular text-sm"><Money v={r.montant} d={2} /></span>
        <Select value={undefined} onValueChange={(v) => reclasser(r.label, v)} disabled={busy === r.label}>
          <SelectTrigger className="h-8 w-[190px]" data-testid={`rex-restant-select-${r.label}`}><SelectValue placeholder="Reclasser vers…" /></SelectTrigger>
          <SelectContent>
            {postes.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
            <SelectSeparator />
            <SelectItem value="exclu">Exclu du compte de résultat</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
  return (
    <Card className="card-shadow" data-testid="rex-labels-restants-card">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base"><ListChecks className="h-4 w-4" />Libellés restants à trancher — {moisLabel(mois)}</CardTitle>
        <CardDescription>Les libellés Qonto qui n'entrent dans <b>aucun poste</b> : <b>non mappés</b> (décision requise) ou <b>exclus</b> (déjà comptés ailleurs). Reclassez en <b>1 clic</b> — la décision est mémorisée et le mois recalculé.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-amber-700"><AlertTriangle className="h-3.5 w-3.5" />Non mappés — à trancher ({nonMappe.length})</p>
          {nonMappe.length ? <div className="divide-y rounded-lg border border-amber-200">{nonMappe.map((r) => <Row key={r.label} r={r} />)}</div>
            : <p className="rounded-lg border border-dashed px-3 py-3 text-center text-xs text-muted-foreground" data-testid="rex-restant-nonmappe-empty">Aucun libellé non mappé — tout est rattaché.</p>}
        </div>
        <div>
          <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-slate-600"><Ban className="h-3.5 w-3.5" />Exclus du compte de résultat ({exclus.length})</p>
          {exclus.length ? <div className="divide-y rounded-lg border">{exclus.map((r) => <Row key={r.label} r={r} />)}</div>
            : <p className="rounded-lg border border-dashed px-3 py-3 text-center text-xs text-muted-foreground">Aucun libellé exclu ce mois.</p>}
        </div>
      </CardContent>
    </Card>
  );
};


const MoisView = ({ mois, setMois }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({ cout_rd: true, ca: false, frais_generaux: false });
  const [leafOpen, setLeafOpen] = useState({});
  const [syncingIndes, setSyncingIndes] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get('/rex/dashboard', { params: { mois } }); setData(data); }
    catch (e) { toast.error('Chargement du mois impossible'); }
    setLoading(false);
  }, [mois]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <Loading />;
  if (!data) return null;
  const m = data.month;
  const prev = data.previous;
  const lines = data.lines;
  const st = m.statut || {};
  const controlSet = new Set(data.control_months || []);
  const isControl = controlSet.has(mois);
  const qontoOk = st.qonto === 'ok' || st.qonto === 'fige' || st.qonto === 'releve_historique';
  const qontoLabel = st.qonto === 'ok' ? 'Qonto synchronisé'
    : st.qonto === 'fige' ? 'Historique figé'
    : st.qonto === 'releve_historique' ? 'Relevé historique (Qonto non connecté)'
    : 'Qonto non configuré';
  const prevLineVal = (key) => { if (!prev) return null; if (key === 'ca') return prev.ca; if (key === 'total_couts') return prev.total_couts; if (key === 'rex') return prev.rex; return null; };
  const cloture = m.cloture || {};
  const isClosed = !!cloture.closed;
  const hasQontoExc = !!data.qonto_exceptions_open;
  const canClose = !m.frozen && !isControl && !m.provisoire && m.cloturable !== false && !isClosed && !hasQontoExc;
  const closeMonth = async () => { try { await api.post(`/rex/months/${mois}/close`); toast.success(`Mois ${moisLabel(mois)} clôturé`); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Clôture impossible'); } };
  const reopenMonth = async () => { try { await api.post(`/rex/months/${mois}/reopen`, { confirm: true, reason: 'Réouverture manuelle depuis la vue Mois' }); toast.success(`Mois ${moisLabel(mois)} réouvert`); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Réouverture impossible'); } };
  // Bouton dédié « paie indés figée » : source DISTINCTE des flux Qonto (jamais couplée).
  // REX consomme la publication figée de Suivi Social ; il n'écrit jamais dans social_*.
  const syncIndesFiges = async () => {
    setSyncingIndes(true);
    try {
      const { data: r } = await api.post('/rex/indes/sync-freeze', null, { params: { mois } });
      if (r.status === 'applied') {
        const n = r.nb_indes ?? null;
        toast.success(`Paie indépendants figée récupérée — ${eur2(r.total)}${n != null ? ` (${n} indépendant${n > 1 ? 's' : ''})` : ''}`);
        load();
      } else if (r.status === 'closed') {
        toast.info('Mois clôturé — aucune écriture.');
      } else {
        toast.warning(`Paie indépendants non figée pour ${moisLabel(mois)}`, {
          description: 'Figez d’abord la paie dans Suivi Social → Paie indés, puis relancez.',
          action: { label: 'Ouvrir Suivi Social', onClick: () => navigate('/suivi-social') },
        });
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Synchronisation de la paie indés impossible'); }
    setSyncingIndes(false);
  };

  return (
    <div className="space-y-6" data-testid="rex-view-mois">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5" data-testid="rex-month-switcher">
          {data.months_available.map((ym) => (
            <button key={ym} onClick={() => setMois(ym)} data-testid={`rex-month-${ym}`}
              className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors ${ym === mois ? 'border-transparent text-white' : 'bg-white text-foreground hover:bg-muted'}`}
              style={ym === mois ? { background: 'var(--tenant-accent-500)' } : {}}>
              {moisShort(ym)}{controlSet.has(ym) ? <span className={`text-[9px] uppercase ${ym === mois ? 'text-white/80' : 'text-amber-600'}`}>contrôle</span> : null}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2" data-testid="rex-cloture-actions">
          {isClosed ? (
            <>
              <Badge className="gap-1 bg-slate-700 text-white hover:bg-slate-700" data-testid="rex-cloture-badge"><Lock className="h-3 w-3" />Clôturé le {fmtDateTime(cloture.closed_at)}</Badge>
              <AlertDialog>
                <AlertDialogTrigger asChild><Button size="sm" variant="outline" data-testid="rex-reopen-btn"><Unlock className="mr-2 h-4 w-4" />Réouvrir le mois</Button></AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Réouvrir {moisLabel(mois)} ?</AlertDialogTitle>
                    <AlertDialogDescription>Un mois clôturé est censé être définitif. La réouverture est tracée dans le journal d'audit (qui / quand / IP) et permet à nouveau les recalculs. Confirmez-vous ?</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel><AlertDialogAction onClick={reopenMonth} data-testid="rex-reopen-confirm">Réouvrir</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          ) : canClose ? (
            <AlertDialog>
              <AlertDialogTrigger asChild><Button size="sm" data-testid="rex-close-btn" style={{ background: 'var(--tenant-accent-500)' }}><Lock className="mr-2 h-4 w-4" />Clôturer le mois</Button></AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader><AlertDialogTitle>Clôturer {moisLabel(mois)} ?</AlertDialogTitle>
                  <AlertDialogDescription>Les montants du mois seront <b>figés définitivement</b> : toute évolution ultérieure des tarifs, du référentiel ou des régions ne les modifiera plus (§12). Action tracée dans le journal d'audit.</AlertDialogDescription></AlertDialogHeader>
                <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel><AlertDialogAction onClick={closeMonth} data-testid="rex-close-confirm">Clôturer</AlertDialogAction></AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : (m.frozen ? <Badge variant="outline" className="text-[10px]">Historique figé</Badge>
              : m.provisoire ? <Badge variant="outline" className="gap-1 border-amber-300 text-amber-700 text-[10px]"><Clock className="h-3 w-3" />Non clôturable (provisoire)</Badge>
              : hasQontoExc ? <Badge variant="outline" className="gap-1 border-rose-300 text-rose-700 text-[10px]"><Ban className="h-3 w-3" />Non clôturable (exceptions Qonto)</Badge>
              : null)}
        </div>
      </div>

      {isClosed ? (
        <Alert className="border-slate-300 bg-slate-50" data-testid="rex-cloture-banner">
          <Lock className="h-4 w-4 text-slate-700" />
          <AlertTitle className="text-slate-800">Mois clôturé — montants figés</AlertTitle>
          <AlertDescription className="text-sm text-slate-600">Clôturé le <b>{fmtDateTime(cloture.closed_at)}</b>{cloture.closed_by ? <> par <b>{cloture.closed_by}</b></> : null}. Les valeurs ci-dessous ne sont plus recalculées (§12). Utilisez « Réouvrir le mois » (confirmation requise) pour autoriser à nouveau les modifications.</AlertDescription>
        </Alert>
      ) : null}

      {m.provisoire ? (
        <Alert className="border-amber-300 bg-amber-50" data-testid="rex-provisoire-banner">
          <Clock className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-800">Mois provisoire — non clôturable</AlertTitle>
          <AlertDescription className="text-sm text-amber-700">
            {isControl ? 'Mois de contrôle hors compte de résultat (non consolidé dans la vue Année). ' : ''}
            {(m.indisponibilites || []).includes('indes_bulletin_rd') ? (
              <span className="block"><b>Indépendants au bulletin indisponibles</b> : la paie indés de {moisLabel(mois)} n'est pas figée dans Suivi Social. Le Coût RD ne peut donc pas être affiché amputé de cette part (ce serait un résultat faussement meilleur). Figez la paie dans <b>Suivi Social → Paie indés</b>, puis cliquez <b>« Synchroniser la paie indépendants figée »</b> ci-dessous. </span>
            ) : null}
            {(m.indisponibilites || []).some((k) => ['cout_bureau', 'frais_generaux', 'indes_qonto_rd'].includes(k)) ? (
              <span className="block">Un ou plusieurs postes alimentés par <b>Qonto</b> sont indisponibles (source non connectée / non synchronisée). </span>
            ) : null}
            Les postes concernés affichent « — » (jamais 0 €) et le résultat d'exploitation n'est pas calculé tant que les données manquantes ne sont pas disponibles.
          </AlertDescription>
        </Alert>
      ) : null}

      {data.qonto_demo ? (
        <Alert className="border-indigo-300 bg-indigo-50" data-testid="rex-demo-banner">
          <ShieldAlert className="h-4 w-4 text-indigo-600" />
          <AlertTitle className="text-indigo-800">Montants issus d'un jeu reconstruit (démonstration)</AlertTitle>
          <AlertDescription className="text-sm text-indigo-700">
            Le Coût Bureau, les Frais généraux et les indés (flux Qonto) de ce mois proviennent d'un jeu <b>reconstruit et labellisé</b> (<code>demo_reconstruit</code>) — ce ne sont <b>pas</b> les transactions bancaires réelles. Ils reproduisent les cibles validées du compte de résultat (le tirage live Qonto renvoyait des libellés à mapper). Pour recalculer depuis les transactions réelles : mapper les labels (Administration → Labels) puis « Vérifier la synchronisation » dans l'onglet Qonto.
          </AlertDescription>
        </Alert>
      ) : null}

      {data.qonto_exceptions_open ? (
        <Alert className="border-rose-300 bg-rose-50" data-testid="rex-qonto-exc-banner">
          <ShieldAlert className="h-4 w-4 text-rose-600" />
          <AlertTitle className="text-rose-800">{data.qonto_exceptions_open} exception(s) Qonto — mois non clôturable</AlertTitle>
          <AlertDescription className="text-sm text-rose-700">
            Des transactions Qonto ne sont rattachées à aucun poste (label absent ou sans correspondance). Elles n'entrent dans aucun montant. Traitez-les dans l'onglet <b>Qonto → Exceptions Qonto</b> (labelliser / mapper / exclure) avant de clôturer.
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <StatusPill ok={st.paie_importee} label="Paie salariés importée" testid="rex-st-paie" />
        <StatusPill ok={st.facturation_saisie} warn={!st.facturation_saisie} label={st.facturation_saisie ? 'Facturation saisie' : 'Facturation non saisie'} testid="rex-st-fact" />
        <StatusPill ok={st.indes_figes} warn={!st.indes_figes} label={st.indes_figes ? 'Indés figés' : 'Paie indépendants non figée'} testid="rex-st-indes" />
        <StatusPill ok={qontoOk} warn={!qontoOk} label={qontoLabel} testid="rex-st-qonto" />
      </div>

      {!m.frozen && !isClosed ? (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed border-muted-foreground/30 bg-muted/30 p-3" data-testid="rex-indes-sync">
          <Users className="h-4 w-4 shrink-0 text-muted-foreground" />
          <div className="min-w-[12rem] flex-1">
            <p className="text-sm font-medium text-foreground">Paie indépendants figée</p>
            <p className="text-xs text-muted-foreground">
              Source Suivi Social (distincte des flux Qonto). Récupère le coût des indépendants au bulletin figé pour <b>{moisLabel(mois)}</b> — lecture seule, aucune écriture dans Suivi Social.
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={syncIndesFiges} disabled={syncingIndes} data-testid="rex-indes-sync-btn">
            <RefreshCw className={`mr-2 h-4 w-4 ${syncingIndes ? 'animate-spin' : ''}`} />
            Synchroniser la paie indépendants figée
          </Button>
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Chiffre d'affaires" value={m.ca === null ? 'Non saisie' : eur0(m.ca)} sub={m.detail?.ca?.volume ? `${fmtInt(m.detail.ca.volume)} mandats` : null} tone="revenu" testid="rex-kpi-ca" />
        <KpiCard label="Total coût du personnel" value={m.total_personnel === null ? '—' : eur0(m.total_personnel)} sub={m.provisoire ? 'Coût Bureau indisponible' : null} tone="cout" testid="rex-kpi-personnel" />
        <KpiCard label="Résultat d'exploitation" value={m.rex === null ? '—' : eur0(m.rex)} sub={m.provisoire ? 'Provisoire — non calculé' : (m.rex < 0 ? 'Mois déficitaire' : null)} tone={m.provisoire ? 'default' : (m.rex < 0 ? 'deficit' : 'resultat')} testid="rex-kpi-rex" />
        <KpiCard label="% REX / CA" value={pct1(m.pct_rex_ca)} sub={m.frozen ? 'Mois figé' : (m.provisoire ? 'Provisoire' : 'Régime vivant')} testid="rex-kpi-pct" />
      </div>

      <Card className="card-shadow">
        <CardHeader className="pb-2">
          <CardTitle className="font-display text-base">Compte de résultat — {moisLabel(mois)}</CardTitle>
          <CardDescription>{m.frozen ? 'Mois figé (historique Excel, lecture seule).' : 'Régime vivant. Cliquez une ligne pour déplier le détail.'}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="divide-y" data-testid="rex-pnl-table">
            {lines.map((ln) => {
              const expandable = (ln.children || []).length > 0;
              const isOpen = open[ln.key];
              const emphasis = ln.kind === 'resultat' || ln.kind === 'total';
              return (
                <div key={ln.key}>
                  <button onClick={() => expandable && setOpen((o) => ({ ...o, [ln.key]: !o[ln.key] }))}
                    className={`flex w-full items-center justify-between py-3 text-left ${expandable ? 'cursor-pointer hover:bg-muted/50' : 'cursor-default'} ${emphasis ? 'font-semibold' : ''} ${ln.kind === 'soustotal' ? 'italic text-muted-foreground' : ''}`}
                    data-testid={`rex-line-${ln.key}`}>
                    <span className="flex items-center gap-2">
                      {expandable ? <ChevronRight className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? 'rotate-90' : ''}`} /> : <span className="w-4" />}
                      <span className={`text-sm ${ln.kind === 'resultat' ? 'text-foreground' : ''}`}>{ln.label}{ln.key === 'ca' && ln.volume ? <span className="ml-2 text-xs text-muted-foreground">({fmtInt(ln.volume)} mandats)</span> : null}</span>
                    </span>
                    <span className="flex items-center gap-2 font-medium" data-testid={`rex-line-${ln.key}-value`}>
                      {ln.source_note ? <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700" data-testid={`rex-line-${ln.key}-indispo`}><Info className="h-3 w-3" />{ln.source_note}</span> : null}
                      {ln.key === 'pct' || ln.key === 'pct_cumul' || ln.kind === 'resultat' || ln.kind === 'cumul'
                        ? <ResultChip v={ln.value} isPct={ln.key === 'pct' || ln.key === 'pct_cumul'} strong={ln.kind === 'resultat'} />
                        : <Money v={ln.value} d={2} className={emphasis ? 'font-semibold' : ''} />}
                      {ln.source_note ? null : <Delta current={ln.value} previous={prevLineVal(ln.key)} />}
                    </span>
                  </button>
                  {expandable && isOpen ? (
                    <div className="mb-2 rounded-lg bg-muted/40 px-3 py-1" data-testid={`rex-line-${ln.key}-detail`}>
                      {ln.note ? <p className="py-2 pl-6 text-xs italic text-muted-foreground">{ln.note}</p> : null}
                      {(ln.children || []).map((c, i) => {
                        const hasLeaves = (c.leaves || []).length > 0;
                        const lk = `${ln.key}-${i}`;
                        const lOpen = leafOpen[lk];
                        return (
                          <div key={i}>
                            <button
                              onClick={() => hasLeaves && setLeafOpen((o) => ({ ...o, [lk]: !o[lk] }))}
                              className={`flex w-full items-center justify-between py-1.5 text-sm text-muted-foreground ${hasLeaves ? 'cursor-pointer hover:text-foreground' : 'cursor-default'}`}
                              data-testid={`rex-mchild-${ln.key}-${i}`}
                              aria-expanded={hasLeaves ? !!lOpen : undefined}
                              title={hasLeaves ? 'Déplier le détail (transactions / personnes)' : undefined}>
                              <span className="flex min-w-0 items-center gap-1.5 pl-6">
                                {hasLeaves
                                  ? <ChevronRight className={`h-3 w-3 shrink-0 transition-transform duration-200 ${lOpen ? 'rotate-90 text-[color:var(--tenant-accent-600)]' : ''}`} />
                                  : <span className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/50" />}
                                <span className="truncate">{c.label}</span>
                                {hasLeaves ? <span className="shrink-0 text-[9px] text-muted-foreground/70">({c.leaves.length})</span> : null}
                              </span>
                              <span className="flex shrink-0 items-center gap-2">
                                {c.source_note ? <span className="text-[10px] font-medium text-amber-700">{c.source_note}</span> : null}
                                <Money v={c.value !== undefined ? c.value : c.montant} d={2} muted />
                              </span>
                            </button>
                            {hasLeaves && lOpen ? (
                              <div className="mb-1" data-testid={`rex-mchild-${ln.key}-${i}-leaves`}>
                                {(c.leaves || []).map((lf, li) => (
                                  <div key={li} className="flex items-center justify-between py-1 pl-12 text-[11px] text-muted-foreground" data-testid={`rex-mleaf-${ln.key}-${i}-${li}`}>
                                    <span className="flex min-w-0 items-center gap-1.5">
                                      {lf.kind === 'personne' ? <Users className="h-3 w-3 shrink-0 opacity-50" /> : <ReceiptEuro className="h-3 w-3 shrink-0 opacity-50" />}
                                      <span className="truncate">{lf.label}</span>
                                      {lf.etiquette ? <span className="shrink-0 rounded bg-muted px-1 text-[9px] uppercase tracking-wide opacity-70">{lf.etiquette}</span> : null}
                                    </span>
                                    <Money v={lf.value} d={2} muted />
                                  </div>
                                ))}
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {data.main_doeuvre?.has_data ? (() => {
        const md = data.main_doeuvre;
        const sal = md.salaries[0]; const ind = md.independants[0]; const tot = md.total[0];
        const shareSal = md.share_salaries[0];
        const shareInd = (typeof shareSal === 'number') ? Math.round((100 - shareSal) * 10) / 10 : null;
        return (
          <Card className="card-shadow" data-testid="rex-md-recap-mois">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 font-display text-base"><Users className="h-4 w-4" />Main-d'œuvre — salariés vs indépendants</CardTitle>
              <CardDescription>Répartition du coût de production (RD + RE + RP) pour {moisLabel(mois)} — salariés (paie) d'un côté, indépendants (bulletin + flux Qonto) de l'autre.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="rounded-lg border border-border bg-[color:var(--tenant-accent-050)] p-3" data-testid="rex-md-mois-salaries">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><span className="inline-block h-2.5 w-2.5 rounded-full bg-[color:var(--tenant-accent-500)]" />Coût salariés</div>
                  <p className="mt-1 font-display text-lg font-semibold tabular"><Money v={sal} d={2} /></p>
                  <p className="text-[11px] text-muted-foreground">Fiches de paie · RD + RE + RP{typeof shareSal === 'number' ? ` · ${shareSal}\u202f%` : ''}</p>
                </div>
                <div className="rounded-lg border border-border bg-amber-50 p-3" data-testid="rex-md-mois-independants">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><span className="inline-block h-2.5 w-2.5 rounded-full bg-amber-500" />Coût indépendants</div>
                  <p className="mt-1 font-display text-lg font-semibold tabular"><Money v={ind} d={2} /></p>
                  <p className="text-[11px] text-muted-foreground">Bulletin + flux Qonto{typeof shareInd === 'number' ? ` · ${shareInd}\u202f%` : ''}</p>
                </div>
                <div className="rounded-lg border border-border bg-muted/50 p-3" data-testid="rex-md-mois-total">
                  <div className="text-xs text-muted-foreground">Total main-d'œuvre</div>
                  <p className="mt-1 font-display text-lg font-semibold tabular"><Money v={tot} d={2} /></p>
                  <p className="text-[11px] text-muted-foreground">Salariés + indépendants</p>
                </div>
              </div>
              {typeof shareSal === 'number' ? (
                <div className="h-2.5 w-full overflow-hidden rounded-full bg-amber-500/30" title={`Salariés ${shareSal}% · Indépendants ${shareInd}%`} data-testid="rex-md-mois-bar">
                  <div className="h-full rounded-full bg-[color:var(--tenant-accent-500)]" style={{ width: `${shareSal}%` }} />
                </div>
              ) : null}
            </CardContent>
          </Card>
        );
      })() : null}

      {/* Détail complet des frais généraux + rapport « libellés restants » (P1 juillet) */}
      <FraisGenerauxDetailCard mois={mois} />
      <LabelsRestantsCard mois={mois} onChanged={load} />
    </div>
  );
};

// =========================================================================== //
//  E1 — VUE ANNÉE                                                              //
// =========================================================================== //
const AnneeView = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({ cout_rd: true });
  const [leafOpen, setLeafOpen] = useState({});
  useEffect(() => { (async () => { try { const { data } = await api.get('/rex/annual'); setData(data.annual); } catch (e) { toast.error('Chargement annuel impossible'); } setLoading(false); })(); }, []);
  if (loading) return <Loading />;
  if (!data) return null;
  return (
    <div className="space-y-4" data-testid="rex-view-annee">
      <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-[var(--rex-grid-shadow)]">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-gradient-to-r from-[color:var(--tenant-accent-050)] to-transparent px-4 py-3">
          <div className="flex items-center gap-2.5">
            <span className="grid h-7 w-7 place-items-center rounded-lg text-white shadow-[0_4px_10px_-4px_var(--tenant-shadow)]" style={{ background: 'var(--tenant-gradient)' }}><CalendarRange className="h-4 w-4" /></span>
            <div>
              <p className="font-display text-sm font-semibold leading-tight text-foreground">Compte de résultat annuel</p>
              <p className="text-[11px] leading-tight text-muted-foreground">{data.years.length} exercices · montants HT en euros</p>
            </div>
          </div>
        </div>
        <div className="rex-scroll max-h-[74vh] overflow-auto">
          <table className="min-w-full border-separate border-spacing-0 text-[13px] leading-[1.3]" data-testid="rex-annee-grid">
            <thead>
              <tr>
                <th className="sticky left-0 top-0 z-40 w-[46vw] min-w-[150px] max-w-[204px] border-b border-r border-border bg-card px-2.5 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground shadow-[6px_0_10px_-6px_rgba(24,24,24,0.14)] md:w-auto md:min-w-[240px] md:max-w-none md:px-4">Poste</th>
                {data.years.map((y) => (
                  <th key={y.year} className="sticky top-0 z-20 min-w-[132px] whitespace-nowrap border-b border-border bg-card/95 px-4 py-2.5 text-right align-bottom font-display text-[12.5px] font-semibold backdrop-blur">
                    {y.label}{y.partiel ? <span className="ml-1 text-[9px] uppercase text-amber-600">partiel</span> : null}{y.provisoire ? <span className="ml-1 text-[9px] uppercase text-rose-600">provisoire</span> : null}
                  </th>
                ))}
                <th className="sticky top-0 z-20 min-w-[104px] whitespace-nowrap border-b border-l-2 border-border border-l-[color:var(--tenant-accent-100)] bg-card/95 px-4 py-2.5 text-right align-bottom text-[11px] font-medium uppercase tracking-wide text-muted-foreground backdrop-blur">{data.evolution_label || 'Évolution'}</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row, ri) => {
                const bg = row.kind === 'resultat' ? 'bg-[hsl(var(--rex-result-row))]' : row.kind === 'total' ? 'bg-[hsl(var(--rex-total-col))]' : row.kind === 'soustotal' ? 'bg-[hsl(var(--rex-zebra))]' : (ri % 2 === 1 ? 'bg-[hsl(var(--rex-zebra))]' : 'bg-card');
                const accentBar = (row.kind === 'resultat' || row.key === 'ca') ? 'border-l-[3px] border-l-[color:var(--tenant-accent-500)]' : 'border-l-[3px] border-l-transparent';
                const labelCls = row.kind === 'resultat' ? 'font-display font-semibold' : row.kind === 'total' ? 'font-semibold' : row.kind === 'soustotal' ? 'italic text-muted-foreground' : '';
                const expandable = EXPANDABLE[row.key] && (row.children || []).length > 0;
                const isOpen = open[row.key];
                return (
                <Fragment key={row.key}>
                <tr className={`group ${bg} transition-colors duration-150 hover:bg-[hsl(var(--accent))]`} data-testid={`rex-arow-${row.key}`}>
                  <td className={`sticky left-0 z-30 w-[46vw] min-w-[150px] max-w-[204px] border-b border-r border-border ${bg} ${accentBar} px-2.5 py-2.5 text-left shadow-[6px_0_10px_-6px_rgba(24,24,24,0.14)] transition-colors duration-150 group-hover:bg-[hsl(var(--accent))] md:w-auto md:min-w-[240px] md:max-w-none md:px-4 ${labelCls}`}>
                    <button className={`flex min-w-0 max-w-full items-center gap-1.5 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--tenant-accent-500)] ${expandable ? 'cursor-pointer' : 'cursor-default'}`} onClick={() => expandable && setOpen((o) => ({ ...o, [row.key]: !o[row.key] }))} data-testid={`rex-arow-toggle-${row.key}`} aria-expanded={expandable ? !!isOpen : undefined}>
                      {expandable ? <ChevronRight className={`h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-200 ${isOpen ? 'rotate-90 text-[color:var(--tenant-accent-600)]' : ''}`} /> : <span className="w-3.5 shrink-0" />}
                      <span className="min-w-0 truncate">{row.label}{row.target_pct != null ? <span className="ml-1 text-[9px] font-normal normal-case text-muted-foreground">≤ {row.target_pct}{'\u202f'}% CA</span> : null}</span>
                    </button>
                  </td>
                  {row.values.map((v, i) => (
                    <td key={i} className="whitespace-nowrap border-b border-border/60 px-4 py-2.5 text-right tracking-[0.01em]">
                      <div>{(row.kind === 'resultat' || row.kind === 'cumul')
                        ? <ResultChip v={v} strong={row.kind === 'resultat'} />
                        : <Money v={v} d={2} className={row.kind === 'total' ? 'font-semibold' : ''} />}</div>
                      {row.key !== 'ca' && isNum(row.pct_ca[i]) ? <div className={`mt-0.5 text-[11px] tabular ${row.target_pct != null ? (row.pct_ca[i] > row.target_pct ? 'text-rose-600 font-medium' : 'text-emerald-600 font-medium') : 'text-muted-foreground'}`} title={row.target_pct != null ? `cible max ${row.target_pct}\u202f% du CA` : null}>{pct2(row.pct_ca[i])} du CA{row.target_pct != null ? ` (≤ ${row.target_pct}\u202f%)` : ''}</div> : null}
                    </td>
                  ))}
                  <td className="whitespace-nowrap border-b border-l-2 border-border/60 border-l-[color:var(--tenant-accent-100)] px-4 py-2.5 text-right">
                    {isNum(row.evolution) ? <span className={`tabular text-xs font-semibold ${row.evolution >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{row.evolution >= 0 ? '+' : ''}{pct2(row.evolution)}</span> : <span className="text-muted-foreground">—</span>}
                  </td>
                </tr>
                {expandable && isOpen ? (row.children || []).map((ch, ci) => {
                  const hasLeaves = (ch.leaves || []).length > 0;
                  const lk = `${row.key}-${ci}`;
                  const lOpen = leafOpen[lk];
                  return (
                    <Fragment key={lk}>
                      <tr className="fade-up bg-[hsl(var(--rex-zebra))]" data-testid={`rex-achild-${row.key}-${ci}`}>
                        <td className="sticky left-0 z-30 w-[46vw] min-w-[150px] max-w-[204px] border-b border-r border-l-[3px] border-border border-l-transparent bg-[hsl(var(--rex-zebra))] px-2 py-1.5 pl-3 text-left text-xs text-muted-foreground shadow-[6px_0_10px_-6px_rgba(24,24,24,0.14)] md:w-auto md:min-w-[240px] md:max-w-none md:px-4 md:pl-9">
                          <button className={`inline-flex min-w-0 max-w-full items-center gap-2 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--tenant-accent-500)] ${hasLeaves ? 'cursor-pointer hover:text-foreground' : 'cursor-default'}`} onClick={() => hasLeaves && setLeafOpen((o) => ({ ...o, [lk]: !o[lk] }))} data-testid={`rex-achild-toggle-${row.key}-${ci}`} aria-expanded={hasLeaves ? !!lOpen : undefined} title={hasLeaves ? 'Déplier le détail (transactions / personnes)' : undefined}>
                            {hasLeaves ? <ChevronRight className={`h-3 w-3 shrink-0 transition-transform duration-200 ${lOpen ? 'rotate-90 text-[color:var(--tenant-accent-600)]' : ''}`} /> : <span className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/50" />}
                            <span className="truncate">{ch.label}</span>
                            {hasLeaves ? <span className="shrink-0 text-[9px] text-muted-foreground/70">({ch.leaves.length})</span> : null}
                          </button>
                        </td>
                        {(ch.values || []).map((v, i) => <td key={i} className="whitespace-nowrap border-b border-border/50 px-4 py-1.5 text-right"><Money v={v} d={2} muted /></td>)}
                        <td className="border-b border-l-2 border-border/50 border-l-[color:var(--tenant-accent-100)] px-4 py-1.5" />
                      </tr>
                      {hasLeaves && lOpen ? (ch.leaves || []).map((lf, li) => {
                        const lfAmount = (lf.values || []).reduce((a, v) => a + (typeof v === 'number' ? v : 0), 0);
                        const lfHasAmount = (lf.values || []).some((v) => typeof v === 'number');
                        return (
                        <tr key={`${lk}-${li}`} className="fade-up bg-card" data-testid={`rex-aleaf-${row.key}-${ci}-${li}`}>
                          <td className="sticky left-0 z-30 w-[46vw] min-w-[150px] max-w-[204px] border-b border-r border-l-[3px] border-border border-l-transparent bg-card px-2 py-1 pl-4 text-left text-[11px] text-muted-foreground shadow-[6px_0_10px_-6px_rgba(24,24,24,0.14)] md:w-auto md:min-w-[240px] md:max-w-none md:px-4 md:pl-[3.25rem]">
                            <div className="flex min-w-0 items-center justify-between gap-1.5">
                              <span className="flex min-w-0 items-center gap-1">
                                {lf.kind === 'personne' ? <Users className="h-3 w-3 shrink-0 opacity-50" /> : <ReceiptEuro className="h-3 w-3 shrink-0 opacity-50" />}
                                <span className="truncate">{lf.label}</span>
                                {lf.etiquette ? <span className="hidden shrink-0 rounded bg-muted px-1 text-[9px] uppercase tracking-wide opacity-70 md:inline">{lf.etiquette}</span> : null}
                              </span>
                              {lfHasAmount ? <span className="shrink-0 rounded-full border border-[color:var(--tenant-accent-100)] bg-[color:var(--tenant-accent-050)] px-1.5 py-0.5 text-[10.5px] font-semibold tabular text-[color:var(--tenant-accent-700)] md:hidden" data-testid="rex-transaction-amount-chip">{eur2(lfAmount)}</span> : null}
                            </div>
                          </td>
                          {(lf.values || []).map((v, i) => <td key={i} className="whitespace-nowrap border-b border-border/40 px-2 py-1 text-right md:px-4">{v != null ? <Money v={v} d={2} muted /> : <span className="text-muted-foreground/30">·</span>}</td>)}
                          <td className="border-b border-l-2 border-border/40 border-l-[color:var(--tenant-accent-100)] px-4 py-1" />
                        </tr>
                        );
                      }) : null}
                    </Fragment>
                  );
                }) : null}
                </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">Consolidation par exercice (somme des mois). Chaque poste se déplie jusqu'à la <b>personne</b> (RD/RE/RP + indés au bulletin) ou la <b>transaction/document</b> (Bureau, Frais généraux, indés flux Qonto, CA). Le poids en % du chiffre d'affaires est affiché sous chaque poste. L'évolution est calculée à périmètre comparable ({data.evolution_label || '—'}) : jamais un exercice partiel comparé à une année pleine. Juillet 2026 (contrôle CA) n'entre pas dans le P&L : 2026 = 6 mois.</p>
    </div>
  );
};

// =========================================================================== //
//  E1 — CONTENEUR (3 vues)                                                     //
// =========================================================================== //
const PilotageTab = ({ mois, setMois }) => {
  const [view, setView] = useState('tableau');
  const [live, setLive] = useState(false);
  const VIEWS = [['tableau', 'Tableau', Table2], ['mois', 'Mois', LayoutGrid], ['annee', 'Année', CalendarRange]];
  return (
    <div className="space-y-5" data-testid="rex-pilotage">
      <div className="inline-flex rounded-xl border bg-muted/50 p-1" data-testid="rex-view-switcher">
        {VIEWS.map(([k, lbl, Icon]) => (
          <button key={k} onClick={() => setView(k)} data-testid={`rex-view-${k}`}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-sm font-medium transition-colors ${view === k ? 'bg-white text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
            <Icon className="h-4 w-4" />{lbl}
          </button>
        ))}
      </div>
      {view === 'tableau' ? <TableauView live={live} setLive={setLive} /> : view === 'mois' ? <MoisView mois={mois} setMois={setMois} /> : <AnneeView />}
    </div>
  );
};

// =========================================================================== //
//  E2 — IMPORT PAIE                                                            //
// =========================================================================== //
const ImportPaieTab = () => {
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [imports, setImports] = useState([]);
  const [vent, setVent] = useState({ juin: null, juillet: null });
  const loadImports = useCallback(async () => { try { const { data } = await api.get('/rex/paie/imports'); setImports(data.imports || []); } catch (e) { /* noop */ } }, []);
  useEffect(() => { loadImports(); }, [loadImports]);
  useEffect(() => { (async () => {
    try {
      const [vj, vjl] = await Promise.all([
        api.get('/rex/ventilation', { params: { mois: '2026-06' } }),
        api.get('/rex/ventilation', { params: { mois: '2026-07' } }),
      ]);
      setVent({ juin: vj.data, juillet: vjl.data });
    } catch (e) { /* noop */ }
  })(); }, []);

  const onPreview = async (f) => {
    if (!f) return; setBusy(true); setPreview(null); setFile(f);
    const fd = new FormData(); fd.append('file', f);
    try { const { data } = await api.post('/rex/paie/preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); setPreview(data); toast.success(`Format ${data.parse.format} détecté — ${data.summary.nb_bulletins} bulletins`); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Analyse impossible'); }
    setBusy(false);
  };
  const onImport = async () => {
    if (!file) return; setBusy(true);
    const fd = new FormData(); fd.append('file', file);
    try { const { data } = await api.post('/rex/paie/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); toast.success(`Paie ${moisLabel(data.mois)} importée — ${data.attribution.nb_attribues} rattachés, ${data.attribution.nb_exceptions} exceptions`); setPreview(null); setFile(null); loadImports(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Import impossible'); }
    setBusy(false);
  };
  const s = preview?.summary; const at = preview?.attribution;
  return (
    <div className="space-y-6" data-testid="rex-import-paie">
      <SectionTitle icon={Upload} desc="PayFit (format A) ou ancien lot (format B). Détection automatique par signature, sans devinette.">Import de la paie salariés</SectionTitle>
      <Card className="card-shadow"><CardContent className="p-4">
        <label className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-border py-10 text-center hover:bg-muted/40" data-testid="rex-paie-dropzone">
          <FileSpreadsheet className="mb-2 h-8 w-8 text-muted-foreground" />
          <span className="text-sm font-medium">Déposer un PDF de bulletins (A ou B)</span>
          <span className="text-xs text-muted-foreground">{file ? file.name : 'Aucun fichier sélectionné'}</span>
          <input type="file" accept="application/pdf" className="hidden" data-testid="rex-paie-file" onChange={(e) => onPreview(e.target.files?.[0])} />
        </label>
      </CardContent></Card>
      {busy ? <Loading /> : null}
      {preview ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            <KpiCard label="Bulletins" value={fmtInt(s.nb_bulletins)} sub={`${s.nb_bulletins_zero} à zéro`} testid="rex-prev-nb" />
            <KpiCard label="Brut total" value={eur0(s.total_brut)} testid="rex-prev-brut" />
            <KpiCard label="Cotisations patronales" value={eur0(s.total_cotisations_patronales)} testid="rex-prev-cotp" />
            <KpiCard label="Transport public" value={eur0(s.total_transport_public)} sub="ajouté au coût" testid="rex-prev-transport" />
            <KpiCard label="Coût employeur total" value={eur0(s.total_cout_employeur)} testid="rex-prev-cout" />
          </div>
          <p className="text-xs text-muted-foreground" data-testid="rex-prev-formule">
            Coût salarié = <b>« Total versé par l'employeur »</b> + <b>transport public employeur</b>. La part employeur
            des <b>titres-restaurant</b> ({eur0(s.total_tr_employeur)}) est <b>exclue</b> du coût salarié : elle est déjà
            comptée dans Frais généraux → TR/Panier repas (virements Swile). Règle générique, appliquée à toutes les
            fiches de paie quel que soit le poste (RD/RE/RP).
          </p>
          <Card className="card-shadow">
            <CardHeader className="pb-2"><CardTitle className="text-base">Attribution par bloc & exceptions</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {Object.entries(at.by_bloc).map(([b, v]) => <Badge key={b} variant="secondary" data-testid={`rex-prev-bloc-${b}`}>{b} : {eur0(v)}</Badge>)}
                <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">{at.nb_attribues} rattachés</Badge>
                <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">{at.nb_exceptions} exceptions</Badge>
              </div>
              {(at.ventilation_regions || []).length ? (
                <div data-testid="rex-prev-ventilation">
                  <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Ventilation par région (adresse salarié)</p>
                  <div className="flex flex-wrap gap-2">
                    {at.ventilation_regions.map((r) => <Badge key={r.region} variant="outline">{r.region} : {r.nb} pers · {eur0(r.cout)}</Badge>)}
                  </div>
                </div>
              ) : null}
              {(at.exceptions || []).length ? (
                <Alert data-testid="rex-prev-exceptions"><FileWarning className="h-4 w-4" /><AlertTitle>Bulletins non rattachés (partiront en file d'exceptions)</AlertTitle>
                  <AlertDescription><ul className="mt-1 list-disc pl-5 text-sm">{at.exceptions.slice(0, 8).map((e, i) => <li key={i}>{e.message}</li>)}</ul></AlertDescription></Alert>
              ) : null}
              <Button onClick={onImport} disabled={busy} data-testid="rex-paie-import-btn" style={{ background: 'var(--tenant-accent-500)' }}>Importer & mettre à jour le mois</Button>
            </CardContent>
          </Card>
        </div>
      ) : null}
      <Card className="card-shadow" data-testid="rex-import-ventilation">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Ventilation par région des derniers imports</CardTitle>
          <CardDescription>Issue de l'adresse salarié du bulletin (jamais l'adresse employeur 95880 Enghien). Les codes postaux inconnus tombent en « Autre » ; sans code postal, aucune région par défaut (→ file d'exceptions).</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {[['juin 2026', vent.juin], ['juillet 2026', vent.juillet]].map(([lbl, data]) => (
            <div key={lbl} className="rounded-xl border p-3" data-testid={`rex-import-vent-${lbl.split(' ')[0]}`}>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">{lbl}</p>
              <div className="flex flex-wrap gap-2">
                {(data?.ventilation_regions || []).map((r) => (
                  <Badge key={r.region} variant="outline" className={r.region === 'Autre' ? 'border-amber-300 text-amber-700' : (r.region === 'Indéterminée' ? 'border-rose-300 text-rose-700' : '')}>
                    {r.region} : {r.nb} pers · {eur0(r.cout)}
                  </Badge>
                ))}
                {!(data?.ventilation_regions || []).length ? <span className="text-xs text-muted-foreground">Aucune donnée.</span> : null}
              </div>
              {(data?.adresses_manquantes || []).length ? (
                <p className="mt-2 text-xs text-amber-600">Code postal illisible (→ exception, aucune région par défaut) : {(data.adresses_manquantes || []).map((a) => a.nom).join(', ')}.</p>
              ) : null}
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="card-shadow">
        <CardHeader className="pb-2"><CardTitle className="text-base">Historique des imports</CardTitle>
          <CardDescription>Audit de provenance : nom du fichier réel, date/heure d'import et empreinte SHA-256 (lève toute ambiguïté sur l'origine des données).</CardDescription></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow><TableHead>Mois</TableHead><TableHead>Format</TableHead><TableHead>Fichier</TableHead><TableHead>Importé le</TableHead><TableHead>Empreinte SHA-256</TableHead><TableHead className="text-right">Coût employeur</TableHead></TableRow></TableHeader>
            <TableBody>
              {imports.length ? imports.map((im) => (
                <TableRow key={im.id} data-testid={`rex-import-row-${im.id}`}>
                  <TableCell>{moisLabel(im.mois)}</TableCell><TableCell><Badge variant="outline">Format {im.format}</Badge></TableCell>
                  <TableCell className="max-w-[200px] truncate text-muted-foreground">{im.original_filename || im.filename}{im.source === 'seed_reference' ? <Badge variant="outline" className="ml-1 text-[9px]">réf.</Badge> : null}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{fmtDateTime(im.imported_at || im.created_at)}</TableCell>
                  <TableCell className="font-mono text-[11px] text-muted-foreground" title={im.sha256} data-testid={`rex-import-sha-${im.id}`}>{im.sha256 ? <span className="inline-flex items-center gap-1"><Fingerprint className="h-3 w-3" />{im.sha256.slice(0, 12)}…</span> : '—'}</TableCell>
                  <TableCell className="text-right tabular">{eur0(im.summary?.total_cout_employeur)}</TableCell>
                </TableRow>
              )) : <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground" data-testid="rex-imports-empty">Aucun import.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

// =========================================================================== //
//  E3 — FACTURATION (modèle réel factures / avoirs)                            //
// =========================================================================== //
const emptyLine = () => ({ libelle: '', canal: 'SP', region: '', quantite: '', prix_ht: '', prix_modifie: false });
const emptyDoc = (mois) => ({ type: 'facture', identifiant: '', association: 'AMNESTY', date_emission: `${mois}-30`, periode_debut: '', periode_fin: '', mois_production: '', lignes: [emptyLine()] });

const FacturationTab = ({ mois, setMois, monthsAvailable, onGoAmnesty }) => {
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [canaux, setCanaux] = useState([]);
  const [saving, setSaving] = useState(false);
  const [periodInfo, setPeriodInfo] = useState(null);
  const [dupConfirm, setDupConfirm] = useState(false);
  const [assocList, setAssocList] = useState([]);
  const [renameFrom, setRenameFrom] = useState('');
  const [renameTo, setRenameTo] = useState('');
  const [renaming, setRenaming] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, c, a] = await Promise.all([
        api.get('/rex/facturation', { params: { mois } }),
        api.get('/rex/facturation/canaux'),
        api.get('/rex/facturation/associations'),
      ]);
      setList(l.data); setCanaux(c.data.canaux || []); setAssocList(a.data.associations || []);
    } catch (e) { toast.error('Chargement facturation impossible'); }
    setLoading(false);
  }, [mois]);
  useEffect(() => { load(); }, [load]);

  const doRename = async () => {
    if (!renameFrom || !renameTo || renameFrom === renameTo) return;
    setRenaming(true);
    try {
      const { data } = await api.post('/rex/facturation/rename-association', { from: renameFrom, to: renameTo });
      toast.success(`« ${data.from} » → « ${data.to} » : ${data.nb_documents} document(s) fusionné(s), ${data.mois_recalcules.length} mois recalculé(s).`);
      setRenameFrom(''); setRenameTo(''); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Renommage impossible'); }
    setRenaming(false);
  };

  const suggestPrice = async (assoc, canal, date) => {
    try { const { data } = await api.get('/rex/facturation/suggestion', { params: { association: assoc, canal, date_emission: date } }); return data.prix_ht; }
    catch { return null; }
  };

  // Période DÉDUITE automatiquement du seul mois (fonction unique partagée avec la
  // paie indés). Renseigne periode_debut/periode_fin (non saisissables) + méta d'affichage.
  const applyPeriode = async (pm) => {
    if (!pm) { setPeriodInfo(null); return; }
    try {
      const { data } = await api.get('/rex/facturation/periode', { params: { mois: pm } });
      setPeriodInfo(data);
      setEditing((d) => d ? { ...d, periode_mois: pm, periode_debut: data.start || '', periode_fin: data.end || '' } : d);
    } catch { toast.error('Calcul de la période impossible'); }
  };

  const startNew = async () => { const d = emptyDoc(mois); const p = await suggestPrice(d.association, d.lignes[0].canal, d.date_emission); if (p != null) d.lignes[0].prix_ht = p; d.periode_mois = mois; setEditing(d); applyPeriode(mois); };
  const startEdit = (doc) => {
    const pm = doc.periode_mois || (doc.periode_debut ? doc.periode_debut.slice(0, 7) : (doc.date_emission || '').slice(0, 7));
    setEditing({ ...doc, periode_mois: pm, lignes: (doc.lignes || []).map((l) => ({ ...l, quantite: l.quantite, prix_ht: l.prix_ht })) });
    applyPeriode(pm);
  };


  const remove = async (id) => { try { await api.delete(`/rex/facturation/${id}`); toast.success('Document supprimé'); load(); } catch { toast.error('Suppression impossible'); } };

  if (loading) return <Loading />;

  // ---- Vue liste ----
  if (!editing) {
    return (
      <div className="space-y-5" data-testid="rex-facturation">
        <div className="flex items-start justify-between gap-3">
          <SectionTitle icon={ReceiptEuro} desc="Registre de tous les documents, toutes associations. Amnesty peut être généré automatiquement depuis un fichier association ; les autres associations se saisissent ici à la main. Chaque document est une facture ou un avoir ; le CA net du mois = somme des sous-totaux HT (avoirs négatifs), rattachés à la date d'émission.">Facturation — {moisLabel(mois)}</SectionTitle>
          <div className="flex flex-wrap items-center gap-2">
            {onGoAmnesty ? (
              <Button variant="outline" onClick={onGoAmnesty} data-testid="rex-fact-goto-amnesty"><FileSpreadsheet className="mr-1.5 h-4 w-4" />Importer depuis un fichier association Amnesty</Button>
            ) : null}
            <Button onClick={startNew} data-testid="rex-fact-new" style={{ background: 'var(--tenant-accent-500)' }}><Plus className="mr-1.5 h-4 w-4" />Nouveau document</Button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {(monthsAvailable || []).map((ym) => (
            <button key={ym} onClick={() => setMois(ym)} className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${ym === mois ? 'border-transparent text-white' : 'bg-white text-foreground hover:bg-muted'}`} style={ym === mois ? { background: 'var(--tenant-accent-500)' } : {}} data-testid={`rex-fact-month-${ym}`}>{moisShort(ym)}</button>
          ))}
        </div>
        {list.a_doc_amnesty ? (
          <Alert className="border-indigo-200 bg-indigo-50" data-testid="rex-fact-amnesty-note">
            <FileSpreadsheet className="h-4 w-4 text-indigo-600" />
            <AlertTitle className="text-indigo-800">Ce mois contient une facturation générée par import Amnesty</AlertTitle>
            <AlertDescription className="text-sm text-indigo-700">
              Les documents marqués <b>« généré par import »</b> proviennent de l'onglet <b>Facturation Amnesty</b>. Ne recréez pas manuellement une facture/avoir Amnesty pour ce mois — vous créeriez un doublon. Pour modifier, repassez par l'import.
            </AlertDescription>
          </Alert>
        ) : null}
        {(list.doublons || []).length ? (
          <Alert className="border-rose-200 bg-rose-50" data-testid="rex-fact-doublons">
            <AlertTriangle className="h-4 w-4 text-rose-600" />
            <AlertTitle className="text-rose-800">Doublon(s) détecté(s) — le chiffre d'affaires est compté en double</AlertTitle>
            <AlertDescription className="text-sm text-rose-700">
              <p className="mb-2">Des documents identiques (même association, même type, même montant) coexistent pour ce mois. Conservez l'original et supprimez le doublon en un clic.</p>
              <div className="space-y-2">
                {(list.doublons || []).map((g, gi) => (
                  <div key={gi} className="rounded-lg border border-rose-200 bg-white p-3" data-testid={`rex-fact-doublon-${gi}`}>
                    <div className="mb-1.5 text-xs text-rose-900">
                      {g.type === 'avoir' ? 'Avoir' : 'Facture'} · <b>{g.association}</b> · <Money v={g.montant} d={2} /> — {g.nb} documents identiques
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-emerald-800">
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                      À conserver : <span className="font-mono">{g.garder.identifiant || g.garder.id}</span>{g.garder.genere_par_import ? ' (généré par import)' : ''}
                    </div>
                    {(g.supprimer || []).map((s) => (
                      <div key={s.id} className="mt-1.5 flex items-center justify-between gap-2 rounded-md bg-rose-50 px-2 py-1.5">
                        <span className="flex items-center gap-1.5 text-xs text-rose-900">
                          <Trash2 className="h-3.5 w-3.5 text-rose-500" />
                          Doublon : <span className="font-mono">{s.identifiant || s.id}</span>{s.genere_par_import ? ' (import)' : ' (saisi à la main)'}
                        </span>
                        <Button size="sm" variant="destructive" disabled={renaming} onClick={() => remove(s.id)} data-testid={`rex-fact-doublon-del-${s.id}`}>
                          Supprimer le doublon
                        </Button>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </AlertDescription>
          </Alert>
        ) : null}
        {(assocList || []).length > 1 ? (
          <Card className="card-shadow" data-testid="rex-fact-rename-tool">
            <CardContent className="flex flex-wrap items-end gap-3 p-4">
              <div className="flex items-center gap-1.5 text-sm font-medium"><ArrowRightLeft className="h-4 w-4 text-muted-foreground" />Fusionner / renommer une association</div>
              <div className="flex flex-col gap-1">
                <label className="text-xs text-muted-foreground">Association à renommer</label>
                <Select value={renameFrom} onValueChange={setRenameFrom}>
                  <SelectTrigger className="h-9 w-56" data-testid="rex-fact-rename-from"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                  <SelectContent>
                    {(assocList || []).map((a) => (
                      <SelectItem key={a.association} value={a.association}>{a.association} ({a.nb_documents})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <ArrowRightLeft className="mb-2 h-4 w-4 text-muted-foreground" />
              <div className="flex flex-col gap-1">
                <label className="text-xs text-muted-foreground">Nouveau libellé (fusionner vers)</label>
                <Select value={renameTo} onValueChange={setRenameTo}>
                  <SelectTrigger className="h-9 w-56" data-testid="rex-fact-rename-to"><SelectValue placeholder="Choisir un libellé existant…" /></SelectTrigger>
                  <SelectContent>
                    {(assocList || []).filter((a) => a.association !== renameFrom).map((a) => (
                      <SelectItem key={a.association} value={a.association}>{a.association} ({a.nb_documents})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Input value={renameTo} onChange={(e) => setRenameTo(e.target.value)} placeholder="ou saisir un libellé" className="h-9 w-48" data-testid="rex-fact-rename-to-input" />
              <Button onClick={doRename} disabled={renaming || !renameFrom || !renameTo || renameFrom === renameTo} data-testid="rex-fact-rename-apply" style={{ background: 'var(--tenant-accent-500)' }}>
                {renaming ? 'Fusion…' : 'Fusionner'}
              </Button>
            </CardContent>
          </Card>
        ) : null}
        <Card className="card-shadow"><CardContent className="p-0">
          <Table>
            <TableHeader><TableRow><TableHead>Identifiant</TableHead><TableHead>Type</TableHead><TableHead>Association</TableHead><TableHead>Émission</TableHead><TableHead>Période</TableHead><TableHead className="text-right">Sous-total HT</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {(list.documents || []).map((d) => (
                <TableRow key={d.id} data-testid={`rex-fact-doc-${d.id}`}>
                  <TableCell className="font-mono text-xs">
                    {d.identifiant || '—'}
                    {d.genere_par_import ? (
                      <span className="mt-0.5 flex items-center gap-1 text-[10px] font-medium text-indigo-600" title={(d.fichiers_source || []).join(', ')} data-testid={`rex-fact-import-badge-${d.id}`}>
                        <FileSpreadsheet className="h-3 w-3" />généré par import{(d.fichiers_source || []).length ? ` · ${(d.fichiers_source || [])[0]}` : ''}
                      </span>
                    ) : null}
                  </TableCell>
                  <TableCell><Badge variant="secondary" className={d.type === 'avoir' ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}>{d.type === 'avoir' ? 'Avoir' : 'Facture'}</Badge></TableCell>
                  <TableCell>{d.association}</TableCell>
                  <TableCell className="tabular text-sm">{d.date_emission}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{d.periode_debut ? `${d.periode_debut} → ${d.periode_fin || '—'}` : (d.mois_production ? `Prod. ${moisLabel(d.mois_production)}` : '—')}</TableCell>
                  <TableCell className="text-right"><Money v={d.sous_total_ht} d={2} /></TableCell>
                  <TableCell className="text-right">
                    {d.genere_par_import ? (
                      <span className="text-[10px] text-muted-foreground">via import</span>
                    ) : (<>
                      <Button size="icon" variant="ghost" onClick={() => startEdit(d)} data-testid={`rex-fact-edit-${d.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(d.id)} data-testid={`rex-fact-del-${d.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                    </>)}
                  </TableCell>
                </TableRow>
              ))}
              {(list.documents || []).length === 0 ? <TableRow><TableCell colSpan={7} className="py-10 text-center text-muted-foreground" data-testid="rex-fact-empty">Aucun document ce mois-ci. Cliquez « Nouveau document ».</TableCell></TableRow> : null}
            </TableBody>
          </Table>
        </CardContent></Card>
        <div className="flex items-center justify-between rounded-xl border bg-muted/30 px-4 py-3">
          <div className="text-sm text-muted-foreground">{list.nb_factures} facture(s) · {list.nb_avoirs} avoir(s) · {fmtInt(list.volume)} mandats facturables</div>
          <div className="text-sm text-muted-foreground">CA net du mois : <span className="ml-1 font-display text-xl font-semibold text-foreground" data-testid="rex-fact-net"><Money v={list.ca_net} d={2} /></span></div>
        </div>
      </div>
    );
  }

  // ---- Vue création / édition ----
  const doc = editing;
  const setDoc = (patch) => setEditing((d) => ({ ...d, ...patch }));
  const setLine = (i, patch) => setEditing((d) => ({ ...d, lignes: d.lignes.map((l, idx) => idx === i ? { ...l, ...patch } : l) }));
  const addLine = () => setEditing((d) => ({ ...d, lignes: [...d.lignes, emptyLine()] }));
  const delLine = (i) => setEditing((d) => ({ ...d, lignes: d.lignes.filter((_, idx) => idx !== i) }));
  const lineMontant = (l) => { const q = parseFloat(l.quantite); const p = parseFloat(l.prix_ht); if (isNaN(q) || isNaN(p)) return null; return q * p; };
  const sousTotal = doc.lignes.reduce((a, l) => a + (lineMontant(l) || 0), 0);
  const tva = sousTotal * 0.20; const ttc = sousTotal * 1.20;

  const onCanalChange = async (i, canal) => {
    setLine(i, { canal });
    const l = doc.lignes[i];
    if (!l.prix_modifie) { const p = await suggestPrice(doc.association, canal, doc.date_emission); if (p != null) setLine(i, { canal, prix_ht: p }); }
  };
  const isAmnestyLike = (assoc) => /amnesty/i.test(assoc || '');
  const doSave = async () => {
    setDupConfirm(false);
    setSaving(true);
    try { const { data } = await api.post('/rex/facturation', { ...doc, lignes: doc.lignes.map((l) => ({ ...l, quantite: parseFloat(l.quantite) || 0, prix_ht: l.prix_ht === '' ? null : parseFloat(l.prix_ht) })) }); toast.success(`Document enregistré — CA net du mois ${eur2(data.ca_net)}`); setEditing(null); setMois(doc.date_emission.slice(0, 7)); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Enregistrement impossible'); }
    setSaving(false);
  };
  const save = async () => {
    const targetMois = doc.date_emission?.slice(0, 7);
    // Avertissement anti-doublon : création manuelle d'un document Amnesty sur un mois
    // qui a déjà une facturation générée par import.
    if (!doc.id && isAmnestyLike(doc.association) && list?.a_doc_amnesty && targetMois === mois) {
      setDupConfirm(true);
      return;
    }
    await doSave();
  };

  return (
    <div className="space-y-5" data-testid="rex-facturation-form">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => setEditing(null)} data-testid="rex-fact-back"><ArrowLeft className="mr-1 h-4 w-4" />Retour</Button>
        <h2 className="font-display text-lg font-semibold">{doc.id ? 'Éditer le document' : 'Nouveau document'}</h2>
      </div>
      <Card className="card-shadow"><CardContent className="grid grid-cols-1 gap-4 p-5 sm:grid-cols-3">
        <div><label className="mb-1 block text-xs font-medium text-muted-foreground">Type</label>
          <Select value={doc.type} onValueChange={(v) => setDoc({ type: v })}><SelectTrigger className="h-9" data-testid="rex-fact-type"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="facture">Facture</SelectItem><SelectItem value="avoir">Avoir (déduction attrition)</SelectItem></SelectContent></Select></div>
        <div><label className="mb-1 block text-xs font-medium text-muted-foreground">Identifiant</label><Input className="h-9" value={doc.identifiant} placeholder="SHA/2026-06-000003" onChange={(e) => setDoc({ identifiant: e.target.value })} data-testid="rex-fact-ident" /></div>
        <div><label className="mb-1 block text-xs font-medium text-muted-foreground">Association cliente</label><Input className="h-9" value={doc.association} onChange={(e) => setDoc({ association: e.target.value })} data-testid="rex-fact-assoc" /></div>
        <div><label className="mb-1 block text-xs font-medium text-muted-foreground">Date d'émission</label><Input type="date" className="h-9" value={doc.date_emission} onChange={(e) => setDoc({ date_emission: e.target.value })} data-testid="rex-fact-date" /></div>
        {doc.type === 'facture' ? (<>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Mois de la période facturée</label>
            <Input type="month" className="h-9" value={doc.periode_mois || ''} onChange={(e) => applyPeriode(e.target.value)} data-testid="rex-fact-periode-mois" />
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Période facturée (déduite automatiquement — non saisissable)</label>
            <div className="flex h-9 flex-wrap items-center gap-2 rounded-md border bg-muted/30 px-3 text-xs" data-testid="rex-fact-periode-auto">
              <Badge variant="outline" className="gap-1"><CalendarRange className="h-3.5 w-3.5" />{doc.periode_debut && doc.periode_fin ? `${doc.periode_debut} → ${doc.periode_fin}` : '—'}</Badge>
              {periodInfo?.nb_semaines ? <Badge variant="secondary" data-testid="rex-fact-periode-weeks">{periodInfo.nb_semaines} sem.</Badge> : null}
              {periodInfo?.delivery_last_week ? <Badge variant="outline" data-testid="rex-fact-periode-delivery">Livraison dern. sem. {periodInfo.delivery_last_week}</Badge> : null}
              {periodInfo?.prelevement_date ? <Badge variant="outline" data-testid="rex-fact-periode-prelev">Prélèvement {periodInfo.prelevement_date}</Badge> : null}
            </div>
          </div>
        </>) : (
          <div><label className="mb-1 block text-xs font-medium text-muted-foreground">Mois de production concerné</label><Input type="month" className="h-9" value={doc.mois_production || ''} onChange={(e) => setDoc({ mois_production: e.target.value })} data-testid="rex-fact-mprod" /></div>
        )}
        <div className="sm:col-span-3 text-xs text-muted-foreground">Le mois de rattachement au compte de résultat est <b>{moisLabel(doc.date_emission?.slice(0, 7))}</b> (mois de la date d'émission), jamais la période.</div>
      </CardContent></Card>

      <Card className="card-shadow"><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow><TableHead>Libellé</TableHead><TableHead>Canal</TableHead><TableHead>Région</TableHead><TableHead className="text-right">Quantité</TableHead><TableHead className="text-right">PU HT</TableHead><TableHead className="text-right">Montant HT</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {doc.lignes.map((l, i) => (
              <TableRow key={i} data-testid={`rex-fact-line-${i}`}>
                <TableCell><Input className="h-9 min-w-[180px]" value={l.libelle} onChange={(e) => setLine(i, { libelle: e.target.value })} data-testid={`rex-fact-line-lib-${i}`} /></TableCell>
                <TableCell>
                  <Select value={l.canal} onValueChange={(v) => onCanalChange(i, v)}><SelectTrigger className="h-9 w-28" data-testid={`rex-fact-line-canal-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{canaux.map((c) => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}</SelectContent></Select>
                </TableCell>
                <TableCell><Input className="h-9 w-24" value={l.region || ''} placeholder="—" onChange={(e) => setLine(i, { region: e.target.value })} data-testid={`rex-fact-line-region-${i}`} /></TableCell>
                <TableCell><Input type="number" className="h-9 w-24 text-right" value={l.quantite} onChange={(e) => setLine(i, { quantite: e.target.value })} data-testid={`rex-fact-line-qte-${i}`} /></TableCell>
                <TableCell>
                  <Input type="number" step="0.01" className={`h-9 w-28 text-right ${l.prix_modifie ? 'border-amber-400 bg-amber-50' : ''}`} value={l.prix_ht} onChange={(e) => setLine(i, { prix_ht: e.target.value, prix_modifie: true })} data-testid={`rex-fact-line-pu-${i}`} />
                  {l.prix_modifie ? <span className="mt-0.5 block text-[10px] text-amber-600">modifié</span> : null}
                </TableCell>
                <TableCell className="text-right"><Money v={lineMontant(l)} d={2} /></TableCell>
                <TableCell><Button size="icon" variant="ghost" onClick={() => delLine(i)} data-testid={`rex-fact-line-del-${i}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="border-t p-3"><Button variant="outline" size="sm" onClick={addLine} data-testid="rex-fact-add-line"><Plus className="mr-1 h-4 w-4" />Ajouter une ligne</Button></div>
      </CardContent></Card>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-muted/30 px-4 py-3">
        <div className="flex gap-6 text-sm">
          <div>Sous-total HT : <span className="font-medium" data-testid="rex-fact-soustotal"><Money v={sousTotal} d={2} /></span></div>
          <div className="text-muted-foreground">TVA (20 %) : <span className="tabular">{eur2(tva)}</span></div>
          <div className="text-muted-foreground">Total TTC : <span className="tabular">{eur2(ttc)}</span></div>
        </div>
        <Button onClick={save} disabled={saving} data-testid="rex-fact-save" style={{ background: 'var(--tenant-accent-500)' }}><Save className="mr-1.5 h-4 w-4" />Enregistrer le document</Button>
      </div>
      <AlertDialog open={dupConfirm} onOpenChange={setDupConfirm}>
        <AlertDialogContent data-testid="rex-fact-dup-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Doublon Amnesty possible</AlertDialogTitle>
            <AlertDialogDescription>
              Une facturation <b>générée par import Amnesty</b> existe déjà pour {moisLabel(mois)}. Créer manuellement un document « {doc.association} » sur ce mois risque de <b>doubler</b> le chiffre d'affaires. Le chemin recommandé est l'onglet <b>Facturation Amnesty</b>. Voulez-vous quand même enregistrer ce document manuel ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="rex-fact-dup-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={doSave} data-testid="rex-fact-dup-confirm">Enregistrer quand même</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <p className="text-xs text-muted-foreground">Le prix unitaire est pré-rempli depuis la grille tarifaire en vigueur à la date d'émission, mais reste modifiable ligne par ligne (surligné si modifié). La TVA n'entre jamais dans le compte de résultat.</p>
    </div>
  );
};

// =========================================================================== //
//  E4 — FILE D'EXCEPTIONS                                                       //
// =========================================================================== //
const TYPE_LABEL = {
  qonto_sans_label: 'Transaction Qonto sans label', facturation_amnesty: 'Écart / code Amnesty inconnu',
  employe_inconnu: 'Salarié non rapproché', bloc_manquant: 'Bloc fonctionnel manquant', code_reserve: 'Code RD réservé',
  tarif_manquant: 'Tarif manquant', code_campagne_manquant: 'Code campagne manquant',
  salarie_sans_code_rd: 'Salarié sans code RD', adresse_manquante: 'Code postal illisible',
  code_postal_illisible: 'Code postal hors régions (→ Autre)',
};
// Guide de résolution par type : ce que ça veut dire + comment le corriger + où aller.
const EXC_GUIDE = {
  qonto_sans_label: { icon: ReceiptEuro, quoi: "Une transaction Qonto ne porte AUCUN label analytique : elle n'entre dans aucun poste et fausserait le résultat.", resoudre: "Reclassez-la en 1 clic ci-dessous (Frais généraux, Coût Bureau, Exclu…). Idéalement, étiquetez-la dans Qonto : le label remonte tel quel à la prochaine synchro." },
  facturation_amnesty: { icon: Coins, quoi: "Une ligne de facturation Amnesty a un code/canal inconnu ou un écart de montant : le chiffre d'affaires serait faux.", resoudre: "Onglet Facturation Amnesty (ou Administration → Grille tarifaire / Codes campagne) : ajoutez la correspondance manquante, puis relancez le calcul du CA." },
  employe_inconnu: { icon: Users, quoi: "Un bulletin de paie ne correspond à aucune personne du Référentiel.", resoudre: "Utilisez « Rapprocher de [Nom] » ci-dessous : le mois est recalculé aussitôt, sans réimport de la paie." },
  salarie_sans_code_rd: { icon: Users, quoi: "Une personne n'a pas de code RD (AT + 4 chiffres) : son coût n'est pas ventilé par bloc (RD / RE / RP). Bloquant — à traiter avant de clôturer.", resoudre: "Cliquez « Rapprocher de [Nom] — [Code RD] » (matricule → nom → proximité). Création d'un nouveau code seulement en dernier recours. Recalcul immédiat, jamais de réimport." },
  bloc_manquant: { icon: Landmark, quoi: "Le code RD de ce bulletin n'est rattaché à aucun bloc fonctionnel (RD / RE / RP).", resoudre: "Rapprochez la personne d'un code RD valide ci-dessous : recalcul immédiat, sans réimport." },
  code_reserve: { icon: ShieldAlert, quoi: "Le code RD porté est un code réservé (non affectable à un coût).", resoudre: "Rapprochez d'un code RD valide ci-dessous : recalcul immédiat, sans réimport." },
  tarif_manquant: { icon: Coins, quoi: "Une prestation facturée n'a pas de tarif en vigueur pour son canal / association.", resoudre: "Onglet Administration → Grille tarifaire : ajoutez le prix HT du canal concerné, à la bonne date de début." },
  code_campagne_manquant: { icon: Hash, quoi: "Un code campagne du fichier terrain est absent de la table des codes.", resoudre: "Onglet Administration → Codes campagne : ajoutez ou ré-importez le code manquant." },
  adresse_manquante: { icon: Home, quoi: "Le bulletin n'a pas de code postal exploitable : aucune région ne peut être déterminée. Le coût reste compté.", resoudre: "Information : à compléter côté paie quand c'est possible. N'empêche pas la clôture." },
  code_postal_illisible: { icon: MapPin, quoi: "Le code postal ne correspond à aucune région connue : la personne tombe en « Autre ». Le coût reste compté.", resoudre: "Information (non bloquant) : rattachez le département à une région via Administration → Régions si nécessaire, ou laissez « Autre »." },
};
// Destinations proposées pour reclasser un label Qonto « sans correspondance » en 1 clic.
const RECLASS_POSTES = [
  ['frais_generaux', 'Frais généraux'], ['cout_bureau', 'Coût Bureau'],
  ['rd_independants', 'Coût RD (indés)'], ['cout_re', 'Coût RE'], ['cout_rp', 'Coût RP'],
  ['exclu', 'Exclu du compte de résultat'],
];

// Carte d'UNE exception, avec ses actions 1-clic INTÉGRÉES (reclasser / rapprocher /
// marquer traitée) — tout se règle ici, sans changer d'onglet.
const ExceptionCard = ({ e, mois, onChanged }) => {
  const g = EXC_GUIDE[e.type] || {};
  const Gi = g.icon || AlertTriangle;
  const [busy, setBusy] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const isQontoLabel = e.type === 'qonto_sans_label';
  const isRappro = ['salarie_sans_code_rd', 'employe_inconnu', 'code_reserve'].includes(e.type);
  const isBloc = e.type === 'bloc_manquant';
  const label = e.payload?.analytical_label || e.payload?.label_txn;

  const reclasser = async (poste) => {
    setBusy(true);
    try { await api.post('/rex/labels/reclasser', { label, poste, mois });
      toast.success(`« ${label} » → ${DEST_LABEL[poste] || poste}`); onChanged && onChanged(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Action impossible'); }
    setBusy(false);
  };
  const rapprocher = async (body) => {
    setBusy(true);
    const okMsg = body.action === 'set_bloc' ? `Coût rangé en ${body.bloc} — mois recalculé (décision rejouée sur les mois suivants)`
      : body.action === 'assign_code_rd' ? `Code ${body.code_rd} attribué — mois recalculé`
      : 'Rapprochement appliqué — mois recalculé';
    try { await api.post(`/rex/exceptions/${e.id}/rapprocher`, body);
      toast.success(okMsg); onChanged && onChanged(); }
    catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || `Action refusée (erreur ${err?.response?.status || '?'}) — raison non transmise par le serveur.`));
    }
    setBusy(false);
  };
  const close = async () => {
    setBusy(true);
    try { await api.post(`/rex/exceptions/${e.id}/close`); toast.success('Exception marquée comme traitée'); onChanged && onChanged(); }
    catch (err) { toast.error('Action impossible'); }
    setBusy(false);
  };

  return (
    <Card className="card-shadow" data-testid={`rex-exc-${e.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="mb-1.5 flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className={`gap-1 ${e.blocking ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-600'}`}><Gi className="h-3 w-3" />{e.type_label || TYPE_LABEL[e.type] || e.type}</Badge>
              {e.blocking ? <Badge className="bg-rose-600 text-[10px] hover:bg-rose-600">bloquant</Badge> : <Badge variant="outline" className="text-[10px]">information</Badge>}
              {isQontoLabel && e.payload?.montant_ttc != null ? <span className="text-xs text-muted-foreground">{eur2(e.payload.montant_ttc)} TTC · {e.payload?.counterparty || e.payload?.label_txn}</span> : null}
            </div>
            <p className="text-sm font-medium text-foreground">{e.message}</p>
            {g.quoi ? <p className="mt-1 text-xs text-muted-foreground"><b>Ce que ça veut dire :</b> {g.quoi}</p> : null}
            {e.payload?.cout_employeur != null ? <p className="mt-1 text-xs text-muted-foreground">Coût employeur : {eur2(e.payload.cout_employeur)}</p> : null}
          </div>
          <Button size="sm" variant="ghost" className="shrink-0 text-muted-foreground" onClick={close} disabled={busy} title="Retirer de la liste sans autre action" data-testid={`rex-exc-close-${e.id}`}>Marquer traitée</Button>
        </div>

        {/* Action 1-clic : reclasser un label Qonto sans correspondance */}
        {isQontoLabel ? (
          <div className="mt-3 rounded-lg border border-sky-200 bg-sky-50/50 p-2.5 dark:border-sky-900/40 dark:bg-sky-950/20" data-testid={`rex-exc-reclass-${e.id}`}>
            <p className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-sky-800 dark:text-sky-300"><ArrowRightLeft className="h-3.5 w-3.5" />Décision 1 clic — envoyer « {label} » vers :</p>
            <div className="flex flex-wrap gap-1.5">
              {RECLASS_POSTES.map(([val, lbl]) => (
                <Button key={val} size="sm" variant="outline" className="h-8" disabled={busy} onClick={() => reclasser(val)} data-testid={`rex-exc-reclass-${e.id}-${val}`}>{lbl}</Button>
              ))}
            </div>
          </div>
        ) : null}

        {/* Action 1-clic : ranger dans un bloc fonctionnel (bloc_manquant). Le bloc
            est hérité des mois précédents s'il existe ; ce choix ne se pose donc que
            pour un code réellement nouveau, et vaut ensuite pour les mois suivants. */}
        {isBloc ? (
          <div className="mt-3 rounded-lg border border-indigo-200 bg-indigo-50/50 p-2.5 dark:border-indigo-900/40 dark:bg-indigo-950/20" data-testid={`rex-exc-bloc-${e.id}`}>
            <p className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-indigo-800 dark:text-indigo-300"><Landmark className="h-3.5 w-3.5" />Ranger le coût dans un bloc — 1 clic, sans réimport (la décision est définitive et rejouée sur les mois suivants) :</p>
            <div className="flex flex-wrap gap-1.5">
              {[['RD', 'Recherche & Développement'], ['RE', 'Recherche & Exploitation'], ['RP', 'Recherche Publique']].map(([bl, full]) => (
                <Button key={bl} size="sm" className="h-8 bg-indigo-600 text-white hover:bg-indigo-700" disabled={busy} onClick={() => rapprocher({ action: 'set_bloc', bloc: bl })} data-testid={`rex-exc-bloc-${e.id}-${bl}`} title={full}>Ranger en {bl}</Button>
              ))}
            </div>
            <p className="mt-1.5 text-[11px] text-muted-foreground">Le code RD ({e.payload?.code_rd || 'AT+4 chiffres'}) ne change aucun montant de bulletin : il ne sert qu'à ventiler le coût dans un bloc.</p>
          </div>
        ) : null}

        {/* Action 1-clic : rapprochement intelligent (P2) */}
        {isRappro ? (
          <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50/50 p-2.5 dark:border-emerald-900/40 dark:bg-emerald-950/20" data-testid={`rex-exc-rappro-${e.id}`}>
            <p className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-emerald-800 dark:text-emerald-300"><Users className="h-3.5 w-3.5" />Rapprochements proposés (matricule → nom → proximité) — validez en 1 clic, sans réimport :</p>
            {(e.suggestions || []).length ? (
              <div className="flex flex-wrap gap-1.5">
                {(e.suggestions || []).map((s, i) => (
                  <Button key={i} size="sm" className="h-8 bg-emerald-600 text-white hover:bg-emerald-700" disabled={busy}
                    onClick={() => rapprocher(s.action === 'assign_code_rd' ? { action: 'assign_code_rd', code_rd: s.code_rd } : { action: 'link_person', key: s.key })}
                    data-testid={`rex-exc-rappro-${e.id}-sugg-${i}`}>
                    <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
                    Rapprocher de {s.nom}{s.code_rd ? ` — ${s.code_rd}` : ''}
                    <span className="ml-1 opacity-70">· {s.via === 'matricule' ? 'matricule' : s.via === 'proximité' ? `proximité ${Math.round((s.score || 0) * 100)}\u202f%` : 'nom'}</span>
                  </Button>
                ))}
              </div>
            ) : <p className="text-xs text-muted-foreground">Aucune correspondance dans le référentiel — <b>en dernier recours</b>, saisissez le code RD (AT + 4 chiffres) à attribuer :</p>}
            <div className="mt-2 flex items-center gap-1.5">
              <Input value={manualCode} onChange={(ev) => setManualCode(ev.target.value.toUpperCase())} placeholder="AT + 4 chiffres" className="h-8 w-40" data-testid={`rex-exc-rappro-${e.id}-manual`} />
              <Button size="sm" variant="outline" className="h-8" disabled={busy || !manualCode.trim()} onClick={() => rapprocher({ action: 'assign_code_rd', code_rd: manualCode.trim() })} data-testid={`rex-exc-rappro-${e.id}-manual-apply`}>Attribuer ce code (dernier recours)</Button>
            </div>
          </div>
        ) : null}

        {g.resoudre && !isQontoLabel && !isRappro ? (
          <p className="mt-2.5 flex items-start gap-1.5 rounded-lg border border-border/60 bg-muted/30 px-2.5 py-1.5 text-xs text-muted-foreground">
            <ListChecks className="mt-0.5 h-3.5 w-3.5 shrink-0" /><span><b>Comment résoudre :</b> {g.resoudre}</span>
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
};

const ExceptionsTab = () => {
  const [months, setMonths] = useState([]);
  const [mois, setMois] = useState(null);
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  const loadMonths = useCallback(async () => {
    try {
      const { data } = await api.get('/rex/cloture/status');
      const ms = data.months || [];
      setMonths(ms);
      setMois((cur) => {
        if (cur) return cur;
        const live = ms.filter((m) => !m.frozen);
        const openLive = live.filter((m) => !m.closed);
        const def = openLive[openLive.length - 1] || live[live.length - 1] || ms[ms.length - 1];
        return def ? def.mois : null;
      });
    } catch (e) { /* noop */ }
  }, []);
  const loadBoard = useCallback(async () => {
    if (!mois) return;
    setLoading(true);
    try { const { data } = await api.get('/rex/exceptions/board', { params: { mois } }); setBoard(data); }
    catch (e) { toast.error('Chargement des exceptions impossible'); }
    setLoading(false);
  }, [mois]);
  useEffect(() => { loadMonths(); }, [loadMonths]);
  useEffect(() => { loadBoard(); }, [loadBoard]);

  const refresh = () => { loadBoard(); loadMonths(); };
  const closeMonth = async () => {
    setClosing(true);
    try { await api.post(`/rex/months/${mois}/close`); toast.success(`${moisLabel(mois)} clôturé ✓`); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Clôture impossible'); }
    setClosing(false);
  };
  const recomputeQonto = async () => {
    try { await api.post(`/rex/months/${mois}/recompute-qonto`); toast.success('Qonto recalculé pour le mois'); loadBoard(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Recalcul impossible'); }
  };

  const closure = board?.closure;
  const blocking = board?.blocking || [];
  const info = board?.info || [];

  return (
    <div className="space-y-5" data-testid="rex-exceptions">
      <SectionTitle icon={AlertTriangle} desc="Tout se traite ICI : rapprocher, reclasser, puis clôturer — sans changer d'onglet ni réimporter la paie.">Exceptions & clôture du mois</SectionTitle>

      {/* Sélecteur de mois — « je vois toujours sur quel mois j'agis » */}
      <Card className="card-shadow" data-testid="rex-exceptions-monthbar">
        <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
          <div className="flex items-center gap-3">
            <div>
              <p className="text-xs font-medium text-muted-foreground">Mois de travail</p>
              <MonthPicker value={mois} onChange={setMois} months={months} testid="rex-exceptions-month" />
            </div>
            <div className="text-sm">
              <span className="text-muted-foreground">Vous agissez sur : </span>
              <span className="font-semibold text-foreground" data-testid="rex-exceptions-current-month">{moisLabel(mois)}</span>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={recomputeQonto} data-testid="rex-exceptions-recompute" title="Ré-applique le mapping des labels Qonto sur ce mois"><RefreshCw className="mr-1.5 h-3.5 w-3.5" />Recalculer Qonto — {moisLabel(mois)}</Button>
        </CardContent>
      </Card>

      {/* Rapprochement automatique depuis le Suivi Social (comptes recruteurs). */}
      {board?.nb_auto_rapproches ? (
        <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-xs text-emerald-800 dark:border-emerald-900/40 dark:bg-emerald-950/20 dark:text-emerald-300" data-testid="rex-exceptions-autolink">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          <span><b>{board.nb_auto_rapproches} salarié(s) rapproché(s) automatiquement</b> depuis les comptes recruteurs du Suivi Social (rapprochement par le nom, code recruteur repris) — aucun réimport, aucune saisie.</span>
        </div>
      ) : null}

      {/* Panneau de clôture (blocages live) */}
      {closure ? (
        <Card className={`card-shadow border-2 ${closure.closable ? 'border-emerald-300' : 'border-amber-300'}`} data-testid="rex-closure-panel">
          <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
            <div className="min-w-0 flex-1">
              {closure.closable ? (
                <p className="flex items-center gap-2 text-sm font-semibold text-emerald-700 dark:text-emerald-300" data-testid="rex-closure-ready"><CheckCircle2 className="h-5 w-5" />{moisLabel(mois)} est prêt à être clôturé — aucun blocage.</p>
              ) : (
                <>
                  <p className="flex items-center gap-2 text-sm font-semibold text-amber-700 dark:text-amber-300"><ShieldAlert className="h-5 w-5" />{moisLabel(mois)} n'est pas encore clôturable :</p>
                  <ul className="mt-1.5 space-y-1" data-testid="rex-closure-reasons">
                    {(closure.reasons || []).map((r, i) => (
                      <li key={i} className="flex items-center gap-1.5 text-xs text-muted-foreground"><Ban className="h-3 w-3 text-amber-600" />{r.message}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>
            <Button onClick={closeMonth} disabled={!closure.closable || closing} data-testid="rex-closure-close-btn"
              className={closure.closable ? 'bg-emerald-600 text-white hover:bg-emerald-700' : ''} style={closure.closable ? {} : undefined}>
              <Lock className="mr-2 h-4 w-4" />Clôturer {moisLabel(mois)}
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {loading ? <Loading /> : (
      <>
      {/* À TRAITER POUR CLÔTURER (bloquant) */}
      <div data-testid="rex-exceptions-blocking">
        <div className="mb-2 flex items-center gap-2">
          <h3 className="text-sm font-semibold text-foreground">À traiter pour clôturer</h3>
          <Badge className="bg-rose-600 text-white hover:bg-rose-600">{blocking.length}</Badge>
          <span className="text-xs text-muted-foreground">— ces points empêchent un compte de résultat juste</span>
        </div>
        {blocking.length ? (
          <div className="space-y-3">{blocking.map((e) => <ExceptionCard key={e.id} e={e} mois={mois} onChanged={refresh} />)}</div>
        ) : (
          <Card className="card-shadow"><CardContent className="py-6 text-center text-sm text-muted-foreground" data-testid="rex-exceptions-blocking-empty"><CheckCircle2 className="mx-auto mb-1.5 h-6 w-6 text-emerald-500" />Aucune exception bloquante — rien n'empêche la clôture de ce point de vue.</CardContent></Card>
        )}
      </div>

      {/* POUR INFORMATION (non bloquant) */}
      <div data-testid="rex-exceptions-info">
        <div className="mb-2 flex items-center gap-2">
          <h3 className="text-sm font-semibold text-foreground">Pour information</h3>
          <Badge variant="outline">{info.length}</Badge>
          <span className="text-xs text-muted-foreground">— le résultat est correct ; à renseigner quand c'est possible (n'empêche pas la clôture)</span>
        </div>
        {info.length ? (
          <div className="space-y-3">{info.map((e) => <ExceptionCard key={e.id} e={e} mois={mois} onChanged={refresh} />)}</div>
        ) : (
          <Card className="card-shadow"><CardContent className="py-6 text-center text-sm text-muted-foreground" data-testid="rex-exceptions-info-empty">Rien à signaler.</CardContent></Card>
        )}
      </div>
      </>
      )}

      {/* Guide (replié par défaut) */}
      <Card className="card-shadow" data-testid="rex-exceptions-guide">
        <CardHeader className="cursor-pointer pb-2" onClick={() => setShowGuide((s) => !s)}>
          <CardTitle className="flex items-center gap-2 text-base"><ChevronRight className={`h-4 w-4 transition-transform ${showGuide ? 'rotate-90' : ''}`} /><ListChecks className="h-4 w-4" />Guide : les types d'exceptions et leur résolution</CardTitle>
        </CardHeader>
        {showGuide ? (
          <CardContent className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
            {Object.entries(EXC_GUIDE).map(([type, g]) => {
              const Gi = g.icon || AlertTriangle;
              return (
                <div key={type} className="rounded-lg border border-border/60 bg-muted/20 p-3" data-testid={`rex-exc-guide-${type}`}>
                  <div className="mb-1 flex items-center gap-1.5 text-sm font-medium"><Gi className="h-3.5 w-3.5 text-muted-foreground" />{TYPE_LABEL[type] || type}</div>
                  <p className="text-xs text-muted-foreground">{g.quoi}</p>
                  <p className="mt-1 text-xs text-emerald-700 dark:text-emerald-300"><b>Résolution :</b> {g.resoudre}</p>
                </div>
              );
            })}
          </CardContent>
        ) : null}
      </Card>
    </div>
  );
};

// =========================================================================== //
//  E5 — RÉFÉRENTIEL                                                             //
// =========================================================================== //
const ANO_LABEL = { prenom_double: 'Prénom en double', doublon_rapprochement: 'Doublon possible', nom_incomplet: 'Nom incomplet' };
const DEC_LABEL = { distinctes: 'Deux personnes distinctes', fusionner: 'Fusion (différée)', ignorer: 'Vérifié — ne plus signaler' };

const ReferentielTab = () => {
  const [pers, setPers] = useState(null);
  const [reserved, setReserved] = useState(null);
  const [doublons, setDoublons] = useState([]);
  const [vent, setVent] = useState({ juin: null, juillet: null });
  const [casse, setCasse] = useState(null);        // P6 — casse des canaux
  const [partage, setPartage] = useState(null);    // P7 — analyse de partage rex_personnes
  const [decisions, setDecisions] = useState([]);  // P5 — anomalies déjà tranchées
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);          // clé de l'action en cours (1 clic)
  const [showRef, setShowRef] = useState(false);   // P8 — bloc « référence & stats » replié
  const [showDecided, setShowDecided] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, r, d, vj, vjl, cz, pa, dec] = await Promise.all([
        api.get('/rex/personnes'), api.get('/rex/codes-reserves'), api.get('/rex/doublons'),
        api.get('/rex/ventilation', { params: { mois: '2026-06' } }),
        api.get('/rex/ventilation', { params: { mois: '2026-07' } }),
        api.get('/rex/canaux/casse-report'),
        api.get('/rex/personnes/partage-analyse'),
        api.get('/rex/anomalies-noms/decisions'),
      ]);
      setPers(p.data); setReserved(r.data); setDoublons(d.data.doublons_social_collaborateurs || []);
      setVent({ juin: vj.data, juillet: vjl.data });
      setCasse(cz.data); setPartage(pa.data); setDecisions(dec.data.decisions || []);
    } catch (e) { /* noop */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  // P5 — résolution 1-clic PERMANENTE (mémorisée en base ; jamais de fusion automatique).
  const resolveAnomalie = async (a, decision) => {
    const bkey = `${a.type}|${a.ref}|${decision}`;
    setBusy(bkey);
    try {
      const { data } = await api.post('/rex/anomalies-noms/resolve', { type: a.type, ref: a.ref, decision });
      toast.success(data.message || 'Décision enregistrée.');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action impossible'); }
    setBusy(null);
  };
  const reopenAnomalie = async (d) => {
    const bkey = `reopen|${d.anomalie_type}|${d.ref}`;
    setBusy(bkey);
    try {
      await api.post('/rex/anomalies-noms/decisions/reset', { type: d.anomalie_type, ref: d.ref });
      toast.success('Anomalie ré-ouverte.');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Réouverture impossible'); }
    setBusy(null);
  };
  // P6 — normalisation de casse (1 clic). Pour un groupe ambigu, `fact` = canal de
  // facturation choisi (arbitrage d'impact financier explicite par l'utilisateur).
  const normaliserCasse = async (g, fact) => {
    const bkey = `casse|${g.key}|${fact}`;
    setBusy(bkey);
    try {
      const { data } = await api.post('/rex/canaux/normaliser-casse', {
        key: g.key, canonical_terrain: g.canonical_suggested, canal_facturation: fact });
      toast.success(`Casse normalisée : « ${data.canonical} » → ${data.canal_facturation} (${data.nb_variantes_fusionnees} variante(s) fusionnée(s)).`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Normalisation impossible'); }
    setBusy(null);
  };

  if (loading || !pers) return <Loading />;
  const personnes = pers.personnes || [];
  const anomalies = pers.anomalies_noms || [];
  const casseGroupes = casse?.groupes || [];

  const VentCard = ({ title, data }) => (
    <Card className="card-shadow">
      <CardHeader className="pb-2"><CardTitle className="text-base">Ventilation par région — {title}</CardTitle>
        <CardDescription>Depuis l'adresse salarié (jamais l'employeur 95880 Enghien).</CardDescription></CardHeader>
      <CardContent className="space-y-1 text-sm">
        {(data?.ventilation_regions || []).map((r) => (
          <div key={r.region} className="flex items-center justify-between border-b py-1.5" data-testid={`rex-vent-${title}-${r.region}`}>
            <span>{r.region}<span className="ml-2 text-xs text-muted-foreground">{r.nb} pers.</span></span>
            <Money v={r.cout} d={2} />
          </div>
        ))}
        {(data?.adresses_manquantes || []).length ? (
          <p className="pt-2 text-xs text-amber-600">Adresse non extractible (→ exception) : {(data.adresses_manquantes || []).map((a) => a.nom).join(', ')}.</p>
        ) : <p className="pt-2 text-xs text-muted-foreground">Aucune adresse manquante.</p>}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6" data-testid="rex-referentiel">
      <SectionTitle icon={Users} desc="Rapprochement bulletin→personne : matricule de paie puis nom normalisé. Le code RD (AT+4 chiffres) n'apparaît sur aucun bulletin, ne sert jamais au rapprochement et n'est jamais normalisé (O ≠ 0).">Référentiel des personnes</SectionTitle>

      {/* ================= ACTIONS À TRAITER (en haut — 0 clic inutile) ================= */}
      <div className="flex items-center gap-2" data-testid="rex-ref-actions-header">
        <ListChecks className="h-4 w-4 text-[color:var(--tenant-accent-600)]" />
        <h3 className="text-sm font-semibold text-foreground">Actions à traiter</h3>
        <Badge className="bg-rose-600 text-white hover:bg-rose-600" data-testid="rex-ref-actions-count">{anomalies.length + casseGroupes.length}</Badge>
        <span className="text-xs text-muted-foreground">— tranchez en 1 clic ; l'outil ne devine jamais</span>
      </div>

      {/* P5 — anomalies de noms : résolution 1-clic PERMANENTE */}
      <Card className="card-shadow" data-testid="rex-anomalies-noms">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-amber-600" />Anomalies de noms à trancher ({anomalies.length})</CardTitle>
          <CardDescription>Décision <b>permanente en 1 clic</b> (mémorisée) — <b>jamais de fusion automatique</b>. « Fusionner » enregistre l'intention : la fusion réelle est différée (chantier référentiel).</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {anomalies.length ? anomalies.map((a, i) => (
            <div key={i} className="flex flex-wrap items-start justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50/50 p-2.5 text-sm" data-testid={`rex-anomalie-${a.type}`}>
              <div className="flex min-w-0 items-start gap-2">
                <Badge variant="outline" className="shrink-0 border-amber-300 text-amber-700 text-[10px]">{ANO_LABEL[a.type] || a.type}</Badge>
                <div className="min-w-0"><p className="font-medium">{a.nom}</p><p className="text-xs text-muted-foreground">{a.detail}</p></div>
              </div>
              <div className="flex shrink-0 flex-wrap gap-1.5">
                {a.type === 'doublon_rapprochement' ? (
                  <>
                    <Button size="sm" variant="outline" disabled={busy === `${a.type}|${a.ref}|distinctes`} onClick={() => resolveAnomalie(a, 'distinctes')} data-testid={`rex-anomalie-distinctes-${i}`}><Users className="mr-1 h-3.5 w-3.5" />Deux personnes distinctes</Button>
                    <Button size="sm" variant="outline" className="border-sky-300 text-sky-700 hover:text-sky-800" disabled={busy === `${a.type}|${a.ref}|fusionner`} onClick={() => resolveAnomalie(a, 'fusionner')} data-testid={`rex-anomalie-fusionner-${i}`}><ArrowRightLeft className="mr-1 h-3.5 w-3.5" />Fusionner</Button>
                  </>
                ) : (
                  <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:text-emerald-800" disabled={busy === `${a.type}|${a.ref}|ignorer`} onClick={() => resolveAnomalie(a, 'ignorer')} data-testid={`rex-anomalie-ok-${i}`}><CheckCircle2 className="mr-1 h-3.5 w-3.5" />C'est correct</Button>
                )}
              </div>
            </div>
          )) : (
            <p className="flex items-center gap-2 py-4 text-sm text-emerald-700 dark:text-emerald-300" data-testid="rex-anomalies-empty"><CheckCircle2 className="h-4 w-4" />Aucune anomalie de nom en attente — tout est tranché.</p>
          )}
          {decisions.length ? (
            <div className="pt-1">
              <button className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground" onClick={() => setShowDecided((s) => !s)} data-testid="rex-anomalies-decided-toggle">
                <ChevronRight className={`h-3.5 w-3.5 transition-transform ${showDecided ? 'rotate-90' : ''}`} />{decisions.length} anomalie(s) déjà tranchée(s)
              </button>
              {showDecided ? (
                <div className="mt-1.5 space-y-1">
                  {decisions.map((d, i) => (
                    <div key={i} className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-muted/20 px-2.5 py-1.5 text-xs" data-testid={`rex-anomalie-decided-${i}`}>
                      <span><span className="font-medium">{d.ref}</span> · {DEC_LABEL[d.decision] || d.decision}</span>
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" disabled={busy === `reopen|${d.anomalie_type}|${d.ref}`} onClick={() => reopenAnomalie(d)} data-testid={`rex-anomalie-reopen-${i}`}><RotateCcw className="mr-1 h-3 w-3" />Réouvrir</Button>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* P6 — normalisation de la CASSE des canaux (+ arbitrage d'impact financier) */}
      <Card className="card-shadow" data-testid="rex-canaux-casse">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><ArrowRightLeft className="h-4 w-4" />Casse des canaux à normaliser ({casseGroupes.length})</CardTitle>
          <CardDescription>Variantes ne différant que par la <b>casse / les espaces</b>. Si toutes pointent vers le même canal de facturation → normalisation en 1 clic (aucun impact). Sinon → vous <b>arbitrez</b> le canal à conserver (prix HT par mandat).</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {casseGroupes.length ? casseGroupes.map((g) => {
            const gid = (g.key || '').replace(/\s+/g, '-');
            return (
              <div key={g.key} className="rounded-lg border p-3" data-testid={`rex-casse-${gid}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">« {g.canonical_suggested} »</p>
                    <p className="text-xs text-muted-foreground">{g.variants.length} variante(s) : {g.variants.map((v) => `« ${v.canal_terrain} » → ${v.canal_facturation}`).join(' · ')}</p>
                  </div>
                  {g.ambigu
                    ? <Badge variant="outline" className="border-amber-300 text-amber-700"><Coins className="mr-1 h-3 w-3" />Impact financier — arbitrage requis</Badge>
                    : <Badge variant="outline" className="border-emerald-300 text-emerald-700">Aucun impact financier</Badge>}
                </div>
                {g.ambigu ? (
                  <div className="mt-2 space-y-1.5">
                    <p className="text-xs text-muted-foreground">Ces variantes pointent vers des canaux de facturation <b>différents</b>. Choisissez celui à conserver :</p>
                    <div className="flex flex-wrap gap-2">
                      {g.options.map((o) => (
                        <Button key={o.canal_facturation} size="sm" variant="outline" disabled={busy === `casse|${g.key}|${o.canal_facturation}`} onClick={() => normaliserCasse(g, o.canal_facturation)} data-testid={`rex-casse-opt-${gid}-${o.canal_facturation}`}>
                          Conserver {o.canal_facturation} — {isNum(o.prix_ht) ? `${eur2(o.prix_ht)} HT/mandat` : 'tarif inconnu'}
                        </Button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="mt-2">
                    <Button size="sm" disabled={busy === `casse|${g.key}|${g.facturations[0]}`} onClick={() => normaliserCasse(g, g.facturations[0])} data-testid={`rex-casse-normaliser-${gid}`} style={{ background: 'var(--tenant-accent-500)' }}>
                      <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />Normaliser en 1 clic → {g.facturations[0]}
                    </Button>
                  </div>
                )}
              </div>
            );
          }) : (
            <p className="flex items-center gap-2 py-4 text-sm text-emerald-700 dark:text-emerald-300" data-testid="rex-casse-empty"><CheckCircle2 className="h-4 w-4" />Aucune variante de casse à corriger.</p>
          )}
        </CardContent>
      </Card>

      {/* Alertes actionnables : codes RD manquants / valeurs aberrantes */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Alert className={pers.nb_sans_code_rd ? 'border-amber-200' : 'border-emerald-200'} data-testid="rex-sans-code-rd">
          <FileWarning className="h-4 w-4" /><AlertTitle>{pers.nb_sans_code_rd} salarié(s) sans code RD</AlertTitle>
          <AlertDescription className="text-sm">Ces personnes figurent en file d'exceptions (« salarié sans code RD ») pour attribution d'un code AT+4 chiffres. {(pers.sans_code_rd || []).slice(0, 6).map((s) => s.nom).join(', ')}{(pers.sans_code_rd || []).length > 6 ? '…' : ''}</AlertDescription>
        </Alert>
        <Alert className={pers.valeurs_aberrantes?.length ? 'border-rose-200' : 'border-emerald-200'} data-testid="rex-aberrants">
          {pers.valeurs_aberrantes?.length ? <XCircle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
          <AlertTitle>Valeurs aberrantes dans « Code RD » : {pers.valeurs_aberrantes?.length || 0}</AlertTitle>
          <AlertDescription className="text-sm">{pers.valeurs_aberrantes?.length ? `Listées sans correction : ${pers.valeurs_aberrantes.join(', ')}.` : 'Aucune valeur non conforme (AT+4 chiffres) dans la colonne Code RD.'}</AlertDescription>
        </Alert>
      </div>

      {/* ================= ANALYSE & RAPPORTS (P7 — RAPPORT SEUL) ================= */}
      {partage ? (
        <Card className="card-shadow" data-testid="rex-partage-analyse">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><FileSearch className="h-4 w-4" />Analyse de partage du référentiel « rex_personnes »</CardTitle>
            <CardDescription>Rapport en <b>lecture seule</b> — <b>aucune fusion effectuée dans ce lot</b>. La décision de fusionner est réservée au CEO.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              {partage.partage_avec_autres_modules
                ? <Badge variant="destructive" data-testid="rex-partage-badge">Partagé avec d'autres modules</Badge>
                : <Badge className="bg-emerald-600 text-white hover:bg-emerald-600" data-testid="rex-partage-badge">Isolé — périmètre REX uniquement</Badge>}
              <Badge variant="outline">{partage.nb_rex_personnes} fiches</Badge>
              <Badge variant="outline">{partage.nb_rex_personnes_histo} ligne(s) d'historisation</Badge>
              <Badge variant="outline" className="border-amber-300 text-amber-700" data-testid="rex-partage-risque">Risque : {partage.risque}</Badge>
            </div>
            <div className="space-y-2 rounded-lg border bg-muted/20 p-3">
              <p><b>Conclusion.</b> {partage.conclusion}</p>
              {partage.pont_avec_social ? (
                <p className="text-xs text-muted-foreground"><b>Pont avec le social :</b> {partage.pont_avec_social.champ_rex} ↔ {partage.pont_avec_social.champ_externe} ({partage.pont_avec_social.nb_collaborateurs_social} collaborateurs). {partage.pont_avec_social.note}</p>
              ) : null}
              {(partage.dependances_internes_rex || []).length ? (
                <div className="text-xs text-muted-foreground">
                  <b>Dépendances internes REX :</b>
                  <ul className="mt-1 list-disc pl-5">
                    {partage.dependances_internes_rex.map((dep, i) => (<li key={i}>{dep.collection} — {dep.lien} ({dep.nb}) · {dep.impact_fusion}</li>))}
                  </ul>
                </div>
              ) : null}
            </div>
            <Alert className="border-sky-200 bg-sky-50/50 dark:bg-sky-500/5" data-testid="rex-partage-reco">
              <Info className="h-4 w-4 text-sky-600" />
              <AlertTitle className="text-sky-800 dark:text-sky-300">Recommandation (chantier dédié — non exécuté ici)</AlertTitle>
              <AlertDescription className="text-sm text-sky-700 dark:text-sky-300">{partage.recommandation}</AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      ) : null}

      {/* ================= RÉFÉRENCE & STATISTIQUES (replié par défaut — P8) ================= */}
      <Card className="card-shadow" data-testid="rex-reference-block">
        <CardHeader className="cursor-pointer pb-2" onClick={() => setShowRef((s) => !s)}>
          <CardTitle className="flex items-center gap-2 text-base"><ChevronRight className={`h-4 w-4 transition-transform ${showRef ? 'rotate-90' : ''}`} /><Table2 className="h-4 w-4" />Référence &amp; statistiques (consultation)</CardTitle>
          <CardDescription>Codes réservés, table des personnes, ventilation régionale et doublons — replié pour garder les actions en tête.</CardDescription>
        </CardHeader>
        {showRef ? (
          <CardContent className="space-y-6">
            {reserved ? (
              <Alert data-testid="rex-reserved"><ShieldAlert className="h-4 w-4" /><AlertTitle>Codes RD réservés (garde bloquante — jamais résolus automatiquement)</AlertTitle>
                <AlertDescription className="text-sm">
                  <div className="mt-1 flex flex-wrap gap-2">{reserved.reserved_codes.map((c) => <Badge key={c} variant="destructive">{c}</Badge>)}<Badge variant="outline">Prochain disponible : {reserved.next_available}</Badge></div>
                  <p className="mt-2 text-xs text-muted-foreground">Codes canoniques préservés (jamais corrigés) : {Object.keys(reserved.canonical).join(', ')}. Codes présents en base mais absents du fichier 2026 (listés sans rattachement) : {reserved.codes_absents_reference_2026.join(', ')}.</p>
                </AlertDescription></Alert>
            ) : null}

            <Card className="card-shadow">
              <CardHeader className="pb-2"><CardTitle className="text-base">Personnes ({personnes.length})</CardTitle>
                <CardDescription>Code RD et Matricule de paie sont deux champs distincts, jamais confondus.</CardDescription></CardHeader>
              <CardContent className="p-0"><div className="max-h-[460px] overflow-auto">
                <Table>
                  <TableHeader><TableRow><TableHead>Code RD</TableHead><TableHead>Matricule de paie</TableHead><TableHead>Nom</TableHead><TableHead>CP / Ville</TableHead><TableHead>Région</TableHead></TableRow></TableHeader>
                  <TableBody>{personnes.map((p) => (
                    <TableRow key={p.key} data-testid={`rex-pers-${p.key}`}>
                      <TableCell className="font-mono text-xs">{p.code_rd ? p.code_rd : <span className="text-amber-600">—</span>}</TableCell>
                      <TableCell className="font-mono text-xs">{p.matricule_paie || '—'}</TableCell>
                      <TableCell>{p.nom} {p.prenom || ''}</TableCell>
                      <TableCell className="text-muted-foreground">{p.code_postal ? `${p.code_postal} ${p.ville || ''}` : <span className="text-amber-600">non extraite</span>}</TableCell>
                      <TableCell className="text-xs">{p.region || '—'}</TableCell>
                    </TableRow>))}</TableBody>
                </Table>
              </div></CardContent>
            </Card>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <VentCard title="juin 2026" data={vent.juin} />
              <VentCard title="juillet 2026" data={vent.juillet} />
            </div>

            <Card className="card-shadow">
              <CardHeader className="pb-2"><CardTitle className="text-base">Doublons de matricule (social_collaborateurs — lecture seule, non fusionnés)</CardTitle></CardHeader>
              <CardContent>{doublons.length ? <div className="flex flex-wrap gap-2">{doublons.map((d, i) => <Badge key={i} variant="secondary">{d.matricule} ×{d.occurrences}</Badge>)}</div> : <p className="text-sm text-muted-foreground" data-testid="rex-doublons-empty">Aucun doublon détecté.</p>}</CardContent>
            </Card>
          </CardContent>
        ) : null}
      </Card>
    </div>
  );
};

// =========================================================================== //
//  E6 — QONTO                                                                  //
// =========================================================================== //
const POSTE_LABELS = { frais_generaux: 'Frais généraux', cout_bureau: 'Coût Bureau', indes_qonto_rd: 'Indés RD (flux Qonto)' };
const MOTIF_LABEL = { label_absent: 'Label absent', label_non_mappe: 'Label sans correspondance' };
const MOTIF_CLS = { label_absent: 'border-rose-300 text-rose-700 bg-rose-50', label_non_mappe: 'border-amber-300 text-amber-700 bg-amber-50' };

// --- Écran de sécurité de synchronisation Qonto (aperçu lecture seule + validation) --- //
const QontoSyncSafety = ({ mois, setMois, months, configured, onApplied }) => {
  const [preview, setPreview] = useState(null);
  const [loadingPrev, setLoadingPrev] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [openPoste, setOpenPoste] = useState({});
  const [applying, setApplying] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingForce, setPendingForce] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [syncInfo, setSyncInfo] = useState(null);

  const loadPending = useCallback(async () => {
    try {
      const [{ data }, { data: info }] = await Promise.all([
        api.get('/rex/qonto/sync/pending', { params: { mois } }),
        api.get('/rex/qonto/sync/info', { params: { mois } }),
      ]);
      setCanUndo(!!data.can_undo); setSyncInfo(info);
    } catch (e) { /* noop */ }
  }, [mois]);
  useEffect(() => { loadPending(); }, [loadPending]);

  const runPreview = async () => {
    setPreview(null); setOpenPoste({}); setModalOpen(true); setLoadingPrev(true);
    try {
      const { data } = await api.post('/rex/qonto/sync/preview', null, { params: { mois } });
      setPreview({ ...data.preview, nb_transactions: data.nb_transactions, nb_fenetre: data.nb_fenetre });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Aperçu impossible'); setModalOpen(false); }
    setLoadingPrev(false);
  };
  const apply = async (force) => {
    setApplying(true);
    try {
      const { data } = await api.post('/rex/qonto/sync/apply', null, { params: { mois, force } });
      const status = data?.preview?.status;
      if (status === 'applied') {
        const up = data?.upstream;
        let extra = '';
        if (up && up.status === 'applied') extra = ` · mois précédent ${up.mois} recalculé automatiquement`;
        else if (up && up.status === 'needs_validation') extra = ` · mois précédent ${up.mois} à valider (>50 %)`;
        toast.success(`Synchronisation appliquée — ${data.nb_transactions} transactions${extra}`);
        setModalOpen(false); onApplied && onApplied(); loadPending();
      }
      else if (status === 'needs_validation') { toast.warning('Validation explicite requise (variation > 50 %).'); }
      else { toast.info(`Statut : ${status}`); }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Application impossible'); }
    setApplying(false);
  };
  const undo = async () => {
    setUndoing(true);
    try { await api.post('/rex/qonto/sync/undo', null, { params: { mois } }); toast.success('Dernière synchronisation annulée'); onApplied && onApplied(); loadPending(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Annulation impossible'); }
    setUndoing(false);
  };
  // Application demandée : si des exceptions existent, on exige une confirmation
  // explicite (nb + total TTC) AVANT toute écriture — le bouton ne suffit pas.
  const requestApply = (force) => {
    if ((preview?.nb_exceptions || 0) > 0) { setPendingForce(force); setConfirmOpen(true); }
    else apply(force);
  };
  const exportExceptions = async (fmt) => {
    setExporting(true);
    try {
      const res = await api.get('/rex/qonto/exceptions/export-live', { params: { mois, fmt }, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `exceptions_qonto_${mois}.${fmt === 'xlsx' ? 'xlsx' : 'csv'}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(`Export ${fmt.toUpperCase()} — ${preview?.nb_exceptions || 0} transaction(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Export impossible'); }
    setExporting(false);
  };

  const deltas = preview?.deltas || {};
  const needs = preview?.status === 'needs_validation';
  const detailFor = (k) => k === 'cout_bureau' ? (preview?.bureau_detail || []) : k === 'frais_generaux' ? (preview?.fg_detail || []) : (preview?.indes_detail || []);

  return (
    <Card className="card-shadow" data-testid="rex-qonto-safety">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2"><ShieldCheck className="h-4 w-4" />Synchronisation sécurisée — {moisLabel(mois)}</CardTitle>
        <CardDescription>Garde-fou : l'aperçu est en <b>lecture seule</b> (rien n'est écrit). Toute variation d'un poste supérieure à <b>50 %</b> exige une <b>validation explicite</b> avant application. La dernière synchronisation peut être <b>annulée</b>.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Sélecteur de mois EXPLICITE sur la carte (règle CEO) + état de synchro du mois */}
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-muted/30 p-3" data-testid="rex-qonto-month-bar">
          <div className="flex items-center gap-3">
            <div>
              <p className="text-xs font-medium text-muted-foreground">Mois à synchroniser</p>
              {setMois ? <MonthPicker value={mois} onChange={setMois} months={months} testid="rex-qonto-month" />
                : <span className="text-sm font-semibold">{moisLabel(mois)}</span>}
            </div>
            <div className="text-sm">
              {syncInfo ? (syncInfo.ever_synced ? (
                <span className="flex items-center gap-1.5 text-emerald-700 dark:text-emerald-300" data-testid="rex-qonto-lastsync-month">
                  <CheckCircle2 className="h-4 w-4" />
                  {syncInfo.last_sync_at
                    ? <>Dernière synchro de <b>{moisLabel(mois)}</b> : {fmtDateTime(syncInfo.last_sync_at)}{syncInfo.nb_transactions != null ? ` · ${syncInfo.nb_transactions} txn` : ''}</>
                    : <>{moisLabel(mois)} : {syncInfo.nb_transactions || 0} transaction(s) présente(s)</>}
                </span>
              ) : (
                <Badge className="gap-1 bg-amber-500 text-white hover:bg-amber-500" data-testid="rex-qonto-neversynced"><AlertTriangle className="h-3 w-3" />{moisLabel(mois)} : jamais synchronisé</Badge>
              )) : null}
            </div>
          </div>
          {syncInfo?.frozen ? <Badge variant="outline" className="text-[10px]">mois figé — lecture seule</Badge> : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={runPreview} disabled={!configured || loadingPrev || syncInfo?.frozen} data-testid="rex-qonto-preview-btn" style={{ background: 'var(--tenant-accent-500)' }}>
            <Eye className={`mr-2 h-4 w-4 ${loadingPrev ? 'animate-pulse' : ''}`} />Vérifier la synchronisation — {moisLabel(mois)}
          </Button>
          <Button variant="outline" onClick={undo} disabled={!canUndo || undoing} data-testid="rex-qonto-undo-btn" className="text-rose-600 hover:text-rose-700">
            <Undo2 className={`mr-2 h-4 w-4 ${undoing ? 'animate-spin' : ''}`} />Annuler la dernière synchro
          </Button>
          {!configured ? <span className="text-xs text-muted-foreground">Connexion Qonto requise.</span> : null}
        </div>
      </CardContent>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="rex-qonto-preview-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ArrowRightLeft className="h-5 w-5" />Aperçu de synchronisation — {moisLabel(mois)}</DialogTitle>
            <DialogDescription>
              {loadingPrev || !preview ? 'Lecture seule en cours…' : `Lecture seule : ${preview?.nb_transactions ?? 0} transaction(s) du mois analysées. Aucune écriture tant que vous n'avez pas validé.`}
            </DialogDescription>
          </DialogHeader>

          {loadingPrev || !preview ? (
            <div className="flex flex-col items-center justify-center gap-3 py-10 text-sm text-muted-foreground" data-testid="rex-qonto-preview-loading">
              <RefreshCw className="h-6 w-6 animate-spin" style={{ color: 'var(--tenant-accent-500)' }} />
              Lecture des transactions Qonto (lecture seule)… cela peut prendre quelques secondes.
            </div>
          ) : (
          <>
          {needs ? (
            <Alert className="border-rose-300 bg-rose-50" data-testid="rex-qonto-preview-warning">
              <ShieldAlert className="h-4 w-4 text-rose-600" />
              <AlertTitle className="text-rose-800">Validation explicite requise</AlertTitle>
              <AlertDescription className="text-sm text-rose-700">Au moins un poste varie de plus de 50 % (ou apparaît / disparaît). C'est exactement le type d'écart qui a corrompu juin. Vérifiez le comparatif ci-dessous avant d'appliquer.</AlertDescription>
            </Alert>
          ) : (
            <Alert className="border-emerald-200 bg-emerald-50" data-testid="rex-qonto-preview-ok">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              <AlertTitle className="text-emerald-800">Variations dans les limites</AlertTitle>
              <AlertDescription className="text-sm text-emerald-700">Aucun poste ne dépasse le seuil de 50 %. Vous pouvez appliquer.</AlertDescription>
            </Alert>
          )}

          <div className="rounded-lg border" data-testid="rex-qonto-preview-table">
            <div className="grid grid-cols-[1.4fr_1fr_1fr_1fr] gap-2 border-b bg-muted/50 px-3 py-2 text-xs font-medium text-muted-foreground">
              <span>Poste</span><span className="text-right">Avant</span><span className="text-right">Après</span><span className="text-right">Variation</span>
            </div>
            {['cout_bureau', 'frais_generaux', 'indes_qonto_rd'].map((k) => {
              const d = deltas[k] || {};
              const oldV = d.old; const newV = d.new;
              const dEur = isNum(oldV) && isNum(newV) ? newV - oldV : null;
              const det = detailFor(k);
              const isOpen = openPoste[k];
              return (
                <div key={k} className="border-b last:border-b-0" data-testid={`rex-qonto-preview-row-${k}`}>
                  <div className="grid grid-cols-[1.4fr_1fr_1fr_1fr] items-center gap-2 px-3 py-2 text-sm">
                    <button onClick={() => det.length && setOpenPoste((o) => ({ ...o, [k]: !o[k] }))} className={`flex items-center gap-1.5 text-left ${det.length ? 'cursor-pointer' : 'cursor-default'}`}>
                      {det.length ? <ChevronRight className={`h-3.5 w-3.5 transition-transform ${isOpen ? 'rotate-90' : ''}`} /> : <span className="w-3.5" />}
                      <span className="font-medium">{POSTE_LABELS[k]}</span>
                    </button>
                    <span className="text-right"><Money v={oldV} d={2} muted /></span>
                    <span className="text-right">{isNum(newV) ? <Money v={newV} d={2} /> : <span className="text-[11px] font-medium text-amber-700">indisponible</span>}</span>
                    <span className="flex items-center justify-end gap-1.5 text-right">
                      {dEur !== null ? <Money v={dEur} d={2} /> : <span className="text-muted-foreground">—</span>}
                      {isNum(d.pct) ? <span className="text-[11px] text-muted-foreground">{d.pct > 0 ? '+' : ''}{d.pct}%</span> : null}
                      {d.depasse ? <Badge className="bg-rose-600 text-[9px] hover:bg-rose-600" data-testid={`rex-qonto-preview-badge-${k}`}>&gt; 50 %</Badge> : null}
                    </span>
                  </div>
                  {isOpen && det.length ? (
                    <div className="bg-muted/40 px-3 py-2 space-y-1.5" data-testid={`rex-qonto-preview-detail-${k}`}>
                      <p className="pl-5 text-[10px] uppercase tracking-wide text-muted-foreground/80">Transactions composant le « Après »</p>
                      {det.map((c, i) => (
                        <div key={i} className="rounded-md border border-border/60 bg-background/60">
                          <div className="flex items-center justify-between px-2 py-1 text-xs font-medium">
                            <span>{c.label}{c.nb ? <span className="ml-1 opacity-60">×{c.nb}</span> : null}</span>
                            <Money v={c.montant} d={2} />
                          </div>
                          {(c.txns || []).length ? (
                            <div className="border-t border-border/50 px-2 py-1" data-testid={`rex-qonto-preview-txns-${k}`}>
                              {(c.txns || []).map((tx, ti) => (
                                <div key={ti} className="flex items-center justify-between gap-2 py-0.5 text-[11px] text-muted-foreground">
                                  <span className="flex min-w-0 items-center gap-1.5">
                                    <span className="tabular whitespace-nowrap opacity-70" title="Date d'opération (emitted_at)">{(tx.date_operation || tx.emitted_at || tx.settled_at) ? String(tx.date_operation || tx.emitted_at || tx.settled_at).slice(0, 10) : '—'}</span>
                                    <span className="truncate">{tx.contrepartie || tx.label_txn || '—'}</span>
                                  </span>
                                  <Money v={tx.montant} d={2} muted />
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>

          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="rex-qonto-preview-exceptions">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 font-medium"><Ban className="h-4 w-4" />{preview?.nb_exceptions || 0} transaction(s) non rattachée(s) · {eur2(preview?.exceptions_total_ttc)} TTC</div>
              {(preview?.nb_exceptions || 0) > 0 ? (
                <div className="flex items-center gap-1.5">
                  <Button size="sm" variant="outline" className="h-7 border-amber-300 bg-white/70 text-amber-800 hover:bg-white" onClick={() => exportExceptions('xlsx')} disabled={exporting} data-testid="rex-qonto-preview-exc-export-xlsx"><FileSpreadsheet className="mr-1.5 h-3.5 w-3.5" />Excel</Button>
                  <Button size="sm" variant="outline" className="h-7 border-amber-300 bg-white/70 text-amber-800 hover:bg-white" onClick={() => exportExceptions('csv')} disabled={exporting} data-testid="rex-qonto-preview-exc-export-csv"><Download className="mr-1.5 h-3.5 w-3.5" />CSV</Button>
                </div>
              ) : null}
            </div>
            <p className="mt-0.5 text-xs">Ces transactions (label absent ou sans correspondance) n'entrent dans <b>aucun poste</b> — jamais versées en Frais généraux par défaut. Exportez la liste (avec l'identifiant Qonto) pour les labelliser dans Qonto.</p>
            {(preview?.exceptions || []).slice(0, 6).map((e, i) => (
              <div key={i} className="mt-1 flex items-center justify-between gap-2 border-t border-amber-200/60 pt-1 text-xs">
                <span className="truncate">{e.contrepartie || e.label_txn}{e.analytical_label ? <span className="ml-1 opacity-70">· {e.analytical_label}</span> : null}</span>
                <span className="tabular whitespace-nowrap">{eur2(e.montant_ttc)}</span>
              </div>
            ))}
            {(preview?.nb_exceptions || 0) > 6 ? <p className="mt-1 text-[11px] opacity-70">… et {preview.nb_exceptions - 6} autre(s). Export complet ci-dessus, ou onglet « Exceptions Qonto ».</p> : null}
          </div>
          </>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setModalOpen(false)} data-testid="rex-qonto-preview-cancel">Annuler (ne rien appliquer)</Button>
            {preview && !loadingPrev ? (needs ? (
              <Button onClick={() => requestApply(true)} disabled={applying} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="rex-qonto-preview-apply-force">
                <ShieldAlert className="mr-2 h-4 w-4" />Valider et appliquer malgré l'écart
              </Button>
            ) : (
              <Button onClick={() => requestApply(false)} disabled={applying} style={{ background: 'var(--tenant-accent-500)' }} data-testid="rex-qonto-preview-apply">
                <CheckCircle2 className="mr-2 h-4 w-4" />Appliquer la synchronisation
              </Button>
            )) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation explicite quand l'aperçu comporte des exceptions (nb + total TTC) */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="rex-qonto-apply-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><Ban className="h-5 w-5 text-amber-600" />Appliquer malgré {preview?.nb_exceptions || 0} exception(s) ?</AlertDialogTitle>
            <AlertDialogDescription>
              <b>{preview?.nb_exceptions || 0} transaction(s)</b> pour un total de <b>{eur2(preview?.exceptions_total_ttc)} TTC</b> ne sont rattachées à aucun label reconnu.
              Après application, elles <b>n'entreront dans aucun poste</b> et resteront listées dans « Exceptions Qonto » ; le mois <b>ne sera pas clôturable</b> tant qu'elles subsistent.
              {needs ? <span className="mt-2 block text-rose-700">De plus, au moins un poste varie de plus de 50 %. Vous appliquez cet écart en connaissance de cause.</span> : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="rex-qonto-apply-confirm-cancel">Revenir à l'aperçu</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setConfirmOpen(false); apply(pendingForce); }} className="bg-rose-600 hover:bg-rose-700" data-testid="rex-qonto-apply-confirm-ok">
              Confirmer l'application
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

// --- Écran Exceptions Qonto (déversoir de sécurité) --- //
const QontoExceptionsPanel = ({ mois, configured }) => {
  const [sel, setSel] = useState(mois || '__all__');
  const [data, setData] = useState(null);
  const [live, setLive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingLive, setLoadingLive] = useState(false);
  const [exporting, setExporting] = useState(false);
  const load = useCallback(async () => {
    setLoading(true); setLive(null);
    try { const params = sel && sel !== '__all__' ? { mois: sel } : {}; const { data } = await api.get('/rex/qonto/exceptions', { params }); setData(data); }
    catch (e) { /* noop */ }
    setLoading(false);
  }, [sel]);
  useEffect(() => { load(); }, [load]);
  const specificMois = sel && sel !== '__all__' ? sel : null;
  const isLive = !!live;
  const shown = live || data;
  const rows = shown?.exceptions || [];

  const listLive = async () => {
    if (!specificMois) return;
    setLoadingLive(true);
    try { const { data } = await api.get('/rex/qonto/exceptions/live', { params: { mois: specificMois } }); setLive(data); toast.success(`${data.count} transaction(s) non rattachée(s) — lecture seule (direct Qonto)`); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Lecture live impossible'); }
    setLoadingLive(false);
  };
  const doExport = async (fmt) => {
    setExporting(true);
    try {
      const url = isLive ? '/rex/qonto/exceptions/export-live' : '/rex/qonto/exceptions/export';
      const params = { fmt };
      if (specificMois) params.mois = specificMois;
      const res = await api.get(url, { params, responseType: 'blob' });
      const blobUrl = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = blobUrl;
      a.download = `exceptions_qonto_${specificMois || 'toutes'}.${fmt === 'xlsx' ? 'xlsx' : 'csv'}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(blobUrl);
      toast.success(`Export ${fmt.toUpperCase()} — ${rows.length} ligne(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Export impossible'); }
    setExporting(false);
  };
  return (
    <Card className="card-shadow" data-testid="rex-qonto-exceptions">
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <CardTitle className="text-base flex items-center gap-2"><Ban className="h-4 w-4" />Exceptions Qonto — déversoir de sécurité</CardTitle>
            <CardDescription>Transactions <b>non rattachées</b> (label absent ou sans correspondance vers un poste). Elles n'entrent dans <b>aucun poste</b> ; un mois qui en comporte <b>n'est pas clôturable</b>. Exportez la liste (avec l'<b>identifiant Qonto</b>) pour retrouver et labelliser chaque transaction dans Qonto.</CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={sel} onValueChange={setSel}>
              <SelectTrigger className="h-9 w-[150px]" data-testid="rex-qonto-exc-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tous les mois</SelectItem>
                {(data?.mois_disponibles || []).map((ym) => <SelectItem key={ym} value={ym}>{moisLabel(ym)}</SelectItem>)}
              </SelectContent>
            </Select>
            {configured && specificMois ? (
              <Button variant="outline" size="sm" onClick={listLive} disabled={loadingLive} data-testid="rex-qonto-exc-live-btn">
                <Eye className={`mr-2 h-4 w-4 ${loadingLive ? 'animate-pulse' : ''}`} />Lister en direct
              </Button>
            ) : null}
            <Button variant="outline" size="sm" onClick={() => doExport('xlsx')} disabled={exporting || !rows.length} data-testid="rex-qonto-exc-export-xlsx"><FileSpreadsheet className="mr-2 h-4 w-4" />Excel</Button>
            <Button variant="outline" size="sm" onClick={() => doExport('csv')} disabled={exporting || !rows.length} data-testid="rex-qonto-exc-export"><Download className="mr-2 h-4 w-4" />CSV</Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLive ? (
          <div className="mb-3 flex items-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800" data-testid="rex-qonto-exc-live-banner">
            <Info className="h-4 w-4" />Lecture seule — transactions récupérées <b>en direct</b> depuis Qonto pour {moisLabel(specificMois)}. Aucune écriture, aucune application.
          </div>
        ) : null}
        {loading ? <Skeleton className="h-24 w-full" /> : rows.length === 0 ? (
          <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-4 text-sm text-emerald-800" data-testid="rex-qonto-exc-empty">
            <CheckCircle2 className="h-5 w-5" />Aucune exception Qonto {specificMois ? `sur ${moisLabel(specificMois)}` : ''}{isLive ? ' (lecture live)' : ''} — toutes les transactions sont rattachées à un poste.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="mb-2 text-xs text-muted-foreground" data-testid="rex-qonto-exc-count">{rows.length} exception(s) · total {eur2(shown?.total_ttc)} TTC{isLive ? ' · direct Qonto' : ''}</div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date opér.</TableHead><TableHead>Libellé</TableHead><TableHead>Contrepartie</TableHead><TableHead>Label</TableHead>
                  <TableHead>Motif</TableHead><TableHead className="text-right">TTC</TableHead><TableHead className="text-right">TVA</TableHead><TableHead className="text-right">HT</TableHead>
                  <TableHead>ID Qonto</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={r.id || r.raw_id || i} data-testid={`rex-qonto-exc-row-${r.id || r.raw_id || i}`}>
                    <TableCell className="text-xs whitespace-nowrap" title="Date d'opération (emitted_at)">{(r.date_operation || r.emitted_at || r.settled_at) ? String(r.date_operation || r.emitted_at || r.settled_at).slice(0, 10) : '—'}</TableCell>
                    <TableCell className="text-sm">{r.libelle || '—'}</TableCell>
                    <TableCell className="text-sm">{r.contrepartie || '—'}</TableCell>
                    <TableCell className="text-sm">{r.label_analytique || <span className="text-muted-foreground italic">aucun</span>}</TableCell>
                    <TableCell><span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium ${MOTIF_CLS[r.motif] || ''}`}>{MOTIF_LABEL[r.motif] || r.motif}</span></TableCell>
                    <TableCell className="text-right tabular text-sm">{eur2(r.montant_ttc)}</TableCell>
                    <TableCell className="text-right tabular text-xs text-muted-foreground">{eur2(r.tva)}</TableCell>
                    <TableCell className="text-right tabular text-sm">{eur2(r.montant_ht)}</TableCell>
                    <TableCell className="font-mono text-[10px] text-muted-foreground" title={r.raw_id || ''}>{r.raw_id ? String(r.raw_id).slice(0, 14) : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// --- Vérification (lecture seule) de la lecture des labels Qonto --- //
const QontoLabelCheck = () => {
  const [sample, setSample] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  const run = async () => {
    setLoading(true);
    try { const { data } = await api.get('/rex/qonto/raw-sample', { params: { mois: '2026-06' } }); setSample(data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Lecture impossible'); }
    setLoading(false);
  };
  const s = sample?.stats;
  return (
    <Card className="card-shadow" data-testid="rex-qonto-labelcheck">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2"><FileSearch className="h-4 w-4" />Vérifier la lecture des labels (lecture seule)</CardTitle>
        <CardDescription>Contrôle de la structure brute d'une transaction : l'API Qonto renvoie des <b>identifiants</b> de labels (<code>label_ids</code>), résolus en libellés via l'endpoint <code>/labels</code>. Aucune écriture, aucune synchronisation.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Button variant="outline" size="sm" onClick={run} disabled={loading} data-testid="rex-qonto-labelcheck-btn"><Eye className="mr-2 h-4 w-4" />Analyser une transaction de juin 2026</Button>
        {sample && s ? (
          <div className="space-y-2 text-sm" data-testid="rex-qonto-labelcheck-result">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <div className="rounded-lg border p-2"><p className="text-xs text-muted-foreground">Transactions</p><p className="font-semibold tabular">{s.total}</p></div>
              <div className="rounded-lg border p-2"><p className="text-xs text-muted-foreground">avec label_ids</p><p className="font-semibold tabular">{s.avec_label_ids}</p></div>
              <div className="rounded-lg border p-2"><p className="text-xs text-muted-foreground">résolus via /labels</p><p className="font-semibold tabular text-emerald-600">{s.resolus_via_labels_endpoint}</p></div>
              <div className="rounded-lg border p-2"><p className="text-xs text-muted-foreground">libellés directs</p><p className="font-semibold tabular">{s.avec_labels_noms_directs}</p></div>
            </div>
            <p className="text-xs text-muted-foreground">Le label se trouve dans : <b>{sample.champ_label_dans_transaction}</b></p>
            {(sample.sample?.labels_resolus || []).map((l, i) => (
              <div key={i} className="rounded-lg bg-muted/40 px-3 py-2 text-xs">Identifiant <code>{l.id}</code> → <b>{l.name}</b> <span className="opacity-70">(catégorie : {l.categorie_parente || '—'})</span></div>
            ))}
            <button className="text-xs text-[color:var(--tenant-accent-600)] underline" onClick={() => setShowRaw((v) => !v)} data-testid="rex-qonto-labelcheck-toggle">{showRaw ? 'Masquer' : 'Afficher'} la structure brute (identifiants masqués)</button>
            {showRaw ? <pre className="max-h-64 overflow-auto rounded-lg bg-slate-900 p-3 text-[11px] text-slate-100" data-testid="rex-qonto-labelcheck-raw">{JSON.stringify(sample.sample?.raw, null, 2)}</pre> : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

const QontoTab = ({ mois }) => {
  const [status, setStatus] = useState(null);
  const [labels, setLabels] = useState(null);
  const [audit, setAudit] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ login: '', secret: '' });
  const [testMsg, setTestMsg] = useState(null);
  const [months, setMonths] = useState([]);
  const [syncMois, setSyncMois] = useState(mois || '2026-06');
  const load = useCallback(async () => {
    try {
      const [s, l, a, c] = await Promise.all([
        api.get('/rex/qonto/status'), api.get('/rex/qonto/labels'), api.get('/rex/qonto/credentials-audit'),
        api.get('/rex/cloture/status'),
      ]);
      setStatus(s.data); setLabels(l.data); setAudit(a.data.audit || []);
      const ms = c.data.months || [];
      setMonths(ms);
      setSyncMois((cur) => {
        if (cur && ms.some((m) => m.mois === cur)) return cur;
        const live = ms.filter((m) => !m.frozen);
        const openLive = live.filter((m) => !m.closed);
        const def = openLive[openLive.length - 1] || live[live.length - 1] || ms[ms.length - 1];
        return def ? def.mois : (mois || '2026-06');
      });
    } catch (e) { /* noop */ }
  }, [mois]);
  useEffect(() => { load(); }, [load]);
  const saveCreds = async () => {
    if (!form.login.trim() || !form.secret.trim()) { toast.error('Login et clé requis'); return; }
    setBusy(true);
    try { await api.post('/rex/qonto/credentials', { login: form.login.trim(), secret: form.secret.trim() }); toast.success('Clé Qonto enregistrée (chiffrée, non réaffichée)'); setForm({ login: '', secret: '' }); setTestMsg(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Enregistrement impossible'); }
    setBusy(false);
  };
  const testConn = async () => { setBusy(true); try { const { data } = await api.post('/rex/qonto/test'); setTestMsg({ ok: data.ok, message: data.message }); load(); } catch (e) { setTestMsg({ ok: false, message: e?.response?.data?.detail || 'Test impossible' }); } setBusy(false); };
  const delCreds = async () => { setBusy(true); try { await api.delete('/rex/qonto/credentials'); toast.success('Secrets Qonto supprimés'); setTestMsg(null); load(); } catch (e) { toast.error('Suppression impossible'); } setBusy(false); };
  if (!status) return <Loading />;
  const configured = status.configured;
  return (
    <div className="space-y-5" data-testid="rex-qonto">
      <SectionTitle icon={Landmark} desc="Frais généraux, Coût Bureau et indés (flux Qonto). Connexion en LECTURE SEULE. Catégorie de labels ANALYSE FLUX TREZO (85 labels).">État de la synchronisation Qonto</SectionTitle>
      <Alert data-testid="rex-qonto-status" className={configured ? 'border-emerald-200' : 'border-amber-200'}>
        {configured ? <CheckCircle2 className="h-4 w-4" /> : <Info className="h-4 w-4" />}
        <AlertTitle className="flex items-center gap-2">{configured ? 'Connexion Qonto active' : 'Connexion Qonto non configurée'}
          {status.source ? <Badge variant="outline" className="text-[10px]">source : {status.source === 'db' ? 'base (chiffrée)' : status.source === 'env' ? 'environnement' : 'base + environnement'}</Badge> : null}</AlertTitle>
        <AlertDescription className="text-sm">
          {configured ? (
            <div className="space-y-1">
              <p data-testid="rex-qonto-configured-meta">Clé configurée le <b>{fmtDateTime(status.configured_at)}</b>{status.configured_by ? <> par <b>{status.configured_by}</b></> : null}. La valeur n'est jamais réaffichée.</p>
              {status.double_config ? <p className="text-amber-700" data-testid="rex-qonto-double">Double configuration détectée (base + .env) : <b>la base prime</b>.</p> : null}
            </div>
          ) : (
            <div className="space-y-2"><p>{status.instructions}</p>
              <p className="text-xs text-muted-foreground">Tant que Qonto n'est pas configuré, les frais généraux proviennent de l'historique figé (mois clôturés). L'application reste pleinement utilisable ; les mois vivants passent en « provisoire ».</p></div>
          )}
        </AlertDescription>
      </Alert>

      <Card className="card-shadow" data-testid="rex-qonto-form">
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><KeyRound className="h-4 w-4" />Déposer la clé Qonto (lecture seule)</CardTitle>
          <CardDescription>Écriture seule : la valeur est <b>chiffrée au repos</b> et n'est <b>jamais réaffichée</b> ni renvoyée par l'API. Owner-only et journalisée (qui / quand / IP).</CardDescription></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div><label className="text-xs font-medium text-muted-foreground">Login API</label>
              <Input className="mt-1 h-9" autoComplete="off" placeholder="login-…" value={form.login} onChange={(e) => setForm({ ...form, login: e.target.value })} data-testid="rex-qonto-login" /></div>
            <div><label className="text-xs font-medium text-muted-foreground">Clé secrète</label>
              <Input className="mt-1 h-9" type="password" autoComplete="new-password" placeholder="••••••••" value={form.secret} onChange={(e) => setForm({ ...form, secret: e.target.value })} data-testid="rex-qonto-secret" /></div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={saveCreds} disabled={busy} data-testid="rex-qonto-save" style={{ background: 'var(--tenant-accent-500)' }}><Save className="mr-2 h-4 w-4" />Enregistrer la clé</Button>
            <Button variant="outline" onClick={testConn} disabled={busy || !configured} data-testid="rex-qonto-test"><ShieldCheck className="mr-2 h-4 w-4" />Tester la connexion</Button>
            {configured ? (
              <AlertDialog>
                <AlertDialogTrigger asChild><Button variant="outline" className="text-rose-600 hover:text-rose-700" data-testid="rex-qonto-delete"><Trash2 className="mr-2 h-4 w-4" />Supprimer</Button></AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Supprimer les secrets Qonto ?</AlertDialogTitle>
                    <AlertDialogDescription>La connexion redeviendra « non configurée ». Les mois dépendant de Qonto repasseront en provisoire. Action tracée dans le journal.</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel><AlertDialogAction onClick={delCreds} data-testid="rex-qonto-delete-confirm">Supprimer</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            ) : null}
          </div>
          {testMsg ? <div className={`rounded-lg border p-2 text-sm ${testMsg.ok ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-rose-200 bg-rose-50 text-rose-700'}`} data-testid="rex-qonto-test-msg">{testMsg.ok ? '✓ ' : '✕ '}{testMsg.message}</div> : null}
        </CardContent>
      </Card>

      <QontoSyncSafety mois={syncMois} setMois={setSyncMois} months={months} configured={configured} onApplied={load} />
      {status.last_sync ? <p className="text-xs text-muted-foreground" data-testid="rex-qonto-lastsync">Dernière synchronisation : {moisLabel(status.last_sync.mois)} ({status.last_sync.nb_transactions} transactions) — {fmtDateTime(status.last_sync.last_sync_at)}</p> : null}

      <QontoExceptionsPanel mois={syncMois} configured={configured} />

      <QontoLabelCheck />

      <Card className="card-shadow">
        <CardHeader className="pb-2"><CardTitle className="text-base">Labels analytiques ANALYSE FLUX TREZO (recette 16.8){labels?.count ? ` — ${labels.count}` : ''}</CardTitle></CardHeader>
        <CardContent>{labels?.configured ? <div className="flex flex-wrap gap-2">{(labels.labels || []).map((l) => <Badge key={l.id} variant="outline">{l.name}</Badge>)}</div> : <p className="text-sm text-muted-foreground" data-testid="rex-labels-msg">{labels?.message}</p>}</CardContent>
      </Card>

      <Card className="card-shadow" data-testid="rex-qonto-audit">
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><FileClock className="h-4 w-4" />Journal des opérations sur les secrets</CardTitle><CardDescription>Métadonnées uniquement — aucune valeur de secret n'est jamais enregistrée.</CardDescription></CardHeader>
        <CardContent className="space-y-1 text-sm max-h-[200px] overflow-auto">
          {audit.length ? audit.map((a) => <div key={a.id} className="flex items-center justify-between border-b py-1.5"><span className="font-medium">{QONTO_AUDIT_LABEL[a.action] || a.action}</span><span className="text-xs text-muted-foreground">{a.user || '—'} · {a.ip || '—'} · {fmtDateTime(a.at)}</span></div>) : <p className="text-sm text-muted-foreground">Aucune opération enregistrée.</p>}
        </CardContent>
      </Card>
    </div>
  );
};

// =========================================================================== //
//  E7 — ADMINISTRATION                                                          //
// =========================================================================== //
const DEST_LABEL = {
  frais_generaux: 'Frais généraux', cout_bureau: 'Coût Bureau', rd_independants: 'Coût RD indépendants',
  cout_re: 'Coût RE', cout_rp: 'Coût RP', exclu: 'Exclu (hors résultat)', __none__: 'Non mappé',
};
const DEST_STYLE = {
  frais_generaux: 'border-sky-300 bg-sky-50 text-sky-700',
  cout_bureau: 'border-violet-300 bg-violet-50 text-violet-700',
  rd_independants: 'border-emerald-300 bg-emerald-50 text-emerald-700',
  cout_re: 'border-teal-300 bg-teal-50 text-teal-700',
  cout_rp: 'border-indigo-300 bg-indigo-50 text-indigo-700',
  exclu: 'border-slate-300 bg-slate-100 text-slate-600',
  __none__: 'border-amber-300 bg-amber-50 text-amber-700',
};

// Écran de correspondance : les 85 labels de la catégorie, 3 destinations, compteur.
const QontoLabelMappingPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [q, setQ] = useState('');
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get('/rex/labels/overview'); setData(data); }
    catch (e) { toast.error('Chargement des labels impossible'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  const setDest = async (label, poste) => {
    setSaving(label);
    try {
      await api.post('/rex/label-mapping', { label, poste: poste === '__none__' ? '' : poste });
      setData((d) => {
        const inLabels = d.labels.some((l) => l.label === label);
        const labels = d.labels.map((l) => l.label === label ? { ...l, poste: poste === '__none__' ? null : poste, source: 'admin' } : l);
        let hors = d.hors_categorie || [];
        if (!inLabels) hors = poste === '__none__' ? hors.filter((h) => h.label !== label) : hors.map((h) => h.label === label ? { ...h, poste } : h);
        const mc = labels.filter((l) => l.poste).length;
        return { ...d, labels, hors_categorie: hors, hors_categorie_count: hors.length, mapped_count: mc, unmapped_count: d.total - mc };
      });
      toast.success(`« ${label} » → ${DEST_LABEL[poste] || poste}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Enregistrement impossible'); }
    setSaving(null);
  };
  const resultat = (data?.postes || []).filter((p) => p.famille === 'resultat');
  const rows = (data?.labels || []).filter((l) => l.label.toLowerCase().includes(q.trim().toLowerCase()));
  const pct = data && data.total ? Math.round((data.mapped_count / data.total) * 100) : 0;

  const Selector = ({ l }) => (
    <Select value={l.poste || '__none__'} onValueChange={(v) => setDest(l.label, v)} disabled={saving === l.label}>
      <SelectTrigger className="h-8 w-[220px]" data-testid={`rex-labelmap-select-${l.label}`}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__">Non mappé (exceptions)</SelectItem>
        <SelectSeparator />
        <SelectGroup>
          <SelectLabel>Poste du compte de résultat</SelectLabel>
          {resultat.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
        </SelectGroup>
        <SelectSeparator />
        <SelectItem value="exclu">Exclu du compte de résultat</SelectItem>
      </SelectContent>
    </Select>
  );

  return (
    <Card className="card-shadow" data-testid="rex-labelmap-panel">
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="max-w-[640px]">
            <CardTitle className="text-base flex items-center gap-2"><ListChecks className="h-4 w-4" />Correspondance des labels analytiques → destination</CardTitle>
            <CardDescription>Catégorie <b>ANALYSE FLUX TREZO</b>. Chaque label a <b>trois destinations</b> : un <b>poste du compte de résultat</b>, <b>Exclu</b> (déjà comptabilisé ailleurs ou relève du bilan — jamais dans un poste), ou <b>Non mappé</b> (part en « Exceptions Qonto » tant qu'aucune décision). Un label sans correspondance n'est <b>jamais</b> rattaché par défaut.</CardDescription>
          </div>
          {data ? (
            <div className="min-w-[220px] rounded-lg border bg-muted/40 p-3" data-testid="rex-labelmap-counter">
              <div className="flex items-baseline justify-between">
                <span className="text-sm font-semibold">{data.mapped_count} mappés / {data.total}</span>
                <span className="text-xs text-muted-foreground">{pct}%</span>
              </div>
              <Progress value={pct} className="mt-1.5 h-2" />
              <p className="mt-1.5 text-xs text-muted-foreground"><b className="text-amber-700">{data.unmapped_count}</b> non mappés (en exceptions)</p>
            </div>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtrer un label…" className="h-9 pl-8" data-testid="rex-labelmap-search" />
        </div>
        {loading ? <Skeleton className="h-64 w-full" /> : (
          <div className="max-h-[460px] divide-y overflow-auto rounded-lg border">
            {rows.map((l) => (
              <div key={l.label} className={`flex items-center justify-between gap-3 px-3 py-2 ${!l.poste ? 'bg-amber-50/40' : ''}`} data-testid={`rex-labelmap-row-${l.label}`}>
                <span className="flex min-w-0 items-center gap-2">
                  <span className="truncate text-sm font-medium">{l.label}</span>
                  <span className={`hidden shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-medium sm:inline ${DEST_STYLE[l.poste || '__none__']}`}>{DEST_LABEL[l.poste || '__none__']}</span>
                </span>
                <Selector l={l} />
              </div>
            ))}
            {rows.length === 0 ? <p className="px-3 py-6 text-center text-sm text-muted-foreground">Aucun label ne correspond à « {q} ».</p> : null}
          </div>
        )}
        {(data?.hors_categorie_count || 0) > 0 ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="rex-labelmap-hors-cat">
            <div className="flex items-center gap-2 font-medium"><AlertTriangle className="h-4 w-4" />{data.hors_categorie_count} entrée(s) de mapping hors catégorie (héritage — aucune transaction Qonto ne les porte)</div>
            <p className="mt-0.5">À nettoyer : {data.hors_categorie.map((h) => h.label).join(', ')}.</p>
            <div className="mt-2 space-y-1">
              {data.hors_categorie.map((l) => (
                <div key={l.label} className="flex items-center justify-between gap-3" data-testid={`rex-labelmap-hors-row-${l.label}`}>
                  <span className="text-sm">{l.label} <span className="opacity-70">→ {DEST_LABEL[l.poste] || l.poste}</span></span>
                  <Button size="sm" variant="outline" className="h-7 border-amber-300 bg-white/70" onClick={() => setDest(l.label, '__none__')} disabled={saving === l.label} data-testid={`rex-labelmap-hors-remove-${l.label}`}><Trash2 className="mr-1.5 h-3.5 w-3.5" />Retirer</Button>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

const AdminTab = () => {
  const [tarifs, setTarifs] = useState([]);
  const [codes, setCodes] = useState([]);
  const [codesReport, setCodesReport] = useState(null);
  const [mapping, setMapping] = useState([]);
  const [canaux, setCanaux] = useState({ canaux: [], canal_map: [], regions: [] });
  const [budget, setBudget] = useState([]);
  const [rattach, setRattach] = useState(null);
  const [horizon, setHorizon] = useState(null);
  const [savingHorizon, setSavingHorizon] = useState(false);
  const [newTarif, setNewTarif] = useState({ association: 'AMNESTY', canal: 'SP', date_debut: '2026-01-01', prix_ht: '' });
  const [tarifDiag, setTarifDiag] = useState(null);
  const [repairing, setRepairing] = useState(false);
  const load = useCallback(async () => {
    try {
      const [t, c, m, ca, b, r, h] = await Promise.all([
        api.get('/rex/tarifs'), api.get('/rex/codes-campagne'), api.get('/rex/label-mapping'),
        api.get('/rex/canaux'), api.get('/rex/budget'), api.get('/rex/rattachement'),
        api.get('/rex/pnl-horizon'),
      ]);
      setTarifs(t.data.tarifs || []); setCodes(c.data.codes || []); setCodesReport(c.data.report);
      setMapping(m.data.mapping || []); setCanaux(ca.data); setBudget(b.data.plafonds || []); setRattach(r.data);
      setHorizon(h.data);
      try { const { data: d } = await api.get('/rex/tarifs/diagnostic'); setTarifDiag(d); } catch (e) { /* noop */ }
    } catch (e) { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  const integrateMonth = async (mois) => {
    setSavingHorizon(true);
    try {
      const { data } = await api.post('/rex/pnl-horizon', { mois });
      toast.success(`${moisLabel(mois)} intégré au P&L — horizon : ${moisLabel(data.effective)}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action impossible'); }
    finally { setSavingHorizon(false); }
  };
  const resetHorizon = async () => {
    setSavingHorizon(true);
    try {
      const { data } = await api.post('/rex/pnl-horizon', { reset: true });
      toast.success(`Retour au mode automatique — horizon : ${moisLabel(data.effective)}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action impossible'); }
    finally { setSavingHorizon(false); }
  };
  const addTarif = async () => { try { await api.post('/rex/tarifs', newTarif); toast.success('Tarif enregistré'); setNewTarif({ ...newTarif, prix_ht: '' }); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); } };
  const runRepair = async () => {
    setRepairing(true);
    try {
      const { data } = await api.post('/rex/tarifs/repair', { dry_run: false });
      toast.success(`${data.deleted} tarif(s) suspect(s) supprimé(s)`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Réparation impossible'); }
    finally { setRepairing(false); }
  };
  const reimport = async () => { try { const { data } = await api.post('/rex/codes-campagne/reimport'); toast.success(`${data.nb_codes} codes ré-importés`); load(); } catch (e) { toast.error('Ré-import impossible'); } };
  const [savingRattach, setSavingRattach] = useState(null);
  const saveRattach = async (label, decalage_mois, diviseur) => {
    setSavingRattach(label);
    try {
      const { data } = await api.post('/rex/rattachement', { label, decalage_mois, diviseur });
      const nb = (data.recomputed || []).length;
      toast.success(`Rattachement « ${label} » enregistré${nb ? ` — ${nb} mois vivant(s) recalculé(s)` : ''}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Enregistrement impossible'); }
    finally { setSavingRattach(null); }
  };

  const codesByAssoc = {};
  codes.forEach((c) => { const y = (c.mois || '').slice(0, 4); codesByAssoc[c.association] = codesByAssoc[c.association] || {}; codesByAssoc[c.association][y] = (codesByAssoc[c.association][y] || 0) + 1; });

  return (
    <div className="space-y-6" data-testid="rex-admin">
      <SectionTitle icon={Settings2} desc="Grille tarifaire (4 canaux), codes campagne, correspondances canaux & labels, plafonds budgétaires, décalages de rattachement.">Administration</SectionTitle>

      <Card className="card-shadow" data-testid="rex-horizon-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><CalendarRange className="h-4 w-4" />Horizon du compte de résultat (P&L)</CardTitle>
          <CardDescription>
            Dernier mois affiché dans les vues <b>Tableau</b>, <b>Année</b> et le sélecteur <b>Mois</b>.
            En mode <b>automatique</b>, un mois entre dans le P&L <b>dès que son CA est calculé</b> (import Amnesty fait) —
            aucune action requise chaque mois. Le bouton ci-dessous permet, si besoin, de <b>forcer</b> l'entrée d'un mois
            (ex. le pré-ouvrir avant sa facturation).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg bg-muted/40 px-3 py-2.5 text-sm">
            <div>
              <p className="text-[10px] uppercase text-muted-foreground">Horizon effectif</p>
              <p className="font-semibold" data-testid="rex-horizon-effective">{horizon ? moisLabel(horizon.effective) : '—'}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase text-muted-foreground">Automatique (CA calculé)</p>
              <p className="font-medium" data-testid="rex-horizon-auto">{horizon ? moisLabel(horizon.auto) : '—'}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase text-muted-foreground">Override manuel</p>
              <p className="font-medium" data-testid="rex-horizon-manual">{horizon?.manual ? moisLabel(horizon.manual) : 'aucun'}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {horizon?.next ? (
              <Button size="sm" disabled={savingHorizon} onClick={() => integrateMonth(horizon.next)}
                style={{ background: 'var(--tenant-accent-500)' }} className="gap-1.5 text-white" data-testid="rex-horizon-integrate">
                <CalendarPlus className="h-4 w-4" />Intégrer {moisLabel(horizon.next)} au P&L
              </Button>
            ) : null}
            {horizon?.manual ? (
              <Button size="sm" variant="outline" disabled={savingHorizon} onClick={resetHorizon} data-testid="rex-horizon-reset">
                <RotateCcw className="mr-1.5 h-3.5 w-3.5" />Revenir au mode automatique
              </Button>
            ) : null}
            <span className="text-[11px] text-muted-foreground">
              Astuce : faites d'abord la <b>Facturation Amnesty</b> du mois ; il apparaîtra tout seul. Ce bouton n'est qu'un raccourci.
            </span>
          </div>
        </CardContent>
      </Card>

      <Card className="card-shadow">
        <CardHeader className="pb-2"><CardTitle className="text-base">Grille tarifaire (source du CA — modifiable)</CardTitle><CardDescription>SP, PAP, RUE, RUE ITI. Un canal/association sans tarif en vigueur lève une exception (jamais de prix par défaut).</CardDescription></CardHeader>
        <CardContent className="space-y-3">
          {tarifDiag && (tarifDiag.nb_suspects > 0 || tarifDiag.nb_doublons > 0) && (
            <div className="flex flex-wrap items-start gap-3 rounded-lg border border-rose-300 bg-rose-50 px-3 py-2.5 text-sm dark:border-rose-800 dark:bg-rose-950/40" data-testid="rex-tarif-diag-banner">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
              <div className="flex-1 text-rose-800 dark:text-rose-200">
                <b>{tarifDiag.nb_suspects} tarif(s) suspect(s)</b>{tarifDiag.nb_doublons ? ` + ${tarifDiag.nb_doublons} doublon(s)` : ''} détecté(s)
                {(tarifDiag.suspects || []).length > 0 && (
                  <span className="text-xs"> — ex. {tarifDiag.suspects.map((s) => `${s.association}/${s.canal} à ${fmtEur(s.prix_ht, 2)}`).slice(0, 3).join(', ')}</span>
                )}
                . Ces prix anormalement élevés faussent le CA temps réel (ils ne sont utilisés que pour le calcul live, jamais pour les mois figés).
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" variant="destructive" disabled={repairing} data-testid="rex-tarif-repair-btn">
                    <ShieldAlert className="mr-1.5 h-4 w-4" />Réparer les tarifs
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="rex-tarif-repair-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Réparer la grille des tarifs ?</AlertDialogTitle>
                    <AlertDialogDescription>
                      {tarifDiag.nb_suspects + tarifDiag.nb_doublons} tarif(s) suspect(s)/doublon(s) seront <b>supprimés</b>.
                      Le vrai tarif en vigueur (ex. AMNESTY SP à 202,90 €) est <b>conservé</b>. Aucun mois figé n'est modifié. Action tracée dans le journal d'audit.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="rex-tarif-repair-cancel">Annuler</AlertDialogCancel>
                    <AlertDialogAction onClick={runRepair} data-testid="rex-tarif-repair-confirm">Supprimer les tarifs suspects</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
          <div className="max-h-[280px] overflow-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Association</TableHead><TableHead>Canal</TableHead><TableHead>Début</TableHead><TableHead>Fin</TableHead><TableHead className="text-right">Prix HT</TableHead></TableRow></TableHeader>
            <TableBody>{tarifs.map((t) => {
              const suspectIds = new Set([...(tarifDiag?.suspects || []), ...(tarifDiag?.doublons || [])].map((s) => s.id));
              const suspect = suspectIds.has(t.id);
              return (
              <TableRow key={t.id} className={suspect ? 'bg-rose-50 dark:bg-rose-950/30' : ''} data-testid={`rex-tarif-${t.id}`}><TableCell>{t.association}</TableCell><TableCell><Badge variant="outline">{t.canal}</Badge></TableCell><TableCell className="tabular text-xs">{t.date_debut}</TableCell><TableCell className="tabular text-xs">{t.date_fin || '—'}</TableCell><TableCell className={`text-right tabular ${suspect ? 'font-semibold text-rose-600' : ''}`}>{suspect ? <AlertTriangle className="mr-1 inline h-3 w-3" /> : null}{fmtEur(t.prix_ht, 2)}</TableCell></TableRow>
            ); })}</TableBody>
          </Table></div>
          <div className="flex flex-wrap items-end gap-2 rounded-lg bg-muted/40 p-3">
            <Input className="h-9 w-32" placeholder="Association" value={newTarif.association} onChange={(e) => setNewTarif({ ...newTarif, association: e.target.value })} data-testid="rex-tarif-assoc" />
            <Select value={newTarif.canal} onValueChange={(v) => setNewTarif({ ...newTarif, canal: v })}><SelectTrigger className="h-9 w-32" data-testid="rex-tarif-canal"><SelectValue /></SelectTrigger><SelectContent>{['SP', 'PAP', 'RUE', 'RUE ITI'].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
            <Input className="h-9 w-36" type="date" value={newTarif.date_debut} onChange={(e) => setNewTarif({ ...newTarif, date_debut: e.target.value })} data-testid="rex-tarif-date" />
            <Input className="h-9 w-28" type="number" placeholder="Prix HT" value={newTarif.prix_ht} onChange={(e) => setNewTarif({ ...newTarif, prix_ht: e.target.value })} data-testid="rex-tarif-prix" />
            <Button onClick={addTarif} data-testid="rex-tarif-add" style={{ background: 'var(--tenant-accent-500)' }}>Ajouter / mettre à jour</Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="card-shadow">
          <CardHeader className="pb-2"><CardTitle className="text-base">Correspondance canal terrain → facturation</CardTitle>
            <CardDescription>Un canal de terrain fausserait le CA s'il était mal rattaché (ex. RUE à 192,90 € vs RUE ITI à 195,90 €). Les entrées ambiguës sont signalées, jamais corrigées automatiquement.</CardDescription></CardHeader>
          <CardContent className="space-y-1">
            {canalAmbiguities(canaux.canal_map).map((m) => (
              <div key={m.canal_terrain} className="flex items-center justify-between border-b py-1.5 text-sm" data-testid={`rex-canalmap-${m.canal_facturation}`}>
                <span className="flex items-center gap-1.5 text-muted-foreground">{m.flag ? <AlertTriangle className="h-3.5 w-3.5 text-amber-600" /> : null}{m.canal_terrain}</span>
                <Badge variant="outline" className={m.flag ? 'border-amber-300 text-amber-700' : ''}>{m.canal_facturation}</Badge>
              </div>
            ))}
            {(() => { const amb = canalAmbiguities(canaux.canal_map).filter((m) => m.flag); return amb.length ? (
              <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50/60 p-2.5 text-xs" data-testid="rex-canal-ambigus">
                <p className="mb-1 font-medium text-amber-800">Entrées ambiguës à vérifier ({amb.length}) — sans correction automatique :</p>
                <ul className="list-disc space-y-0.5 pl-4 text-amber-700">
                  {amb.map((m) => <li key={m.canal_terrain}><b>{m.canal_terrain}</b> → {m.canal_facturation} : {m.msg}</li>)}
                </ul>
              </div>
            ) : null; })()}
          </CardContent>
        </Card>
      </div>

      <QontoLabelMappingPanel />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="card-shadow" data-testid="rex-rattach-card">
          <CardHeader className="pb-2"><CardTitle className="text-base">Rattachement par label</CardTitle>
            <CardDescription>Pour chaque label Qonto, choisissez le mois d'imputation au compte de résultat : <b>« Date de transaction »</b> (mois du virement) ou <b>« Mois précédent »</b> (prestation du mois précédant le virement — ex. RD19AUTO, TR/Panier repas, Platform collect). Les montants restent <b>lus dans le flux Qonto</b> (jamais saisis). L'enregistrement réapplique la règle aux mois vivants — jamais aux mois figés/clôturés.</CardDescription></CardHeader>
          <CardContent className="space-y-1 text-sm">
            <div className="flex items-center justify-between border-b py-1.5 text-xs text-muted-foreground">
              <span>Défaut — tous les autres labels</span>
              <Badge variant="outline">Date de transaction</Badge>
            </div>
            {(rattach?.labels || []).map((x) => {
              const excluded = !x.poste_destination || x.poste_destination === 'exclu';
              return (
                <div key={x.label} className="flex flex-wrap items-center justify-between gap-2 border-b py-2" data-testid={`rex-rattach-row-${x.label}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium">{x.label}</span>
                      {x.diviseur !== 1 ? <Badge variant="outline" className="text-[10px]">÷{x.diviseur}</Badge> : null}
                    </div>
                    <div className="text-[11px] text-muted-foreground" data-testid={`rex-rattach-dest-${x.label}`}>
                      {excluded ? 'Exclu du compte de résultat (aucun impact)' : `→ ${DEST_LABEL[x.poste_destination] || x.poste_destination}`}
                    </div>
                  </div>
                  <Select value={String(x.decalage_mois)} onValueChange={(v) => saveRattach(x.label, parseInt(v, 10), x.diviseur)} disabled={savingRattach === x.label}>
                    <SelectTrigger className="h-8 w-[178px]" data-testid={`rex-rattach-select-${x.label}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">Date de transaction</SelectItem>
                      <SelectItem value="-1">Mois précédent</SelectItem>
                      <SelectItem value="1">Mois suivant</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              );
            })}
            <p className="pt-1.5 text-[11px] text-muted-foreground">« Mois précédent » (−1) : un virement réglé en M+1 est imputé à la prestation de M. Le décalage ne déplace que l'<i>attribution</i> ; il ne crée ni ne modifie aucun montant.</p>
          </CardContent>
        </Card>
        <Card className="card-shadow">
          <CardHeader className="pb-2"><CardTitle className="text-base">Régions (départements)</CardTitle></CardHeader>
          <CardContent className="space-y-1 text-sm max-h-[220px] overflow-auto">
            {(canaux.regions || []).map((r) => <div key={r.region} className="flex justify-between border-b py-1.5"><span>{r.region}</span><span className="text-xs text-muted-foreground">{(r.departements || []).join(', ')}</span></div>)}
          </CardContent>
        </Card>
      </div>

      <Card className="card-shadow">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <div><CardTitle className="text-base">Table Codes campagne — {codes.length} codes</CardTitle><CardDescription>Un onglet par association ; 2023 → 2026. Rapprochement sur le code racine (suffixe séquentiel ignoré).</CardDescription></div>
          <Button size="sm" variant="outline" onClick={reimport} data-testid="rex-codes-reimport">Ré-importer le fichier</Button>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">{Object.entries(codesByAssoc).map(([a, yrs]) => <Badge key={a} variant="secondary" data-testid={`rex-codes-assoc-${a}`}>{a} : {Object.entries(yrs).map(([y, n]) => `${y}=${n}`).join(' · ')}</Badge>)}</div>
          <div className="max-h-[300px] overflow-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Association</TableHead><TableHead>Mois</TableHead><TableHead>Canal</TableHead><TableHead>Région</TableHead><TableHead>Code</TableHead><TableHead>Racine</TableHead></TableRow></TableHeader>
              <TableBody>{codes.slice(0, 150).map((c) => (
                <TableRow key={c.id}><TableCell>{c.association}</TableCell><TableCell className="tabular text-xs">{c.mois}</TableCell><TableCell className="text-xs">{c.canal || '—'}</TableCell><TableCell className="text-xs text-muted-foreground">{c.region || '—'}</TableCell><TableCell className="font-mono text-xs">{c.code}</TableCell><TableCell className="font-mono text-xs text-muted-foreground">{c.code_racine}</TableCell></TableRow>
              ))}</TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// =========================================================================== //
//  E8 — IMPORT HISTORIQUE                                                       //
// =========================================================================== //
const HistoriqueTab = () => {
  const [status, setStatus] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.get('/rex/historique/status').then(({ data }) => setStatus(data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const onFile = async (f) => {
    if (!f) return; setBusy(true); setResult(null);
    const fd = new FormData(); fd.append('file', f);
    try { const { data } = await api.post('/rex/historique/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); setResult(data); toast.success(`${data.nb_mois_importes} mois figés importés`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Import impossible'); }
    setBusy(false);
  };
  return (
    <div className="space-y-5" data-testid="rex-historique">
      <SectionTitle icon={FileSpreadsheet} desc="Réservé à l'initialisation. Lit l'onglet 'REX SHARITI' et fige novembre 2023 → mai 2026 (31 mois, lecture seule).">Import de l'historique Excel</SectionTitle>
      {status ? (
        <Alert data-testid="rex-histo-status"><Info className="h-4 w-4" /><AlertTitle>Historique figé — {status.nb_figes} mois ({status.periode})</AlertTitle>
          <AlertDescription className="text-sm">
            <p className="mb-1">{status.note}</p>
            <div className="flex flex-wrap gap-1">{(status.mois_figes || []).map((m) => <Badge key={m} variant="outline" className="text-[10px]">{moisShort(m)}</Badge>)}</div>
            <p className="mt-2 text-xs text-muted-foreground">Régime vivant : {(status.mois_vivants || []).map(moisLabel).join(', ') || '—'}.</p>
          </AlertDescription></Alert>
      ) : null}
      <Card className="card-shadow"><CardContent className="p-4">
        <label className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-border py-10 text-center hover:bg-muted/40" data-testid="rex-histo-dropzone">
          <Upload className="mb-2 h-8 w-8 text-muted-foreground" /><span className="text-sm font-medium">Déposer le classeur master (.xlsx)</span>
          <span className="text-xs text-muted-foreground">Onglet « REX SHARITI » requis</span>
          <input type="file" accept=".xlsx" className="hidden" data-testid="rex-histo-file" onChange={(e) => onFile(e.target.files?.[0])} />
        </label>
      </CardContent></Card>
      {busy ? <Skeleton className="h-24 w-full" /> : null}
      {result ? (
        <Alert data-testid="rex-histo-result"><CheckCircle2 className="h-4 w-4" /><AlertTitle>Import terminé</AlertTitle><AlertDescription className="text-sm">{result.message}</AlertDescription></Alert>
      ) : null}
    </div>
  );
};

// =========================================================================== //
//  E9 — MISE EN PRODUCTION (RUNBOOK)                                           //
// =========================================================================== //
const Voyant = ({ ok, warn, label }) => (
  <span className="inline-flex items-center gap-1.5 text-xs" data-testid={`rex-voyant-${label}`}>
    <span className={`h-2.5 w-2.5 rounded-full ${ok ? 'bg-emerald-500' : warn ? 'bg-amber-500' : 'bg-slate-300'}`} />
    <span className="text-muted-foreground">{label}</span>
  </span>
);

const RunbookTab = () => {
  const [rb, setRb] = useState(null);
  const [audit, setAudit] = useState([]);
  const [corr, setCorr] = useState(null);        // corrections/status
  const [recette, setRecette] = useState(null);  // validation/recette
  const [report, setReport] = useState(null);    // rapport d'application
  const [applying, setApplying] = useState(false);
  const [qdiag, setQdiag] = useState(null);        // diagnostic Qonto lecture seule (juin+juillet)
  const [qdiagLoading, setQdiagLoading] = useState(false);
  const load = useCallback(async () => {
    try {
      const [r, a] = await Promise.all([api.get('/rex/runbook'), api.get('/rex/audit')]);
      setRb(r.data); setAudit(a.data.audit || []);
    } catch (e) { /* noop */ }
  }, []);
  const loadCorr = useCallback(async () => {
    try {
      const [s, rc] = await Promise.all([api.get('/rex/corrections/status'), api.get('/rex/validation/recette')]);
      setCorr(s.data); setRecette(rc.data);
    } catch (e) { /* noop */ }
  }, []);
  useEffect(() => { load(); loadCorr(); }, [load, loadCorr]);
  const applyCorr = async () => {
    setApplying(true);
    try {
      const { data } = await api.post('/rex/corrections/apply');
      setReport(data); toast.success('Corrections de données appliquées'); await loadCorr();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Application impossible'); }
    setApplying(false);
  };
  const runQontoDiag = async () => {
    setQdiagLoading(true);
    try {
      const [juin, juillet] = await Promise.all([
        api.get('/rex/qonto/diagnostic', { params: { mois: '2026-06' } }),
        api.get('/rex/qonto/diagnostic', { params: { mois: '2026-07' } }),
      ]);
      setQdiag({ '2026-06': juin.data, '2026-07': juillet.data });
      toast.success('Diagnostic Qonto exécuté (lecture seule)');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Diagnostic impossible'); }
    setQdiagLoading(false);
  };
  const downloadDoc = async () => {
    try {
      const res = await api.get('/rex/runbook/download', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/markdown;charset=utf-8' }));
      const a = document.createElement('a'); a.href = url; a.download = 'RUNBOOK_PROD_REX.md';
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error('Téléchargement impossible'); }
  };
  if (!rb) return <Loading />;
  const c = rb.checks || {};
  const stepOk = (step) => {
    switch (step.check) {
      case 'qonto_configured': return !!c.qonto_configured;
      case 'derniere_sync': return !!c.derniere_sync;
      case 'historique_complet': return !!c.historique_complet;
      case 'nb_imports_paie': return (c.nb_imports_paie || 0) > 0;
      case 'nb_factures': return (c.nb_factures || 0) > 0;
      case 'nb_exceptions_open': return (c.nb_exceptions_open || 0) === 0;
      case 'mois_clotures': return (c.mois_clotures || []).length > 0;
      default: return null;
    }
  };
  return (
    <div className="space-y-6" data-testid="rex-runbook">
      <SectionTitle icon={Rocket} desc="Parcours guidé de mise en production, exécutable entièrement à la souris. Le document Markdown autonome (procédures de reprise incluses) est téléchargeable ci-dessous.">Mise en production — runbook</SectionTitle>

      <Card className="card-shadow">
        <CardHeader className="pb-2"><CardTitle className="text-base">Voyants de contrôle (en direct)</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap gap-x-6 gap-y-2">
          <Voyant ok={c.qonto_configured} warn={!c.qonto_configured} label={`Secrets Qonto${c.qonto_source ? ` (${c.qonto_source})` : ''}`} />
          <Voyant ok={c.historique_complet} warn={!c.historique_complet} label={`Historique figé (${c.nb_mois_figes || 0}/31)`} />
          <Voyant ok={(c.nb_imports_paie || 0) > 0} warn={!(c.nb_imports_paie > 0)} label={`Imports paie (${c.nb_imports_paie || 0})`} />
          <Voyant ok={(c.nb_factures || 0) > 0} warn={!(c.nb_factures > 0)} label={`Documents facturation (${c.nb_factures || 0})`} />
          <Voyant ok={(c.nb_exceptions_open || 0) === 0} warn={(c.nb_exceptions_open || 0) > 0} label={`Exceptions ouvertes (${c.nb_exceptions_open || 0})`} />
          <Voyant ok={(c.mois_clotures || []).length > 0} label={`Mois clôturés (${(c.mois_clotures || []).length})`} />
        </CardContent>
      </Card>

      {/* ── Corrections de données (Preview → Production) ─────────────────────── */}
      <Card className="card-shadow" data-testid="rex-corrections-card">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base"><ShieldAlert className="h-4 w-4" />Corrections de données (Preview → Production)</CardTitle>
          <CardDescription>
            Le <b>code</b> se déploie, les <b>données</b> non. Certaines corrections vivent en base
            (table de correspondance Amnesty, correspondance des labels Qonto) et doivent être
            appliquées <b>en production</b> après déploiement. Ce bouton les (ré)installe de façon
            <b> idempotente</b>. <b>Aucune injection de montant</b> : après application, re-synchronisez
            Qonto sur le mois pour que la transaction (ex. « Save Process video ») entre dans les frais généraux.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {corr ? (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <div className="rounded-lg border p-3" data-testid="corr-status-mapping">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Mapping Amnesty 26SPAFA75C</p>
                <p className="mt-1 text-sm">
                  Résolu : <b>{corr.mapping?.resolu_canal || '—'}</b> @ {isNum(corr.mapping?.resolu_prix) ? eur2(corr.mapping.resolu_prix) : '—'}
                  <span className="ml-1 text-xs text-muted-foreground">(source {corr.mapping?.resolu_source || '—'})</span>
                  {corr.mapping?.ok ? <CheckCircle2 className="ml-1 inline h-4 w-4 text-emerald-600" /> : <AlertTriangle className="ml-1 inline h-4 w-4 text-amber-600" />}
                </p>
              </div>
              <div className="rounded-lg border p-3" data-testid="corr-status-labels">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Label Qonto « Save Process video »</p>
                <p className="mt-1 text-sm">
                  Mappé → <b>{corr.labels_qonto?.save_process_poste || 'non mappé'}</b>
                  {corr.labels_qonto?.ok ? <CheckCircle2 className="ml-1 inline h-4 w-4 text-emerald-600" /> : <AlertTriangle className="ml-1 inline h-4 w-4 text-amber-600" />}
                </p>
                {corr.labels_qonto?.diagnostic ? Object.entries(corr.labels_qonto.diagnostic).map(([mois, arr]) => (
                  <p key={mois} className="mt-0.5 text-[11px] text-muted-foreground" data-testid={`corr-status-diag-${mois}`}>
                    {moisLabel(mois)} : {(arr?.[0]?.destination) || '—'} · {isNum(arr?.[0]?.montant_ht) ? eur2(arr[0].montant_ht) : '—'} ({arr?.[0]?.nb_transactions ?? 0} txn)
                  </p>
                )) : null}
              </div>
            </div>
          ) : <Loading />}
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={applyCorr} disabled={applying} data-testid="rex-corrections-apply" style={{ background: 'var(--tenant-accent-500)' }}>
              <RefreshCw className={`mr-2 h-4 w-4 ${applying ? 'animate-spin' : ''}`} />Appliquer les corrections de données
            </Button>
            <span className="text-xs text-muted-foreground">Sûr à relancer autant de fois que nécessaire.</span>
          </div>
          {report ? (
            <Alert className="border-emerald-300 bg-emerald-50" data-testid="rex-corrections-report">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              <AlertTitle>Rapport d'application</AlertTitle>
              <AlertDescription className="text-sm">
                <ul className="mt-1 list-disc space-y-1 pl-5">
                  {report.mapping_amnesty ? <li>{report.mapping_amnesty.message}</li> : null}
                  {report.labels_qonto ? <li>{report.labels_qonto.message}</li> : null}
                </ul>
              </AlertDescription>
            </Alert>
          ) : null}
        </CardContent>
      </Card>

      {/* ── Diagnostic Qonto — lecture de l'API (avant/après, lecture seule) ─────── */}
      <Card className="card-shadow" data-testid="rex-qonto-diag-card">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between text-base">
            <span className="flex items-center gap-2"><Search className="h-4 w-4" />Diagnostic Qonto — lecture de l'API (avant/après)</span>
            <Button size="sm" onClick={runQontoDiag} disabled={qdiagLoading} data-testid="rex-qonto-diag-run" style={{ background: 'var(--tenant-accent-500)' }}>
              <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${qdiagLoading ? 'animate-spin' : ''}`} />Lancer le diagnostic (juin & juillet)
            </Button>
          </CardTitle>
          <CardDescription>
            Lecture seule (aucune écriture). Compare l'ancienne lecture (fenêtre par <b>settled_at</b>,
            date de dénouement) et la nouvelle (par <b>emitted_at</b>, date d'opération). Montre le
            nombre de transactions lues avant/après, celles récupérées, le détail Atlassian « Save
            Process video » et l'impact par poste. À lancer <b>en production</b> après déploiement + re-synchro.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!qdiag ? <p className="text-sm text-muted-foreground">Cliquez « Lancer le diagnostic ».</p> : (
            ['2026-06', '2026-07'].map((mois) => {
              const d = qdiag[mois] || {};
              if (d.configured === false) {
                return <Alert key={mois} data-testid={`qdiag-unconfigured-${mois}`}><AlertTriangle className="h-4 w-4" /><AlertTitle>{moisLabel(mois)}</AlertTitle><AlertDescription className="text-sm">{d.message}</AlertDescription></Alert>;
              }
              const pa = d.postes_avant || {}; const pb = d.postes_apres || {};
              const postes = ['cout_bureau', 'frais_generaux', 'rd_independants', 'cout_re', 'cout_rp'];
              return (
                <div key={mois} className="rounded-lg border p-3" data-testid={`qdiag-${mois}`}>
                  <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                    <b>{moisLabel(mois)}</b>
                    <span data-testid={`qdiag-counts-${mois}`}>lues : <b>{d.nb_lues_avant_settled}</b> (settled) → <b>{d.nb_lues_apres_emitted}</b> (emitted)</span>
                    <span className="text-[color:var(--tenant-accent-700)]">récupérées : <b>{d.nb_recuperees}</b></span>
                    <span className="text-muted-foreground">{d.nb_comptes} compte(s) : {(d.ibans || []).join(', ')}</span>
                  </div>
                  {(d.transactions_recuperees || []).length ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-[12px]" data-testid={`qdiag-recovered-${mois}`}>
                        <thead><tr className="text-left text-muted-foreground"><th className="pr-2">Opération (emitted)</th><th className="pr-2">Contrepartie</th><th className="pr-2 text-right">Montant</th><th className="pr-2">Devise</th><th className="pr-2">Étiquette</th><th className="pr-2">Dénouement (settled)</th><th className="pr-2">Mois avant</th><th>Mois après</th></tr></thead>
                        <tbody>
                          {d.transactions_recuperees.map((t, i) => (
                            <tr key={i} className="border-t">
                              <td className="pr-2 tabular">{(t.emitted_at || '').slice(0, 10)}</td>
                              <td className="pr-2">{t.contrepartie}</td>
                              <td className="pr-2 text-right tabular">{eur2(t.amount)}{t.local_currency && t.local_currency !== 'EUR' ? <span className="text-muted-foreground"> ({t.local_amount} {t.local_currency})</span> : null}</td>
                              <td className="pr-2">{t.currency}</td>
                              <td className="pr-2">{(t.labels || []).join(', ') || <span className="text-amber-600">—</span>}</td>
                              <td className="pr-2 tabular text-muted-foreground">{t.settled_at ? t.settled_at.slice(0, 10) : <span className="text-amber-600">null</span>}</td>
                              <td className="pr-2 tabular text-muted-foreground">{t.mois_rattachement_avant || <span className="text-amber-600">—</span>}</td>
                              <td className="tabular font-medium text-[color:var(--tenant-accent-700)]">{t.mois_rattachement_apres || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : <p className="text-xs text-muted-foreground">Aucune transaction récupérée sur ce mois.</p>}
                  {d.atlassian_save_process ? (
                    <pre className="mt-2 overflow-x-auto rounded bg-muted/50 p-2 text-[11px]" data-testid={`qdiag-atlassian-${mois}`}>{JSON.stringify({
                      emitted_at: d.atlassian_save_process.emitted_at, settled_at: d.atlassian_save_process.settled_at,
                      status: d.atlassian_save_process.status, amount: d.atlassian_save_process.amount,
                      currency: d.atlassian_save_process.currency, local_amount: d.atlassian_save_process.local_amount,
                      local_currency: d.atlassian_save_process.local_currency, labels: d.atlassian_save_process.labels_resolus,
                    }, null, 2)}</pre>
                  ) : null}
                  <table className="mt-2 w-full text-[12px]" data-testid={`qdiag-postes-${mois}`}>
                    <thead><tr className="text-left text-muted-foreground"><th>Poste</th><th className="text-right">Avant (settled)</th><th className="text-right">Après (emitted)</th><th className="text-right">Δ</th></tr></thead>
                    <tbody>
                      {postes.map((p) => {
                        const a = pa[p] || 0; const b = pb[p] || 0; const delta = Math.round((b - a) * 100) / 100;
                        return (
                          <tr key={p} className="border-t">
                            <td>{p}</td>
                            <td className="text-right tabular">{eur2(a)}</td>
                            <td className="text-right tabular">{eur2(b)}</td>
                            <td className={`text-right tabular ${delta !== 0 ? 'font-semibold text-[color:var(--tenant-accent-700)]' : 'text-muted-foreground'}`}>{delta === 0 ? '—' : eur2(delta)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      {/* ── Recette — validation (attendu / obtenu / écart) ──────────────────── */}
      <Card className="card-shadow" data-testid="rex-recette-card">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between text-base">
            <span className="flex items-center gap-2"><ListChecks className="h-4 w-4" />Recette — validation (attendu / obtenu / écart)</span>
            <Button size="sm" variant="outline" onClick={loadCorr} data-testid="rex-recette-refresh"><RefreshCw className="mr-1.5 h-3.5 w-3.5" />Rafraîchir</Button>
          </CardTitle>
          <CardDescription>Preuve calculée à la volée depuis la base (à exécuter en production). Un écart n'est jamais comblé de force : sa cause est indiquée.</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow><TableHead>Catégorie</TableHead><TableHead>Contrôle</TableHead><TableHead className="text-right">Attendu</TableHead><TableHead className="text-right">Obtenu</TableHead><TableHead className="text-right">Écart</TableHead><TableHead>Statut</TableHead></TableRow></TableHeader>
            <TableBody>
              {(recette?.checks || []).map((c) => (
                <TableRow key={c.id} data-testid={`rex-recette-row-${c.id}`}>
                  <TableCell className="text-xs text-muted-foreground">{c.categorie}</TableCell>
                  <TableCell className="text-sm">{c.label}{c.cause && !c.ok ? <div className="mt-0.5 text-[11px] text-amber-600">{c.cause}</div> : null}</TableCell>
                  <TableCell className="text-right tabular text-xs">{isNum(c.attendu) ? eur2(c.attendu) : String(c.attendu)}</TableCell>
                  <TableCell className="text-right tabular text-xs">{c.obtenu == null ? '—' : (isNum(c.obtenu) ? eur2(c.obtenu) : String(c.obtenu))}</TableCell>
                  <TableCell className="text-right tabular text-xs">{c.ecart == null ? '—' : (isNum(c.ecart) ? eur2(c.ecart) : String(c.ecart))}</TableCell>
                  <TableCell>{c.ok ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">OK</Badge> : <Badge variant="secondary" className="bg-amber-100 text-amber-700">à vérifier</Badge>}</TableCell>
                </TableRow>
              ))}
              {!(recette?.checks || []).length ? <TableRow><TableCell colSpan={6} className="py-6 text-center text-muted-foreground">Chargement…</TableCell></TableRow> : null}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={downloadDoc} data-testid="rex-runbook-download" style={{ background: 'var(--tenant-accent-500)' }}><Download className="mr-2 h-4 w-4" />Télécharger le runbook (RUNBOOK_PROD_REX.md)</Button>
        <span className="text-xs text-muted-foreground">Document autonome, lisible hors application, incluant les procédures de reprise (app ne démarre pas, import interrompu, erreur Qonto).</span>
      </div>

      <Card className="card-shadow">
        <CardHeader className="pb-2"><CardTitle className="text-base">Parcours pas à pas</CardTitle><CardDescription>Chaque étape indique l'écran, l'action précise et le résultat attendu.</CardDescription></CardHeader>
        <CardContent className="space-y-3">
          {(rb.steps || []).map((s) => {
            const ok = stepOk(s);
            return (
              <div key={s.n} className="rounded-xl border p-3" data-testid={`rex-runbook-step-${s.n}`}>
                <div className="flex items-start gap-3">
                  <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${ok === true ? 'bg-emerald-100 text-emerald-700' : ok === false ? 'bg-amber-100 text-amber-700' : 'bg-muted text-muted-foreground'}`}>{ok === true ? '✓' : s.n}</div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium">{s.titre}</p>
                      <Badge variant="outline" className="text-[10px]">{s.ecran}</Badge>
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground"><b>Action —</b> {s.action}</p>
                    <p className="mt-0.5 text-sm text-muted-foreground"><b>Résultat attendu —</b> {s.resultat}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Card className="card-shadow" data-testid="rex-audit-journal">
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><FileClock className="h-4 w-4" />Journal d'audit</CardTitle><CardDescription>Clôtures / réouvertures et opérations sur les secrets Qonto. Aucune valeur secrète — métadonnées uniquement.</CardDescription></CardHeader>
        <CardContent className="max-h-[320px] overflow-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Domaine</TableHead><TableHead>Action</TableHead><TableHead>Mois</TableHead><TableHead>Utilisateur</TableHead><TableHead>IP</TableHead><TableHead>Horodatage</TableHead></TableRow></TableHeader>
            <TableBody>{audit.length ? audit.map((a) => (
              <TableRow key={a.id}>
                <TableCell className="text-xs">{a.domain === 'cloture' ? 'Clôture' : 'Secrets Qonto'}</TableCell>
                <TableCell className="text-xs font-medium">{(a.domain === 'cloture' ? CLOTURE_AUDIT_LABEL : QONTO_AUDIT_LABEL)[a.action] || a.action}</TableCell>
                <TableCell className="tabular text-xs">{a.mois || '—'}</TableCell>
                <TableCell className="text-xs">{a.user || '—'}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{a.ip || '—'}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{fmtDateTime(a.at)}</TableCell>
              </TableRow>
            )) : <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground">Aucune entrée d'audit.</TableCell></TableRow>}</TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

// =========================================================================== //
//  E3bis — FACTURATION AMNESTY AUTOMATIQUE (P1)                                //
//  Clôture en 3 gestes : dépôt fichier(s) association → aperçu/écart → valider. //
//  Facture = mandats nets ; Avoir = attrition initiale (décalage +3 mois) ;     //
//  CA = facture − avoir. Codes inconnus / cross-check KO / prix manquant bloquent.//
// =========================================================================== //
// Ligne d'ajustement TOTALEMENT éditable (mappée OU non) : canal (liste), PU HT + date,
// quantité (avec la quantité SOURCE affichée à côté). Un bouton « Enregistrer » n'est actif
// que si quelque chose a changé. La source de résolution (manuel / préfixe / référentiel)
// est indiquée. Aucune régénération automatique : l'appelant la PROPOSE après l'enregistrement.
const EditableLigneRow = ({ l, kind, lignesFacture, onSaveLine, busy }) => {
  const cur = { canal: l.canal || '', pu: l.prix_ht != null ? String(l.prix_ht) : '', date: l.prix_date || '', qte: String(l.quantite) };
  const [canal, setCanal] = useState(cur.canal);
  const [pu, setPu] = useState(cur.pu);
  const [date, setDate] = useState(cur.date);
  const [qte, setQte] = useState(cur.qte);
  useEffect(() => { setCanal(cur.canal); setPu(cur.pu); setDate(cur.date); setQte(cur.qte); /* eslint-disable-next-line */ }, [l.canal, l.prix_ht, l.prix_date, l.quantite, l.code_origine]);
  const puFor = (c) => { const g = (lignesFacture || []).find((x) => x.code === c); return g ? g.pu : ''; };
  const onCanal = (v) => { setCanal(v); if (pu === '' || pu == null) setPu(String(puFor(v))); };
  const dirty = (canal !== cur.canal) || (pu !== cur.pu) || (date !== cur.date) || ((parseInt(qte || '0', 10) || 0) !== l.quantite);
  const sug = l.suggestion;
  const srcLabel = { manuel: 'manuel', prefixe: 'préfixe', referentiel: 'référentiel' }[l.mapped_source] || null;
  const montant = (canal && pu !== '' && qte !== '') ? (parseFloat(pu) * parseInt(qte || '0', 10) * (kind === 'avoir' ? -1 : 1)) : l.montant_ht;
  return (
    <TableRow data-testid={`amn-edit-row-${kind}-${l.code_origine}`} className="align-top">
      <TableCell className="font-mono text-xs">
        {l.code_origine}
        <div className="mt-1 space-y-0.5 text-[11px] text-muted-foreground">
          <div>{fmtInt(l.nb_mandats)} mandats</div>
          {l.lieu_majoritaire ? <div className="inline-flex items-center gap-0.5"><MapPin className="h-3 w-3" />{l.lieu_majoritaire}</div> : null}
          {srcLabel ? <Badge variant="outline" className="mt-0.5 h-4 px-1 text-[9px]" data-testid={`amn-src-${l.code_origine}`}>{srcLabel}</Badge> : <span className="text-amber-600">non mappé</span>}
          {sug ? <div className="text-[10px] text-[color:var(--tenant-accent-600)]">grille {sug.prefixe} → {sug.ligne}</div> : null}
        </div>
      </TableCell>
      <TableCell>
        <Select value={canal || undefined} onValueChange={onCanal}>
          <SelectTrigger className="h-8 w-40" data-testid={`amn-edit-canal-${kind}-${l.code_origine}`}><SelectValue placeholder="Choisir…" /></SelectTrigger>
          <SelectContent>{(lignesFacture || []).map((o) => <SelectItem key={o.code} value={o.code}>{o.code} — {o.label}</SelectItem>)}</SelectContent>
        </Select>
      </TableCell>
      <TableCell>
        <Input type="number" step="0.01" value={pu} onChange={(e) => setPu(e.target.value)} className="h-8 w-24 text-right tabular" data-testid={`amn-edit-pu-${kind}-${l.code_origine}`} placeholder="—" />
        <Input type="date" value={date || ''} onChange={(e) => setDate(e.target.value)} className="mt-1 h-7 w-32 text-[11px]" data-testid={`amn-edit-date-${kind}-${l.code_origine}`} title="Date d'application du prix" />
      </TableCell>
      <TableCell className="text-right">
        <Input type="number" value={qte} onChange={(e) => setQte(e.target.value)} className={`ml-auto h-8 w-20 text-right tabular ${l.override ? 'border-[color:var(--tenant-accent-500)]' : ''}`} data-testid={`amn-edit-qte-${kind}-${l.code_origine}`} />
        <div className="text-[10px] text-muted-foreground">source {l.quantite_source}{l.override ? ' · ajusté' : ''}</div>
      </TableCell>
      <TableCell className="text-right"><Money v={montant} d={2} /></TableCell>
      <TableCell className="text-right">
        <Button size="sm" className="h-8" disabled={!dirty || busy || !canal} data-testid={`amn-edit-save-${kind}-${l.code_origine}`} onClick={() => onSaveLine(kind, l, { canal, pu, date, qte })} style={{ background: 'var(--tenant-accent-500)' }}>
          <Save className="mr-1 h-3.5 w-3.5" />Enregistrer
        </Button>
      </TableCell>
    </TableRow>
  );
};

const AdjustLignesTable = ({ lignes, kind, lignesFacture, onSaveLine, busy }) => (
  <Table>
    <TableHeader>
      <TableRow>
        <TableHead>Code origine</TableHead>
        <TableHead>Canal (ligne de facture)</TableHead>
        <TableHead>PU HT · date</TableHead>
        <TableHead className="text-right">Quantité</TableHead>
        <TableHead className="text-right">Montant HT</TableHead>
        <TableHead className="text-right">Action</TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      {lignes.map((l) => (
        <EditableLigneRow key={`${kind}-${l.code_origine}`} l={l} kind={kind} lignesFacture={lignesFacture} onSaveLine={onSaveLine} busy={busy} />
      ))}
      {!lignes.length ? <TableRow><TableCell colSpan={6} className="py-6 text-center text-muted-foreground">Aucune ligne.</TableCell></TableRow> : null}
    </TableBody>
  </Table>
);

const ReadOnlyLignes = ({ lignes }) => (
  <Table>
    <TableHeader>
      <TableRow>
        <TableHead>Code origine</TableHead>
        <TableHead>Ligne de facture</TableHead>
        <TableHead>Canal</TableHead>
        <TableHead className="text-right">Quantité</TableHead>
        <TableHead className="text-right">PU HT</TableHead>
        <TableHead className="text-right">Montant HT</TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      {lignes.map((l) => (
        <TableRow key={l.code_origine} data-testid={`amn-cl-${l.code_origine}`}>
          <TableCell className="font-mono text-xs">{l.code_origine}</TableCell>
          <TableCell>{l.mapped ? l.libelle : <span className="inline-flex items-center gap-1 text-amber-600"><Ban className="h-3.5 w-3.5" /> non mappé</span>}</TableCell>
          <TableCell className="text-xs text-muted-foreground">{l.canal || '—'}</TableCell>
          <TableCell className="text-right tabular">{fmtInt(l.quantite)}{l.override ? <span className="ml-1 text-[10px] text-[color:var(--tenant-accent-600)]">(ajusté)</span> : null}</TableCell>
          <TableCell className="text-right tabular">{isNum(l.prix_ht) ? eur2(l.prix_ht) : <span className="text-amber-600">à définir</span>}</TableCell>
          <TableCell className="text-right"><Money v={l.montant_ht} d={2} /></TableCell>
        </TableRow>
      ))}
      {!lignes.length ? <TableRow><TableCell colSpan={6} className="py-6 text-center text-muted-foreground">Aucune ligne.</TableCell></TableRow> : null}
    </TableBody>
  </Table>
);

const AmnestyTab = ({ months, onGoFacturation }) => {
  const [busy, setBusy] = useState(false);
  const [mapping, setMapping] = useState([]);
  const [canaux, setCanaux] = useState([]);
  const [lignesFacture, setLignesFacture] = useState([]);   // 4 lignes de facture + PU réf.
  const [prefixGrid, setPrefixGrid] = useState({});         // grille de préfixes (autorité)
  const [importsAll, setImportsAll] = useState([]);
  const [ma, setMa] = useState(null);                 // aperçu CONSOLIDÉ du mois
  const [moisFacture, setMoisFacture] = useState('');  // mois de facturation CHOISI (geste 0)
  const [periode, setPeriode] = useState(null);        // période déduite du mois choisi
  const [evergiving, setEvergiving] = useState(null);
  const [uploadReport, setUploadReport] = useState(null); // { ok:[], mismatches:[], errors:[] }
  const [adjust, setAdjust] = useState(null);          // { importId, apercu, imp } — dialog d'ajustement
  const [mapForm, setMapForm] = useState(null);        // { code_origine, libelle, canal, prix_unitaire_ht, prix_date }
  const [regen, setRegen] = useState(null);            // { mois, docs, frozen } — proposition de régénération

  const loadMapping = useCallback(async () => {
    try {
      const { data } = await api.get('/rex/amnesty/mapping');
      setMapping(data.mapping || []); setCanaux(data.canaux || []);
      setLignesFacture(data.lignes_facture || []); setPrefixGrid(data.prefix_grid || {});
    } catch { /* silencieux */ }
  }, []);
  const loadImportsAll = useCallback(async () => {
    try { const { data } = await api.get('/rex/amnesty/imports'); setImportsAll(data.imports || []); }
    catch { /* silencieux */ }
  }, []);
  const loadEvergiving = useCallback(async (mois) => {
    if (!mois) { setEvergiving(null); return; }
    try { const { data } = await api.get('/rex/facturation', { params: { mois } }); setEvergiving(data); }
    catch { setEvergiving(null); }
  }, []);
  // Aperçu CONSOLIDÉ du mois : agrège TOUS les fichiers en aperçu/validés rattachés au
  // mois (facture + avoirs) — c'est lui qu'on réaffiche en revenant sur le mois, SANS
  // avoir à redéposer les fichiers.
  const refreshMonth = useCallback(async (mois) => {
    if (!mois) { setMa(null); return; }
    try { const { data } = await api.get('/rex/amnesty/month-apercu', { params: { mois } }); setMa(data.apercu); }
    catch { setMa(null); }
  }, []);

  useEffect(() => { loadMapping(); loadImportsAll(); }, [loadMapping, loadImportsAll]);

  // Geste 0 : le mois de facturation est choisi EN PREMIER. On (re)charge alors
  // automatiquement l'aperçu consolidé + la période + la saisie existante.
  useEffect(() => {
    setUploadReport(null); setAdjust(null);
    if (!moisFacture) { setPeriode(null); setMa(null); setEvergiving(null); return; }
    let alive = true;
    (async () => {
      try { const { data } = await api.get('/rex/facturation/periode', { params: { mois: moisFacture } }); if (alive) setPeriode(data); }
      catch { if (alive) setPeriode(null); }
    })();
    loadEvergiving(moisFacture);
    refreshMonth(moisFacture);
    return () => { alive = false; };
  }, [moisFacture, loadEvergiving, refreshMonth]);

  // Normalise le NOM de fichier avant upload : accents retirés, espaces/parenthèses →
  // « _ ». Certains filtres de sécurité en amont (edge/WAF du domaine de prod) rejettent
  // les requêtes dont le nom de pièce jointe contient accents/espaces/parenthèses AVANT
  // qu'elles n'atteignent le serveur (d'où « illisible » sans détail). Le rattachement
  // au mois est déduit du CONTENU (jamais du nom), donc nettoyer le nom est sans impact.
  const safeUploadName = (name) => {
    const n = String(name || 'fichier.xlsx');
    const dot = n.lastIndexOf('.');
    const base = dot > 0 ? n.slice(0, dot) : n;
    const ext = dot > 0 ? n.slice(dot) : '';
    const cb = base
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\w.-]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
    return (cb || 'fichier') + (ext.toLowerCase() || '.xlsx');
  };

  // Dépôt MULTIPLE : on téléverse chaque fichier (idempotent par hash), on agrège les
  // refus (mois incohérent) et on rafraîchit l'aperçu consolidé + le registre.
  const onUploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length || !moisFacture) return;
    setBusy(true);
    const report = { ok: [], mismatches: [], errors: [] };
    for (const f of files) {
      // Nom nettoyé pour l'upload ; le nom ORIGINAL reste affiché dans le rapport.
      const safeName = safeUploadName(f.name);
      const safeFile = (safeName !== f.name)
        ? new File([f], safeName, { type: f.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        : f;
      const fd = new FormData(); fd.append('file', safeFile); fd.append('mois_facture', moisFacture);
      try {
        const { data } = await api.post('/rex/amnesty/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (data.coherence === 'mismatch' || data.ok === false) report.mismatches.push({ filename: f.name, message: data.message });
        else report.ok.push({ filename: f.name, deja: data.deja_importe });
      } catch (e) { report.errors.push({ filename: f.name, message: e?.response?.data?.detail || 'Fichier illisible ou non reconnu' }); }
    }
    setUploadReport(report);
    await Promise.all([loadImportsAll(), refreshMonth(moisFacture), loadEvergiving(moisFacture)]);
    setBusy(false);
    if (report.ok.length) toast.success(`${report.ok.length} fichier(s) ajouté(s) à l'aperçu de ${moisLabel(moisFacture)}`);
    if (report.mismatches.length) toast.error(`${report.mismatches.length} fichier(s) refusé(s) — mois incohérent`);
    if (report.errors.length) toast.error(`${report.errors.length} fichier(s) illisible(s)`);
  };

  const doValidate = async (mois) => {
    if (!mois) {
      toast.error("Mois de facturation introuvable — impossible de valider. Choisissez d'abord le mois en haut de l'écran, ou utilisez le bouton « Valider » de la ligne du fichier.");
      return;
    }
    setBusy(true);
    let ok = false;
    try {
      const { data } = await api.post(`/rex/amnesty/validate?mois=${mois}`);
      ok = true;
      toast.success(`${moisLabel(mois)} validé — CA ${eur2(data.ca)} (${data.nb_facture_lignes} ligne(s) facture, ${data.nb_avoirs} avoir(s), ${(data.documents || []).length} document(s) généré(s))`);
      setAdjust(null);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || `Validation de ${moisLabel(mois)} impossible (erreur ${e?.response?.status || '?'})`));
    }
    setBusy(false);
    await Promise.all([loadImportsAll(), refreshMonth(mois), loadEvergiving(mois)]);
    return ok;
  };

  const deleteImport = async (iid) => {
    try { await api.delete(`/rex/amnesty/import/${iid}`); toast.success('Fichier supprimé'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Suppression impossible'); }
    if (adjust?.importId === iid) setAdjust(null);
    await Promise.all([loadImportsAll(), refreshMonth(moisFacture)]);
  };

  const openAdjust = async (iid) => {
    try { const { data } = await api.get('/rex/amnesty/apercu', { params: { iid } }); setAdjust({ importId: iid, apercu: data.apercu, imp: data.import }); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Aperçu indisponible'); }
  };
  // Proposition de RÉGÉNÉRATION (jamais automatique) : après une édition, on liste les
  // documents déjà générés du mois et on demande. Refusé si mois figé/clôturé.
  const proposeRegen = useCallback(async (mois) => {
    if (!mois) return;
    try {
      const { data } = await api.get('/rex/amnesty/generated', { params: { mois } });
      if ((data.nb || 0) > 0) setRegen({ mois, docs: data.documents || [], frozen: !!data.frozen });
      else toast.success('Modification enregistrée — aucun document généré à régénérer.');
    } catch { /* silencieux */ }
  }, []);
  const doRegen = async () => {
    if (!regen) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/rex/amnesty/regenerate?mois=${regen.mois}`);
      toast.success(`Documents régénérés — CA ${eur2(data.ca)}`);
      setRegen(null);
      await Promise.all([loadImportsAll(), refreshMonth(regen.mois)]);
      if (adjust?.importId) openAdjust(adjust.importId);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Régénération impossible'); }
    setBusy(false);
  };

  // Enregistre UNE ligne (canal/prix/date via le mapping ; quantité via l'override), puis
  // PROPOSE la régénération. Aucune écriture de document ici.
  const saveLine = async (kind, l, { canal, pu, date, qte }) => {
    const iid = adjust?.importId;
    const changedMap = (canal !== (l.canal || '')) || (pu !== (l.prix_ht != null ? String(l.prix_ht) : '')) || ((date || '') !== (l.prix_date || ''));
    const changedQty = (parseInt(qte || '0', 10) || 0) !== l.quantite;
    if (!changedMap && !changedQty) return;
    setBusy(true);
    try {
      if (changedMap) {
        const lab = (lignesFacture.find((x) => x.code === canal) || {}).label || canal;
        await api.post('/rex/amnesty/mapping', { code_origine: l.code_origine, canal, libelle: lab,
          prix_unitaire_ht: pu === '' ? null : parseFloat(pu), prix_date: date || null, source: 'manual' });
      }
      if (changedQty && iid) {
        await api.post('/rex/amnesty/override', { import_id: iid, kind, code_origine: l.code_origine, quantite: qte === '' ? null : parseInt(qte, 10) });
      }
      toast.success(`${l.code_origine} mis à jour`);
      await loadMapping();
      if (iid) await openAdjust(iid);
      await refreshMonth(moisFacture);
      await proposeRegen(moisFacture);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Enregistrement impossible'); }
    setBusy(false);
  };

  const saveMapping = async () => {
    if (!mapForm?.code_origine) return;
    try {
      await api.post('/rex/amnesty/mapping', { ...mapForm, prix_unitaire_ht: mapForm.prix_unitaire_ht === '' ? null : parseFloat(mapForm.prix_unitaire_ht), prix_date: mapForm.prix_date || null, source: 'manual' });
      toast.success(`Mapping ${mapForm.code_origine} enregistré`); setMapForm(null);
      await loadMapping(); await refreshMonth(moisFacture);
      if (adjust?.importId) openAdjust(adjust.importId);
      await proposeRegen(moisFacture);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Enregistrement impossible'); }
  };
  const deleteMapping = async (code) => {
    try { await api.delete(`/rex/amnesty/mapping/${encodeURIComponent(code)}`); toast.success('Mapping supprimé'); loadMapping(); refreshMonth(moisFacture); }
    catch { toast.error('Suppression impossible'); }
  };

  // Suggestion client (grille de préfixes) — pour préremplir/afficher, jamais appliquer seule.
  const suggestFor = (code) => {
    const p = String(code || '').slice(0, -1);
    const g = prefixGrid[p];
    return g ? { ligne: g.ligne, prix_ht: g.prix, prefixe: p, indice: `Préfixe ${p} → ${g.canal_label} · ${g.ligne} · ${eur2(g.prix)}` } : null;
  };
  const suggestibles = (ma?.codes_inconnus || []).filter((c) => suggestFor(c));

  // Mapper UN code depuis l'écran d'ajustement (ligne + PU + date) puis recalcul sur place.
  const mapOneCode = async (code, { ligne, prix_ht, prix_date }) => {
    const label = (lignesFacture.find((x) => x.code === ligne) || {}).label || ligne;
    try {
      await api.post('/rex/amnesty/mapping', { code_origine: code, canal: ligne, libelle: label,
        prix_unitaire_ht: prix_ht === '' ? null : parseFloat(prix_ht), prix_date: prix_date || null, source: 'manual' });
      toast.success(`${code} → ${ligne} (${eur2(parseFloat(prix_ht))})`);
      await loadMapping(); await refreshMonth(moisFacture);
      if (adjust?.importId) openAdjust(adjust.importId);
      await proposeRegen(moisFacture);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Mapping impossible'); }
  };

  // Mapper TOUS les codes suggérés du mois (grille de préfixes, PU 2026), en 1 clic.
  const mapAllSuggested = async (mois) => {
    if (!mois) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/rex/amnesty/mapping/suggest-all?mois=${mois}`);
      if (data.nb) toast.success(`${data.nb} code(s) mappé(s) via la grille de préfixes`);
      if (data.skipped?.length) toast.error(`${data.skipped.length} code(s) sans suggestion — à mapper à la main : ${data.skipped.join(', ')}`);
      if (!data.nb && !data.skipped?.length) toast('Aucun code à mapper');
      if (data.apercu) setMa(data.apercu);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Mapping groupé impossible'); }
    setBusy(false);
    await Promise.all([loadMapping(), loadImportsAll()]);
    if (adjust?.importId) openAdjust(adjust.importId);
  };

  // Mois candidats = mois VIVANTS jusqu'au mois courant (live_months du backend,
  // indépendants du CA). Repli : le mois courant si la liste est vide.
  const moisOptions = (() => {
    const set = new Set((months || []).filter((x) => x >= '2026-06'));
    if (!set.size) {
      const now = new Date();
      set.add(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
    }
    return Array.from(set).sort();
  })();
  const ddmm = (iso) => (iso && iso.length >= 10 ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '—');
  const nbPreview = ma?.nb_preview || 0;
  const nbAvoirMandats = (ma?.avoirs || []).reduce((s, a) => s + (a.nb_mandats || 0), 0);
  const roleLabel = (r) => (r === 'avoir' ? 'Avoir (attrition)' : r === 'mixte' ? 'Facture + avoir' : 'Facture (mandats)');
  // Mois EFFECTIF de validation pour le fichier ouvert dans l'écran d'ajustement :
  // on prend le mois VIVANT (facture au mois d'origine si non figé, sinon l'avoir),
  // à défaut le mois de facturation choisi. Évite un bouton « … de — » sans mois.
  const adjMois = adjust
    ? (adjust.apercu?.facture_ecrite ? adjust.apercu?.mois_facture
        : adjust.apercu?.avoir_ecrit ? adjust.apercu?.mois_avoir
        : (adjust.apercu?.mois_facture || adjust.apercu?.mois_avoir || moisFacture))
    : moisFacture;

  return (
    <div className="space-y-6" data-testid="rex-amnesty">
      <SectionTitle icon={ReceiptEuro} desc="Les factures et avoirs générés ici sont enregistrés dans l'onglet Facturation. Clôture en 3 gestes : (1) choisissez le mois de facturation, (2) déposez un OU PLUSIEURS fichiers association — un aperçu consolidé « ce que j'ai compris » s'affiche avant toute écriture, (3) validez pour générer facture/avoir. CA = facture − avoir. En revenant sur un mois, les fichiers en aperçu se rechargent tout seuls.">
        Facturation Amnesty — automatique
      </SectionTitle>

      {/* Geste 0 : choix du mois de facturation + période déduite */}
      <Card className="card-shadow" data-testid="amn-mois-card"><CardContent className="p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-muted-foreground">1. Mois de facturation</label>
            <Select value={moisFacture || undefined} onValueChange={setMoisFacture}>
              <SelectTrigger className="h-10 w-52" data-testid="amn-mois-select"><SelectValue placeholder="Choisir le mois…" /></SelectTrigger>
              <SelectContent>{moisOptions.map((ym) => <SelectItem key={ym} value={ym} data-testid={`amn-mois-opt-${ym}`}>{moisLabel(ym)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex-1">
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-muted-foreground">Période facturée (déduite — non saisissable)</label>
            <div className="flex h-10 flex-wrap items-center gap-2 rounded-md border bg-muted/30 px-3 text-sm" data-testid="amn-periode">
              {!moisFacture ? <span className="text-muted-foreground">Choisissez d'abord un mois.</span>
                : periode?.start ? (<>
                  <Badge variant="outline" className="gap-1"><CalendarRange className="h-3.5 w-3.5" />{ddmm(periode.start)} → {ddmm(periode.end)}</Badge>
                  {periode.nb_semaines ? <Badge variant="secondary" data-testid="amn-periode-weeks">{periode.nb_semaines} semaines</Badge> : null}
                  {periode.prelevement_date ? <Badge variant="outline">Prélèvement {ddmm(periode.prelevement_date)}</Badge> : null}
                </>) : <span className="text-muted-foreground">Période indisponible pour ce mois.</span>}
            </div>
          </div>
        </div>
      </CardContent></Card>

      {/* Bandeau : fichiers en attente de validation (jamais de travail « en suspens » silencieux) */}
      {moisFacture && nbPreview > 0 ? (
        <Alert className="border-amber-300 bg-amber-50" data-testid="amn-pending-banner">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="flex flex-wrap items-center justify-between gap-3">
            <span data-testid="amn-pending-count">{nbPreview} fichier(s) en attente de validation pour {moisLabel(moisFacture)}</span>
            <Button size="sm" disabled={!ma?.validable || busy} onClick={() => doValidate(moisFacture)} data-testid="amn-pending-validate" style={{ background: 'var(--tenant-accent-500)' }}>
              <ListChecks className="mr-1.5 h-4 w-4" />Valider {moisLabel(moisFacture)}
            </Button>
          </AlertTitle>
          <AlertDescription className="text-sm text-amber-700">
            Ces fichiers sont déjà importés et pré-calculés. Vérifiez l'aperçu consolidé ci-dessous puis validez.
            Tant qu'un fichier reste en aperçu, <b>{moisLabel(moisFacture)} ne peut pas être clôturé</b>.
            {!ma?.validable ? ' Des points bloquants subsistent (voir ci-dessous).' : ''}
          </AlertDescription>
        </Alert>
      ) : null}

      {/* Geste 1 : dépôt MULTIPLE (activé uniquement si un mois est choisi) */}
      <Card className="card-shadow"><CardContent className="p-4">
        {moisFacture ? (
          <label className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-border py-10 text-center hover:bg-muted/40" data-testid="amn-dropzone">
            <FileSpreadsheet className="mb-2 h-8 w-8 text-muted-foreground" />
            <span className="text-sm font-medium">Déposer un ou plusieurs fichiers association — {moisLabel(moisFacture)}</span>
            <span className="text-xs text-muted-foreground">Création mandats et/ou attrition initiale — sélection ou glisser-déposer MULTIPLE. Le mois est déduit du contenu (jamais du nom de fichier).</span>
            <input type="file" multiple accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden" data-testid="amn-file" onChange={(e) => { onUploadFiles(e.target.files); e.target.value = ''; }} />
          </label>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-border/60 bg-muted/20 py-10 text-center opacity-70" data-testid="amn-dropzone-locked">
            <Lock className="mb-2 h-8 w-8 text-muted-foreground" />
            <span className="text-sm font-medium text-muted-foreground">Choisissez d'abord le mois de facturation ci-dessus</span>
            <span className="text-xs text-muted-foreground">La zone de dépôt s'activera ensuite.</span>
          </div>
        )}
      </CardContent></Card>

      {busy ? <Loading /> : null}

      {/* Rapport de dépôt : fichiers refusés (mois incohérent) ou illisibles */}
      {uploadReport && (uploadReport.mismatches.length || uploadReport.errors.length) ? (
        <Alert className="border-rose-300 bg-rose-50" data-testid="amn-upload-report">
          <Ban className="h-4 w-4 text-rose-600" />
          <AlertTitle className="text-rose-800">Certains fichiers n'ont pas été rattachés</AlertTitle>
          <AlertDescription className="text-sm text-rose-700">
            <ul className="mt-1 list-disc space-y-1 pl-5">
              {uploadReport.mismatches.map((m, i) => <li key={`mm${i}`}><b>{m.filename}</b> — {m.message}</li>)}
              {uploadReport.errors.map((m, i) => <li key={`er${i}`}><b>{m.filename}</b> — {m.message}</li>)}
            </ul>
            {uploadReport.ok.length ? <p className="mt-2 text-emerald-700">{uploadReport.ok.length} autre(s) fichier(s) correctement ajouté(s) à l'aperçu.</p> : null}
            <p className="mt-1 text-xs">Corrigez le mois de facturation en haut si besoin, puis redéposez.</p>
          </AlertDescription>
        </Alert>
      ) : null}

      {/* Aperçu CONSOLIDÉ du mois (rechargé automatiquement) */}
      {moisFacture && ma && ma.nb_files > 0 ? (
        <div className="space-y-4" data-testid="amn-apercu">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <KpiCard label="Net facturable" value={fmtInt(ma.facture.nb_mandats)} sub={`facture ${eur2(ma.facture.total_ht)}`} testid="amn-kpi-net" />
            <KpiCard label="Avoir attrition" value={ma.avoirs.length ? fmtInt(nbAvoirMandats) : '—'} sub={ma.avoirs.length ? eur2(ma.total_avoir_ht) : 'aucun'} testid="amn-kpi-attrition" />
            <KpiCard label="CA aperçu (facture − avoir)" value={eur2(ma.ca_preview)} tone="revenu" testid="amn-kpi-ca" />
            <KpiCard label="Fichiers rattachés" value={fmtInt(ma.nb_files)} sub={nbPreview ? `${nbPreview} en aperçu` : 'tous validés'} testid="amn-kpi-files" />
          </div>

          {/* Fichiers rattachés à ce mois (facture + avoirs) */}
          <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="amn-files-summary">
            {ma.files.map((f) => (
              <Badge key={f.id} variant="outline" className="gap-1">
                {f.role === 'avoir' ? <ArrowRightLeft className="h-3 w-3" /> : <ReceiptEuro className="h-3 w-3" />}
                <span className="max-w-[220px] truncate">{f.filename}</span>
                <span className="text-muted-foreground">· {roleLabel(f.role)}</span>
                {f.status === 'validated' ? <CheckCircle2 className="h-3 w-3 text-emerald-600" /> : <span className="text-amber-600">· aperçu</span>}
              </Badge>
            ))}
          </div>

          {/* Blocages / prêt à valider */}
          {(ma.blocages || []).length ? (
            <Alert className="border-amber-300 bg-amber-50" data-testid="amn-blocages"><ShieldAlert className="h-4 w-4 text-amber-600" />
              <AlertTitle>Aperçu non validable — {ma.blocages.length} point(s) à traiter</AlertTitle>
              <AlertDescription>
                <ul className="mt-1 list-disc space-y-1 pl-5 text-sm">
                  {ma.blocages.map((b, i) => (
                    <li key={i}>
                      {b.message}
                      {b.type === 'codes_inconnus' ? (
                        <div className="mt-2 space-y-2">
                          {suggestibles.length ? (
                            <Button size="sm" className="h-7" disabled={busy} data-testid="amn-map-all-suggested" onClick={() => mapAllSuggested(moisFacture)} style={{ background: 'var(--tenant-accent-500)' }}>
                              <ListChecks className="mr-1 h-3.5 w-3.5" />Mapper les {suggestibles.length} code(s) suggéré(s) (grille de préfixes)
                            </Button>
                          ) : null}
                          <div className="inline-flex flex-wrap gap-1">
                            {b.codes.map((c) => {
                              const s = suggestFor(c);
                              return (
                                <Button key={c} size="sm" variant="outline" className="h-6 px-2 text-xs" data-testid={`amn-map-code-${c}`}
                                  onClick={() => setMapForm({ code_origine: c, canal: s?.ligne || '', libelle: s ? (lignesFacture.find((x) => x.code === s.ligne)?.label || s.ligne) : '', prix_unitaire_ht: s ? String(s.prix_ht) : '', prix_date: `${(moisFacture || '2026').slice(0, 4)}-01-01` })}>
                                  <Plus className="mr-1 h-3 w-3" />{c}{s ? ` → ${s.ligne}` : ''}
                                </Button>
                              );
                            })}
                          </div>
                          <p className="text-[11px] text-muted-foreground">Astuce : ouvrez « Revoir » sur un fichier pour mapper chaque code EN LIGNE (nb mandats + lieu majoritaire + PU éditable avec date).</p>
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          ) : (
            <Alert className="border-emerald-300 bg-emerald-50" data-testid="amn-validable"><CheckCircle2 className="h-4 w-4 text-emerald-600" /><AlertTitle>Prêt à valider</AlertTitle>
              <AlertDescription>Tous les codes sont mappés et valorisés. Validez {moisLabel(moisFacture)} ci-dessous.</AlertDescription></Alert>
          )}

          {ma.cross_check_ko?.length ? (
            <Alert data-testid="amn-crosscheck-ko"><Info className="h-4 w-4" /><AlertTitle>Contrôle croisé à surveiller</AlertTitle>
              <AlertDescription className="text-xs">Écart de contrôle croisé sur : {ma.cross_check_ko.join(', ')}. Vérifiez le fichier source (bouton « Revoir » dans le registre).</AlertDescription></Alert>
          ) : null}

          {/* Écran d'écart : Association (fichiers) vs Evergiving (saisie manuelle) */}
          <Card className="card-shadow" data-testid="amn-ecart">
            <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base"><ArrowRightLeft className="h-4 w-4" /> Écran d'écart — Association vs Evergiving</CardTitle>
              <CardDescription>Le(s) fichier(s) association font foi pour Amnesty. La colonne Evergiving rappelle la facturation déjà saisie du mois. Ajustez une quantité via « Revoir » dans le registre si besoin.</CardDescription></CardHeader>
            <CardContent className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div className="rounded-xl border p-3">
                <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Association (fichiers) — {moisLabel(moisFacture)}</p>
                <p className="tabular text-sm">Net facturable <b>{fmtInt(ma.facture.nb_mandats)}</b> mandats · Facture <Money v={ma.facture.total_ht} d={2} /></p>
                {ma.avoirs.length ? <p className="tabular text-sm">Avoir attrition <b>{fmtInt(nbAvoirMandats)}</b> mandats · <Money v={ma.total_avoir_ht} d={2} /></p> : null}
              </div>
              <div className="rounded-xl border p-3">
                <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Evergiving / saisie existante — {moisLabel(moisFacture)}</p>
                {evergiving ? (
                  <p className="tabular text-sm">CA net saisi <b>{eur2(evergiving.ca_net)}</b> · {evergiving.nb_factures} facture(s) · {evergiving.nb_avoirs} avoir(s)</p>
                ) : <p className="text-sm text-muted-foreground">Aucune facturation saisie pour ce mois.</p>}
                <p className="mt-1 text-xs text-muted-foreground">La validation génère les documents Amnesty automatiques (source « amnesty ») sans toucher les documents saisis à la main.</p>
              </div>
            </CardContent>
          </Card>

          {/* Lignes facture (consolidées, lecture seule) */}
          <Card className="card-shadow">
            <CardHeader className="pb-2"><CardTitle className="text-base">Lignes de facture (net facturable par code origine)</CardTitle></CardHeader>
            <CardContent><ReadOnlyLignes lignes={ma.facture.lignes} /></CardContent>
          </Card>

          {/* Lignes avoir (une carte par fichier d'attrition) */}
          {ma.avoirs.map((av, i) => (
            <Card key={i} className="card-shadow">
              <CardHeader className="pb-2"><CardTitle className="text-base">Lignes d'avoir — attrition {moisLabel(av.mois_origine)}</CardTitle>
                {av.filename ? <CardDescription>{av.filename}</CardDescription> : null}</CardHeader>
              <CardContent><ReadOnlyLignes lignes={av.lignes} /></CardContent>
            </Card>
          ))}

          {/* Geste 3 : validation du MOIS CHOISI */}
          <div className="flex flex-wrap items-center gap-2">
            <Button disabled={!ma.validable || busy} onClick={() => doValidate(moisFacture)} data-testid="amn-validate-facture" style={{ background: 'var(--tenant-accent-500)' }}>
              <ListChecks className="mr-1 h-4 w-4" /> Valider la facturation de {moisLabel(moisFacture)}
            </Button>
            <span className="text-xs text-muted-foreground">Génère facture (mandats nets) et/ou avoir (attrition) rattachés à {moisLabel(moisFacture)}. Documents « généré par import », sans toucher la saisie manuelle.</span>
            {!ma.validable ? <span className="text-xs text-amber-600">Traitez d'abord les points bloquants ci-dessus.</span> : null}
          </div>
        </div>
      ) : moisFacture ? (
        <Alert data-testid="amn-empty-month"><Info className="h-4 w-4" /><AlertTitle>Aucun fichier pour {moisLabel(moisFacture)}</AlertTitle>
          <AlertDescription className="text-sm">Déposez le(s) fichier(s) association ci-dessus. L'aperçu consolidé et le bouton de validation apparaîtront ici automatiquement — et se rechargeront à chaque retour sur ce mois.</AlertDescription></Alert>
      ) : null}

      {/* Écran d'AJUSTEMENT d'un fichier : mapping EN LIGNE des codes + override quantités */}
      <Dialog open={!!adjust} onOpenChange={(o) => { if (!o) setAdjust(null); }}>
        <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="amn-adjust-modal">
          <DialogHeader>
            <DialogTitle>Ajuster le fichier — {adjust?.apercu?.filename}</DialogTitle>
            <DialogDescription>Chaque ligne (mappée ou non) est éditable : canal, prix (+ date) et quantité (la quantité source reste affichée). Enregistrez une ligne, puis choisissez de régénérer les documents. L'aperçu se recalcule sur place.</DialogDescription>
          </DialogHeader>
          {adjust ? (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                {adjust.apercu.facture_ecrite ? (
                  <Badge variant="outline" data-testid="amn-adj-badge-facture"><ReceiptEuro className="mr-1 h-3 w-3" /> Facture → {moisLabel(adjust.apercu.mois_facture)}</Badge>
                ) : null}
                {adjust.apercu.attrition?.present && !adjust.apercu.mois_avoir_frozen ? (
                  <Badge variant="outline" data-testid="amn-adj-badge-avoir"><ArrowRightLeft className="mr-1 h-3 w-3" /> Avoir → {moisLabel(adjust.apercu.mois_avoir)}</Badge>
                ) : null}
                {suggestibles.length ? (
                  <Button size="sm" className="ml-auto h-7" disabled={busy} data-testid="amn-adj-map-all" onClick={() => mapAllSuggested(moisFacture)} style={{ background: 'var(--tenant-accent-500)' }}>
                    <ListChecks className="mr-1 h-3.5 w-3.5" />Mapper les {suggestibles.length} code(s) suggéré(s)
                  </Button>
                ) : null}
              </div>

              {/* Comptage & déductions : d'où vient le net facturable (transparence).
                  net = créés − TOUTES les déductions (toutes feuilles de détail). */}
              {adjust.apercu.facture ? (
                <Card data-testid="amn-adj-comptage">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2"><ListChecks className="h-4 w-4" />Comptage — net facturable</CardTitle>
                    <CardDescription className="text-xs">
                      Net facturé = <b>mandats créés</b> − <b>toutes les déductions</b> (chaque feuille de détail autre que « mandats créés » est déduite). Détail ci-dessous pour repérer l'origine d'un écart.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <Badge variant="outline" data-testid="amn-adj-created">Créés : <b className="ml-1">{fmtInt(adjust.apercu.facture.total_created)}</b></Badge>
                      <span className="text-muted-foreground">−</span>
                      {Object.entries(adjust.apercu.facture.deductions_by_sheet || {}).map(([sheet, nb]) => (
                        <Badge key={sheet} variant="secondary" data-testid={`amn-adj-deduc-sheet-${sheet}`}>{sheet} : <b className="ml-1">{fmtInt(nb)}</b></Badge>
                      ))}
                      {!Object.keys(adjust.apercu.facture.deductions_by_sheet || {}).length ? <span className="text-xs text-muted-foreground">aucune déduction</span> : null}
                      <span className="text-muted-foreground">=</span>
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100" data-testid="amn-adj-net">Net : <b className="ml-1">{fmtInt(adjust.apercu.facture.net_total)}</b></Badge>
                    </div>

                    {/* Anomalies = « point commun » d'un écart (RUM déduit absent des créés,
                        RUM déduit en double, ligne de déduction sans RUM). */}
                    {(() => {
                      const an = adjust.apercu.facture.deduction_anomalies || {};
                      const groups = [
                        { key: 'rum_hors_crees', rows: an.rum_hors_crees || [], label: 'RUM déduit ABSENT des mandats créés', cls: 'text-rose-700 border-rose-300 bg-rose-50' },
                        { key: 'rum_dupliques', rows: an.rum_dupliques || [], label: 'RUM déduit EN DOUBLE (compté plusieurs fois)', cls: 'text-amber-700 border-amber-300 bg-amber-50' },
                        { key: 'rum_absents', rows: an.rum_absents || [], label: 'Ligne de déduction SANS RUM', cls: 'text-amber-700 border-amber-300 bg-amber-50' },
                      ].filter((g) => g.rows.length);
                      if (!groups.length) {
                        return <p className="text-xs text-emerald-600" data-testid="amn-adj-deduc-clean">Aucune anomalie de déduction : chaque déduction correspond à un mandat créé, sans doublon.</p>;
                      }
                      return groups.map((g) => (
                        <Alert key={g.key} className={g.cls} data-testid={`amn-adj-anomaly-${g.key}`}>
                          <AlertTriangle className="h-4 w-4" />
                          <AlertTitle className="text-sm">{g.rows.length} {g.label}</AlertTitle>
                          <AlertDescription>
                            <div className="mt-1 max-h-40 overflow-auto rounded border bg-background/60">
                              <table className="w-full text-[11px]">
                                <thead><tr className="text-left text-muted-foreground"><th className="px-2 py-1">RUM</th><th className="px-2 py-1">Feuille</th><th className="px-2 py-1">Code origine</th><th className="px-2 py-1">Statut</th></tr></thead>
                                <tbody>
                                  {g.rows.map((r, i) => (
                                    <tr key={i} className="border-t">
                                      <td className="px-2 py-1 tabular">{r.rum || <span className="text-rose-600">(vide)</span>}</td>
                                      <td className="px-2 py-1">{r.sheet}</td>
                                      <td className="px-2 py-1">{r.code_origine || '—'}</td>
                                      <td className="px-2 py-1">{r.statut || '—'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </AlertDescription>
                        </Alert>
                      ));
                    })()}
                  </CardContent>
                </Card>
              ) : null}

              {/* Protection MOIS FIGÉ : origine figée → aucune facture, seulement l'avoir vivant */}
              {adjust.apercu.mois_facture_frozen && adjust.apercu.facture?.lignes?.length ? (
                <Alert className="border-amber-300 bg-amber-50" data-testid="amn-adj-frozen">
                  <Lock className="h-4 w-4 text-amber-600" />
                  <AlertTitle>Mois d'origine {moisLabel(adjust.apercu.mois_facture)} figé — aucune facture ne sera générée</AlertTitle>
                  <AlertDescription className="text-sm text-amber-700">
                    Ce fichier ne produit qu'un <b>avoir rattaché à {moisLabel(adjust.apercu.mois_avoir)}</b>. Les mandats créés d'un mois figé ne sont jamais refacturés (historique en lecture seule). Les lignes de facture ci-dessous sont affichées à titre informatif et NE SERONT PAS écrites.
                  </AlertDescription>
                </Alert>
              ) : null}

              {/* Lignes de facture : éditables/mappables si le mois d'origine est vivant ;
                  en lecture seule si figé (jamais écrites). */}
              {adjust.apercu.facture?.lignes?.length ? (
                <Card><CardHeader className="pb-2"><CardTitle className="text-base">Lignes de facture{adjust.apercu.mois_facture_frozen ? ' (mois figé — non écrites)' : ''}</CardTitle></CardHeader>
                  <CardContent>
                    {adjust.apercu.mois_facture_frozen
                      ? <ReadOnlyLignes lignes={adjust.apercu.facture.lignes} />
                      : <AdjustLignesTable lignes={adjust.apercu.facture.lignes} kind="facture" lignesFacture={lignesFacture} onSaveLine={saveLine} busy={busy} />}
                  </CardContent></Card>
              ) : null}

              {adjust.apercu.attrition?.present ? (
                <Card><CardHeader className="pb-2"><CardTitle className="text-base">Lignes d'avoir (attrition) → {moisLabel(adjust.apercu.mois_avoir)}</CardTitle></CardHeader>
                  <CardContent><AdjustLignesTable lignes={adjust.apercu.attrition.lignes} kind="avoir" lignesFacture={lignesFacture} onSaveLine={saveLine} busy={busy} /></CardContent></Card>
              ) : null}

              <div className="flex flex-wrap items-center gap-2 border-t pt-3">
                <Button disabled={(adjust.apercu.codes_inconnus?.length || 0) > 0 || busy || !adjMois} onClick={() => doValidate(adjMois)} data-testid="amn-adj-validate" style={{ background: 'var(--tenant-accent-500)' }}>
                  <ListChecks className="mr-1 h-4 w-4" />Valider la facturation de {moisLabel(adjMois)}
                </Button>
                {(adjust.apercu.codes_inconnus?.length || 0) > 0
                  ? <span className="text-xs text-amber-600" data-testid="amn-adj-remaining">{adjust.apercu.codes_inconnus.length} code(s) encore à définir : {adjust.apercu.codes_inconnus.join(', ')}</span>
                  : <span className="text-xs text-emerald-600">Aucun code à définir — prêt à valider.</span>}
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjust(null)} data-testid="amn-adjust-close">Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Proposition de RÉGÉNÉRATION (jamais automatique) : documents déjà générés impactés */}
      <Dialog open={!!regen} onOpenChange={(o) => { if (!o) setRegen(null); }}>
        <DialogContent className="max-w-lg" data-testid="amn-regen-modal">
          <DialogHeader>
            <DialogTitle>Régénérer les documents de {regen ? moisLabel(regen.mois) : ''} ?</DialogTitle>
            <DialogDescription>
              Votre modification (canal / prix / quantité) a été enregistrée. Elle n'a PAS
              encore mis à jour les documents déjà générés ci-dessous. Vous décidez.
            </DialogDescription>
          </DialogHeader>
          {regen ? (
            <div className="space-y-3">
              {regen.frozen ? (
                <Alert className="border-amber-300 bg-amber-50" data-testid="amn-regen-frozen">
                  <Lock className="h-4 w-4 text-amber-600" />
                  <AlertTitle>Mois figé / clôturé — régénération refusée</AlertTitle>
                  <AlertDescription className="text-sm text-amber-700">
                    {moisLabel(regen.mois)} est en lecture seule. Les documents ci-dessous ne
                    peuvent pas être régénérés. Rouvrez le mois (clôture) si c'est vraiment voulu.
                  </AlertDescription>
                </Alert>
              ) : (
                <p className="text-sm text-muted-foreground" data-testid="amn-regen-count">
                  {regen.docs.length} document(s) déjà généré(s) seraient recalculés :
                </p>
              )}
              <ul className="space-y-1 rounded-lg border p-2 text-sm" data-testid="amn-regen-list">
                {regen.docs.map((d) => (
                  <li key={d.id} className="flex items-center justify-between gap-2">
                    <span className="inline-flex items-center gap-1.5">
                      {d.type === 'avoir' ? <ArrowRightLeft className="h-3.5 w-3.5" /> : <ReceiptEuro className="h-3.5 w-3.5" />}
                      <span className="font-mono text-xs">{d.identifiant}</span>
                    </span>
                    <span className="tabular text-xs text-muted-foreground">{eur2(d.sous_total_ht)} · {fmtInt(d.volume)} mandats</span>
                  </li>
                ))}
                {!regen.docs.length ? <li className="text-muted-foreground">Aucun document.</li> : null}
              </ul>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegen(null)} data-testid="amn-regen-later">Plus tard</Button>
            <Button disabled={!regen || regen.frozen || busy} onClick={doRegen} data-testid="amn-regen-confirm" style={{ background: 'var(--tenant-accent-500)' }}>
              <RefreshCw className="mr-1.5 h-4 w-4" />Régénérer maintenant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Table de correspondance code origine -> ligne facture + PU */}
      <Card className="card-shadow" data-testid="amn-mapping-card">
        <CardHeader className="pb-2"><CardTitle className="flex items-center justify-between text-base">
          <span>Table de correspondance — code origine → ligne de facture & prix unitaire</span>
          <Button size="sm" variant="outline" onClick={() => setMapForm({ code_origine: '', libelle: '', canal: '', prix_unitaire_ht: '', prix_date: '2026-01-01' })} data-testid="amn-map-add"><Plus className="mr-1 h-4 w-4" />Ajouter</Button>
        </CardTitle>
          <CardDescription>Administrable. Aucun mapping automatique par ressemblance : un code inconnu reste bloquant tant qu'il n'est pas saisi ici.</CardDescription></CardHeader>
        <CardContent>
          {mapForm ? (
            <div className="mb-3 space-y-2 rounded-xl border p-3" data-testid="amn-map-form">
              {(() => { const s = suggestFor(mapForm.code_origine); return s ? (
                <div className="flex items-start gap-1.5 rounded-md bg-[color:var(--tenant-accent-050)] px-2 py-1 text-[11px] text-[color:var(--tenant-accent-700)]">
                  <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /><span>Suggestion : <b>{s.ligne}</b> · {eur2(s.prix_ht)} — {s.indice}
                  <Button size="sm" variant="ghost" className="ml-1 h-5 px-1 text-[11px]" onClick={() => setMapForm({ ...mapForm, canal: s.ligne, libelle: (lignesFacture.find((x) => x.code === s.ligne)?.label || s.ligne), prix_unitaire_ht: String(s.prix_ht) })}>appliquer</Button></span>
                </div>
              ) : null; })()}
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-6">
                <Input placeholder="Code origine" value={mapForm.code_origine} onChange={(e) => setMapForm({ ...mapForm, code_origine: e.target.value.trim() })} data-testid="amn-map-code" className="font-mono" />
                <Select value={mapForm.canal || undefined} onValueChange={(v) => { const o = lignesFacture.find((x) => x.code === v) || {}; setMapForm({ ...mapForm, canal: v, libelle: o.label || v, prix_unitaire_ht: (mapForm.prix_unitaire_ht === '' || mapForm.prix_unitaire_ht == null) ? String(o.pu ?? '') : mapForm.prix_unitaire_ht }); }}>
                  <SelectTrigger data-testid="amn-map-canal"><SelectValue placeholder="Ligne de facture" /></SelectTrigger>
                  <SelectContent>{lignesFacture.map((o) => <SelectItem key={o.code} value={o.code}>{o.code} — {o.label}</SelectItem>)}</SelectContent>
                </Select>
                <Input placeholder="Libellé" value={mapForm.libelle} onChange={(e) => setMapForm({ ...mapForm, libelle: e.target.value })} data-testid="amn-map-libelle" />
                <Input type="number" step="0.01" placeholder="PU HT (€)" value={mapForm.prix_unitaire_ht} onChange={(e) => setMapForm({ ...mapForm, prix_unitaire_ht: e.target.value })} data-testid="amn-map-pu" className="text-right" />
                <Input type="date" value={mapForm.prix_date || ''} onChange={(e) => setMapForm({ ...mapForm, prix_date: e.target.value })} data-testid="amn-map-date" title="Date d'application du prix" />
                <div className="flex gap-1"><Button size="sm" onClick={saveMapping} data-testid="amn-map-save" style={{ background: 'var(--tenant-accent-500)' }}><Save className="mr-1 h-4 w-4" />OK</Button><Button size="sm" variant="ghost" onClick={() => setMapForm(null)}>Annuler</Button></div>
              </div>
            </div>
          ) : null}
          <Table>
            <TableHeader><TableRow><TableHead>Code origine</TableHead><TableHead>Ligne de facture</TableHead><TableHead>Canal</TableHead><TableHead className="text-right">PU HT</TableHead><TableHead>Applicable au</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {mapping.length ? mapping.map((m) => (
                <TableRow key={m.code_origine} data-testid={`amn-map-row-${m.code_origine}`}>
                  <TableCell className="font-mono text-xs">{m.code_origine}</TableCell>
                  <TableCell>{m.libelle}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{m.canal || '—'}</TableCell>
                  <TableCell className="text-right tabular">{isNum(m.prix_unitaire_ht) ? eur2(m.prix_unitaire_ht) : <span className="text-amber-600">à définir</span>}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{m.prix_date || '—'}</TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setMapForm({ code_origine: m.code_origine, libelle: m.libelle, canal: m.canal || '', prix_unitaire_ht: m.prix_unitaire_ht ?? '', prix_date: m.prix_date || '' })} data-testid={`amn-map-edit-${m.code_origine}`}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600" onClick={() => deleteMapping(m.code_origine)} data-testid={`amn-map-del-${m.code_origine}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </TableCell>
                </TableRow>
              )) : <TableRow><TableCell colSpan={6} className="py-6 text-center text-muted-foreground" data-testid="amn-map-empty">Aucun mapping. Ajoutez les codes origine Amnesty avec leur ligne de facture et leur prix unitaire.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Fichiers association importés — registre + actions par ligne */}
      <Card className="card-shadow">
        <CardHeader className="pb-2"><CardTitle className="text-base">Fichiers association importés</CardTitle>
          <CardDescription>Idempotent (empreinte SHA-256). Agissez directement ici : <b>Valider</b> le mois, <b>Revoir</b> / ajuster l'aperçu d'un fichier, ou le <b>Supprimer</b>. Un fichier en « aperçu » attend votre validation.</CardDescription></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow><TableHead>Fichier</TableHead><TableHead>Rattachement</TableHead><TableHead className="text-right">Net</TableHead><TableHead className="text-right">Attrition</TableHead><TableHead>Statut</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {importsAll.length ? importsAll.map((im) => {
                const target = im.mois_choisi || im.mois_facture || im.mois_avoir;
                const isPreview = (im.status || 'preview') !== 'validated';
                return (
                  <TableRow key={im.id} data-testid={`amn-import-row-${im.id}`}>
                    <TableCell className="max-w-[220px] truncate">{im.filename}</TableCell>
                    <TableCell className="text-xs">
                      {im.mois_facture ? <Badge variant="outline" className="mr-1">Facture {moisLabel(im.mois_facture)}</Badge> : null}
                      {im.attrition_present ? <Badge variant="outline">Avoir {moisLabel(im.mois_avoir)}</Badge> : null}
                    </TableCell>
                    <TableCell className="text-right tabular">{fmtInt(im.net_facture)}</TableCell>
                    <TableCell className="text-right tabular">{im.attrition_present ? fmtInt(im.net_avoir) : '—'}</TableCell>
                    <TableCell><Badge variant={isPreview ? 'secondary' : 'default'} className={isPreview ? '' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100'} data-testid={`amn-import-status-${im.id}`}>{isPreview ? 'aperçu' : 'validé'}</Badge></TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => openAdjust(im.id)} data-testid={`amn-import-review-${im.id}`}><Eye className="mr-1 h-3.5 w-3.5" />Revoir</Button>
                        {isPreview ? <Button size="sm" className="h-7 px-2 text-xs" onClick={() => doValidate(target)} disabled={busy} data-testid={`amn-import-validate-${im.id}`} style={{ background: 'var(--tenant-accent-500)' }}><ListChecks className="mr-1 h-3.5 w-3.5" />Valider</Button> : null}
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600" onClick={() => deleteImport(im.id)} data-testid={`amn-import-del-${im.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              }) : <TableRow><TableCell colSpan={6} className="py-6 text-center text-muted-foreground" data-testid="amn-imports-empty">Aucun fichier importé.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

// =========================================================================== //
//  PAGE                                                                        //
// =========================================================================== //
export default function PilotageREX() {
  const [mois, setMois] = useState('2026-06');
  const [tab, setTab] = useState('e1');
  const [overview, setOverview] = useState({ nb_exceptions_open: 0, months: [], facturation_months: [] });
  useEffect(() => { api.get('/rex/overview').then(({ data }) => setOverview(data)).catch(() => {}); }, [tab]);
  const months = overview.months || [];
  const factMonths = overview.live_months || overview.facturation_months || months;

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-8 sm:px-6 lg:px-8" data-testid="pilotage-rex-page">
      <div className="relative mb-6 overflow-hidden rounded-[1.25rem] border border-white/10 p-6 sm:p-8" style={{ background: 'linear-gradient(160deg, var(--exec-hero-ink) 0%, var(--exec-hero-ink-2) 55%, #0A1020 100%)' }}>
        <div className="pointer-events-none absolute inset-0 opacity-70" style={{ background: 'radial-gradient(900px 520px at 18% 10%, rgba(238,114,46,0.18), transparent 55%), radial-gradient(700px 520px at 85% 0%, rgba(59,130,246,0.16), transparent 60%)' }} />
        <div className="relative flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: 'var(--tenant-accent-500)' }}><Calculator className="h-6 w-6 text-white" /></div>
            <div>
              <h1 className="font-display text-2xl font-semibold text-white sm:text-3xl">Pilotage REX</h1>
              <p className="text-sm text-white/70">Résultat d'Exploitation · <span className="font-semibold text-white/90">SHARITI</span> · réservé au propriétaire</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {overview.nb_exceptions_open ? <Badge className="bg-amber-500/90 text-white hover:bg-amber-500">{overview.nb_exceptions_open} exception(s)</Badge> : null}
            <Badge className="bg-white/10 text-white hover:bg-white/10" data-testid="rex-scope-badge">Owner-only · mono-entité</Badge>
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4 flex h-auto flex-wrap justify-start gap-1 bg-muted/60 p-1" data-testid="rex-tabs">
          <TabsTrigger value="e1" data-testid="rex-tab-e1">Pilotage REX</TabsTrigger>
          <TabsTrigger value="prog" data-testid="rex-tab-prog">Programmation</TabsTrigger>
          <TabsTrigger value="e2" data-testid="rex-tab-e2">Import paie</TabsTrigger>
          <TabsTrigger value="e3" data-testid="rex-tab-e3">Facturation</TabsTrigger>
          <TabsTrigger value="e3b" data-testid="rex-tab-e3b">Facturation Amnesty</TabsTrigger>
          <TabsTrigger value="e4" data-testid="rex-tab-e4">Exceptions{overview.nb_exceptions_open ? <span className="ml-1.5 rounded-full bg-amber-500 px-1.5 text-[10px] text-white">{overview.nb_exceptions_open}</span> : null}</TabsTrigger>
          <TabsTrigger value="e5" data-testid="rex-tab-e5">Référentiel</TabsTrigger>
          <TabsTrigger value="e6" data-testid="rex-tab-e6">Qonto</TabsTrigger>
          <TabsTrigger value="e7" data-testid="rex-tab-e7">Administration</TabsTrigger>
          <TabsTrigger value="e8" data-testid="rex-tab-e8">Import historique</TabsTrigger>
          <TabsTrigger value="e9" data-testid="rex-tab-e9">Mise en production</TabsTrigger>
        </TabsList>
        <TabsContent value="e1"><PilotageTab mois={mois} setMois={setMois} /></TabsContent>
        <TabsContent value="prog"><ProgrammationTab /></TabsContent>
        <TabsContent value="e2"><ImportPaieTab /></TabsContent>
        <TabsContent value="e3"><FacturationTab mois={mois} setMois={setMois} monthsAvailable={factMonths} onGoAmnesty={() => setTab('e3b')} /></TabsContent>
        <TabsContent value="e3b"><AmnestyTab months={factMonths} onGoFacturation={() => setTab('e3')} /></TabsContent>
        <TabsContent value="e4"><ExceptionsTab /></TabsContent>
        <TabsContent value="e5"><ReferentielTab /></TabsContent>
        <TabsContent value="e6"><QontoTab mois={mois} /></TabsContent>
        <TabsContent value="e7"><AdminTab /></TabsContent>
        <TabsContent value="e8"><HistoriqueTab /></TabsContent>
        <TabsContent value="e9"><RunbookTab /></TabsContent>
      </Tabs>
    </div>
  );
}
